import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MessageCircle, MessageSquare, AlertTriangle, Mail, Clock, CheckCircle, Send, Paperclip, Sparkles, X, Search, Phone, Video, VideoOff, MoreVertical, Mic, MicOff, ThumbsUp, Smile, Image as ImageIcon, Calendar, UserPlus, CalendarPlus, UserCheck, Trash2, Reply, Flag, Pin, Loader2, PhoneOff, Bot, RefreshCw, Link, Download, FileText, ChevronLeft, ChevronRight, ChevronUp, ChevronDown, Check, FolderPlus, Plane, DollarSign, Plus, Pencil } from 'lucide-react';
import { apiCall, aiEmailAPI } from '../services/api';
import useVoiceClient from '../hooks/useVoiceClient';
import { useNylasAccounts, useCategoryTabs } from '../hooks';
import IncomingCallPopup from '../components/IncomingCallPopup';
import { toast } from 'sonner';
import { useDemoMode } from '../context/DemoContext';
import { 
  NewCallModal, 
  CallDetailModal,
  // Refactored components - Small
  CategoryTabs,
  PhoneTypeTabs,
  ConversationListItem,
  ConversationItem,
  ConversationDropdown,
  CallDropdown,
  CallItem,
  CallBadges,
  MessageBubble,
  ChatHeader,
  MessageInput,
  SearchBar,
  EmptyState,
  LoadingSpinner,
  EmailFolderList,
  EmailConnectionPanel,
  EmailPagination,
  Modal,
  ScheduleModal,
  PhoneCallModal,
  MeetingPlatformCard,
  GroupMembersModal,
  MessagingConnectionPanel,
  // Refactored components - Large
  ConversationList,
  MessageList,
  TabHeader,
  ConnectionPanel,
  // Modal components
  CategoryManagementModal,
  AddEmailAccountModal,
  DeleteConfirmModal,
  AddFlightModal,
  AddFinanceModal,
  AddToMeetingModal,
  CommWidgetModal,
  NewMeetingModal,
  AddContactModal,
  MessagingConnectionModal,
  ScheduledMessagesTracker,
  CallsConnectionModal,
  EmailComposeModal,
  MacSyncModal,
  PhoneSettingsModal,
  MessageComposeModal,
  FaceTimeModal,
  DeleteEmailAccountModal,
  EmailDropdown,
  EmailAttachments,
  AIReplyDraft,
  EmailCategoryBadge,
  MeetingPlatformGrid,
  ActiveMeetingView,
  CallListItem,
  CallsConnectionPanel,
  CallCategoryTabs,
  EmailThreadView,
  EmailList,
  VoiceStatusIndicator,
  PageHeader,
  EmailHeader,
  EmailCategoryFilter,
  EmailSearchBar,
  EmailAccountTabs,
  MessageCategoryBar,
  EmailTabHeader,
  ConversationListItemCard,
  CategorizedMessageItem,
  // Hooks
  useConversationHandlers,
  // Utilities
  formatEmailTime,
  categorizeEmail,
  getInitials,
  getAvatarColor,
  getCategoryBadgeColor,
  getPhoneTypeBadgeColor,
  formatMessageTime,
  formatCallDuration
} from '../components/communications';
import DraftCenter from '../components/communications/DraftCenter';
import { useUserId, useAuth, useUserEmail } from '../context/UserAuthContext';

// Module-level session cache — survives component unmount/remount within the same browser tab.
// Cleared only on full page reload. Avoids localStorage size limits (bodies can be MBs).
const _nylasSessionCache = {}; // keyed by "${accountId}_${folder}"

// ---------------------------------------------------------------------------
// Counterfind-only: mockup conversations & messages for the Messages tab.
// 7 conversations spanning sales leads, brand partners, an ops VA, and a
// legal/takedown lawyer — built to look realistic for demos without piping
// in live SMS data. Function is defined at module scope so it's stable
// across renders.
// ---------------------------------------------------------------------------
const _buildCounterfindMockConversations = () => {
  const now = Date.now();
  const minsAgo = (m) => new Date(now - m * 60 * 1000).toISOString();
  const hoursAgo = (h) => new Date(now - h * 3600 * 1000).toISOString();
  const daysAgo = (d) => new Date(now - d * 86400 * 1000).toISOString();

  const mkMsg = (id, text, direction, ts) => ({
    id,
    content: text,
    text,
    body: text,
    timestamp: ts,
    created_at: ts,
    direction, // 'inbound' | 'outbound'
    sender: direction === 'inbound' ? 'them' : 'me',
    status: 'delivered',
  });

  const conversations = [
    {
      id: 'cf-msg-1',
      name: 'Priya Sharma',
      contact_name: 'Priya Sharma',
      phone: '+1 (415) 555-0101',
      company: 'Skytech Apparel',
      role: 'VP of Brand Protection',
      avatar_initials: 'PS',
      last_message: 'Got the proposal — looks good. Can we jump on a call Friday?',
      timestamp: minsAgo(8),
      unread_count: 2,
      category: 'sales',
      messages: [
        mkMsg('m1-1', 'Hey — saw your post on counterfeit detection. We had a huge problem with knockoffs last quarter on TikTok Shop.', 'inbound', hoursAgo(26)),
        mkMsg('m1-2', 'Thanks for reaching out, Priya! Happy to help — what brands are you protecting?', 'outbound', hoursAgo(25)),
        mkMsg('m1-3', 'Mainly Skytech Athletic. We lost an estimated $400k in Q3 to fakes alone.', 'inbound', hoursAgo(24)),
        mkMsg('m1-4', "That's painful. I'll put together a tailored proposal — typical clients see 80%+ takedown success within 30 days.", 'outbound', hoursAgo(23)),
        mkMsg('m1-5', 'Sent the deck to your inbox. Includes pricing for the 12-mo enterprise tier.', 'outbound', hoursAgo(2)),
        mkMsg('m1-6', 'Got the proposal — looks good. Can we jump on a call Friday?', 'inbound', minsAgo(8)),
        mkMsg('m1-7', 'Also, do you handle Amazon counterfeits or just social platforms?', 'inbound', minsAgo(7)),
      ],
    },
    {
      id: 'cf-msg-2',
      name: 'Marcus Webb',
      contact_name: 'Marcus Webb',
      phone: '+1 (212) 555-0142',
      company: 'Velvet Lux',
      role: 'Head of Operations',
      avatar_initials: 'MW',
      last_message: 'Three new luxury counterfeit listings flagged on DHGate this morning.',
      timestamp: minsAgo(42),
      unread_count: 1,
      category: 'client',
      messages: [
        mkMsg('m2-1', 'Morning! Quick update on the Velvet Lux account.', 'inbound', hoursAgo(2)),
        mkMsg('m2-2', 'Three new luxury counterfeit listings flagged on DHGate this morning.', 'inbound', minsAgo(42)),
        mkMsg('m2-3', "Reviewing now — I'll have takedowns filed by EOD.", 'outbound', minsAgo(35)),
      ],
    },
    {
      id: 'cf-msg-3',
      name: 'Jin Park',
      contact_name: 'Jin Park',
      phone: '+1 (646) 555-0177',
      company: 'Counterfind',
      role: 'Senior Operations Lead',
      avatar_initials: 'JP',
      last_message: 'Nike spike — 14 listings hit pending review in the last hour.',
      timestamp: hoursAgo(1),
      unread_count: 3,
      category: 'internal',
      messages: [
        mkMsg('m3-1', 'Heads up — Nike spike — 14 listings hit pending review in the last hour.', 'inbound', hoursAgo(1)),
        mkMsg('m3-2', 'Probably the Air Max 1 colourway drop — counterfeiters track those.', 'inbound', minsAgo(58)),
        mkMsg('m3-3', "I've bumped them all to urgent priority and assigned to Sarah's team.", 'inbound', minsAgo(50)),
      ],
    },
    {
      id: 'cf-msg-4',
      name: 'Hannah Rivera, Esq.',
      contact_name: 'Hannah Rivera, Esq.',
      phone: '+1 (917) 555-0193',
      company: 'Rivera & Cole IP Law',
      role: 'Partner, IP Litigation',
      avatar_initials: 'HR',
      last_message: 'Amazon rejected the Marigold Cosmetics takedown — need notarised brand-authorisation letter.',
      timestamp: hoursAgo(3),
      unread_count: 1,
      category: 'legal',
      messages: [
        mkMsg('m4-1', 'FYI: Amazon rejected the Marigold Cosmetics takedown — need notarised brand-authorisation letter.', 'inbound', hoursAgo(3)),
        mkMsg('m4-2', "Got it. I'll have Velvet Lux's CMO sign one this afternoon.", 'outbound', hoursAgo(2)),
        mkMsg('m4-3', 'Perfect. Once notarised, refile via the Brand Registry portal — usually 48-72h to action.', 'inbound', hoursAgo(2)),
      ],
    },
    {
      id: 'cf-msg-5',
      name: 'Devon Cole',
      contact_name: 'Devon Cole',
      phone: '+1 (310) 555-0204',
      company: 'Apex Electronics',
      role: 'Chief Marketing Officer',
      avatar_initials: 'DC',
      last_message: 'Renewal contract sent over for signature.',
      timestamp: hoursAgo(5),
      unread_count: 0,
      category: 'client',
      messages: [
        mkMsg('m5-1', 'Hey team, ready to renew for another 12 months.', 'inbound', hoursAgo(8)),
        mkMsg('m5-2', "Awesome to hear, Devon! Sending the updated contract over now.", 'outbound', hoursAgo(7)),
        mkMsg('m5-3', 'Renewal contract sent over for signature.', 'outbound', hoursAgo(5)),
      ],
    },
    {
      id: 'cf-msg-6',
      name: 'Sarah Lin',
      contact_name: 'Sarah Lin',
      phone: '+1 (415) 555-0218',
      company: 'Counterfind',
      role: 'Enterprise Sales Director',
      avatar_initials: 'SL',
      last_message: 'Closed the Bristol & Co deal — $48k MRR for 24 months 🎉',
      timestamp: hoursAgo(7),
      unread_count: 0,
      category: 'internal',
      messages: [
        mkMsg('m6-1', "Wait until you hear this…", 'inbound', hoursAgo(7)),
        mkMsg('m6-2', 'Closed the Bristol & Co deal — $48k MRR for 24 months 🎉', 'inbound', hoursAgo(7)),
        mkMsg('m6-3', 'AMAZING. That puts us over Q1 target. Drinks on the company.', 'outbound', hoursAgo(6)),
      ],
    },
    {
      id: 'cf-msg-7',
      name: 'Alex Chen',
      contact_name: 'Alex Chen',
      phone: '+1 (628) 555-0246',
      company: 'Halcyon Watches',
      role: 'Founder & CEO',
      avatar_initials: 'AC',
      last_message: 'Saw a Rolex Submariner clone selling on Etsy for $89. Reported it via the form.',
      timestamp: daysAgo(1),
      unread_count: 0,
      category: 'client',
      messages: [
        mkMsg('m7-1', 'Saw a Rolex Submariner clone selling on Etsy for $89. Reported it via the form.', 'inbound', daysAgo(1)),
        mkMsg('m7-2', "Received! Already in the queue — AI scored it 97% counterfeit. We'll have it removed within 48h.", 'outbound', daysAgo(1)),
        mkMsg('m7-3', 'Incredible turnaround. Thanks team.', 'inbound', daysAgo(1)),
      ],
    },
  ];

  return conversations;
};


const RealEstateManagingPartnerCommunications = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const messagesEndRef = useRef(null);
  const dashboardMessagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const [loading, setLoading] = useState(true);
  
  // User ID from auth context - dynamic based on logged-in user
  const userId = useUserId();
  const userEmail = useUserEmail();
  const isCounterfind = (userEmail || '').toLowerCase() === 'counterfind@operatr.com';
  const isJaden = (userEmail || '').toLowerCase() === 'jaden@gmail.com';
  const { user: authUser } = useAuth();
  
  // Demo mode support
  const { isDemoMode, demoData, triggerViewOnlyWarning } = useDemoMode();
  
  // Use the Nylas accounts hook for unified account management (refactored)
  // Using hideAccount for page-specific tab removal (doesn't affect other pages)
  const { 
    accounts: nylasConnectedAccounts, 
    loading: loadingNylasAccounts,
    hideAccount: hideEmailAccountTab,
    fetchAccounts: refetchNylasAccounts,
    connectAccount: connectNylasAccount
  } = useNylasAccounts(userId, 'email');
  
  const [messageInput, setMessageInput] = useState('');
  const [emailInput, setEmailInput] = useState('');
  // Default tab: jaden@gmail.com leads with Email; everyone else starts on Messages.
  const [activeTab, setActiveTab] = useState(isJaden ? 'email' : 'messages'); // 'dashboard', 'email', 'messages', 'calls', 'meeting'
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [mobileShowChat, setMobileShowChat] = useState(false); // Mobile: show chat view instead of list
  const [conversations, setConversations] = useState([]);
  const [calls, setCalls] = useState([]);
  const [messages, setMessages] = useState([]);
  const [previousMessageCount, setPreviousMessageCount] = useState(0);
  const [isUserScrolling, setIsUserScrolling] = useState(false);
  const [justOpenedConversation, setJustOpenedConversation] = useState(false);
  const [showAIDraft, setShowAIDraft] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showComposeModal, setShowComposeModal] = useState(false);
  const [showEmailComposeModal, setShowEmailComposeModal] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [emailCc, setEmailCc] = useState('');
  const [emailBcc, setEmailBcc] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [contactImages, setContactImages] = useState({}); // Map of phone/name to avatar URL
  
  // Dashboard messaging state (internal app users)
  const [dashboardUsers, setDashboardUsers] = useState([]);
  const [dashboardConversations, setDashboardConversations] = useState([]);
  const [selectedDashboardUser, setSelectedDashboardUser] = useState(null);
  const [dashboardMessages, setDashboardMessages] = useState([]);
  const [dashboardMessageInput, setDashboardMessageInput] = useState('');
  const [showDashboardEmojiPicker, setShowDashboardEmojiPicker] = useState(false);
  const [dashboardAttachment, setDashboardAttachment] = useState(null);
  const dashboardAttachmentInputRef = React.useRef(null);
  const dashboardEmojiPickerRef = React.useRef(null);
  const [dashboardLoading, setDashboardLoading] = useState(false);

  // Auto-scroll to the bottom of the dashboard-contact chat whenever
  // the active conversation changes or new messages arrive.
  useEffect(() => {
    if (!selectedDashboardUser) return;
    const id = requestAnimationFrame(() => {
      dashboardMessagesEndRef.current?.scrollIntoView({ behavior: 'auto', block: 'end' });
    });
    return () => cancelAnimationFrame(id);
  }, [selectedDashboardUser?.id, dashboardMessages.length, dashboardLoading]);
  const [activeFolder, setActiveFolder] = useState('inbox');
  const [isScanning, setIsScanning] = useState(false);
  const [categoryCounts, setCategoryCounts] = useState({});
  
  // Chat notifications state
  const [chatNotifications, setChatNotifications] = useState([]);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [showNotificationsPanel, setShowNotificationsPanel] = useState(false);
  const [teamMembersCollapsed, setTeamMembersCollapsed] = useState(false);
  const [sendingInvite, setSendingInvite] = useState(null);

  // Teams (group chats) state
  const [myTeams, setMyTeams] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [teamMessages, setTeamMessages] = useState([]);
  const [teamMessageInput, setTeamMessageInput] = useState('');
  const [teamsCollapsed, setTeamsCollapsed] = useState(false);

  // Dashboard category filter state
  const [dashboardCategoryFilter, setDashboardCategoryFilter] = useState('all');
  
  // Voice client hook for real-time incoming calls
  const {
    isReady: voiceReady,
    isConnected: voiceConnected,
    activeCall: voiceActiveCall,
    incomingCall,
    isMuted,
    acceptCall,
    rejectCall,
    makeCall: voiceMakeCall,
    endCall: endVoiceCall,
    toggleMute,
    ignoreCall,
    error: voiceError
  } = useVoiceClient('dashboard_user');
  // Restore activeEmailAccount from localStorage
  const [activeEmailAccount, setActiveEmailAccount] = useState(() => {
    try {
      return localStorage.getItem('activeEmailAccount') || 'outlook';
    } catch {
      return 'outlook';
    }
  });
  const [activeCallFilter, setActiveCallFilter] = useState('all');
  const [activeCallCategory, setActiveCallCategory] = useState('all'); // for Family, Friends, Business tabs
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');
  const [scheduledMessages, setScheduledMessages] = useState([]);
  const [showScheduledTracker, setShowScheduledTracker] = useState(false);
  const [messageAttachment, setMessageAttachment] = useState(null);
  const [uploadingMessageAttachment, setUploadingMessageAttachment] = useState(false);
  const messageAttachmentInputRef = React.useRef(null);
  const messageTextareaRef = React.useRef(null);
  const emojiPickerRef = React.useRef(null);

  // Close emoji picker when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (e) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(e.target)) {
        const btn = document.querySelector('[data-testid="emoji-btn"]');
        if (!btn || !btn.contains(e.target)) {
          setShowMsgEmojiPicker(false);
        }
      }
      if (dashboardEmojiPickerRef.current && !dashboardEmojiPickerRef.current.contains(e.target)) {
        const btn = document.querySelector('[data-testid="dashboard-emoji-btn"]');
        if (!btn || !btn.contains(e.target)) {
          setShowDashboardEmojiPicker(false);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  
  // Email pagination state
  const [emailCurrentPage, setEmailCurrentPage] = useState(1);
  const emailsPerPage = 50;
  
  // Email search state
  const [emailSearchQuery, setEmailSearchQuery] = useState('');
  
  // Outlook email state - initialize from localStorage for persistence across page navigation
  const [outlookEmails, setOutlookEmails] = useState(() => {
    try {
      const cached = localStorage.getItem('outlookEmails');
      return cached ? JSON.parse(cached) : [];
    } catch { return []; }
  });
  const [outlookEmailsCache, setOutlookEmailsCache] = useState(() => {
    try {
      const cached = localStorage.getItem('outlookEmailsCache');
      return cached ? JSON.parse(cached) : {};
    } catch { return {}; }
  });
  const [outlookLoading, setOutlookLoading] = useState(false);
  const [outlookConnected, setOutlookConnected] = useState(false); // Deprecated - use Nylas
  const [hasInitiallyFetched, setHasInitiallyFetched] = useState(() => {
    try {
      return localStorage.getItem('outlookHasInitiallyFetched') === 'true';
    } catch { return false; }
  });
  
  // Gmail connection state
  const [gmailConnected, setGmailConnected] = useState(false);
  const [gmailLoading, setGmailLoading] = useState(false);
  const [gmailEmail, setGmailEmail] = useState('');
  
  // Draft Center state
  const [showDraftCenter, setShowDraftCenter] = useState(false);
  const [slackChannelsForDrafts, setSlackChannelsForDrafts] = useState([]);
  
  const [gmailEmails, setGmailEmails] = useState(() => {
    // Load from localStorage cache on mount
    try {
      const cached = localStorage.getItem('gmailEmailsCache');
      if (cached) {
        const parsedCache = JSON.parse(cached);
        // Get emails for the default folder (inbox)
        const savedAccount = localStorage.getItem('activeEmailAccount');
        const cacheKey = `${savedAccount || 'gmail'}_inbox`;
        if (parsedCache[cacheKey] && parsedCache[cacheKey].length > 0) {
          console.log(`📧 Gmail: Loaded ${parsedCache[cacheKey].length} cached emails on mount`);
          return parsedCache[cacheKey];
        }
      }
    } catch (e) { /* ignored */ }
    return [];
  });
  // Restore gmailEmailsCache from localStorage on mount
  const [gmailEmailsCache, setGmailEmailsCache] = useState(() => {
    try {
      const cached = localStorage.getItem('gmailEmailsCache');
      return cached ? JSON.parse(cached) : {};
    } catch {
      return {};
    }
  });
  // Track which Gmail account/folder combos have been fetched this session
  const [fetchedGmailKeys, setFetchedGmailKeys] = useState(() => {
    try {
      const cached = localStorage.getItem('gmailEmailsCache');
      if (cached) {
        return new Set(Object.keys(JSON.parse(cached)));
      }
    } catch (e) { /* ignored */ }
    return new Set();
  });
  
  // Nylas universal email state
  const [nylasEmails, setNylasEmails] = useState(() => {
    // Load cached emails from localStorage on initial render
    try {
      const cached = localStorage.getItem('nylas_emails_cache');
      if (cached) {
        const parsed = JSON.parse(cached);
        console.log('📧 Loaded cached emails from localStorage:', Object.keys(parsed).length, 'accounts');
        // Re-categorize any emails that have 'Other' category (old cache)
        const emails = parsed.emails || [];
        return emails;
      }
    } catch (e) {
      console.log('📧 No cached emails found');
    }
    return [];
  });
  const [nylasLoading, setNylasLoading] = useState(false);
  const [nylasEmailsCache, setNylasEmailsCache] = useState(() => {
    // Load cache from localStorage
    try {
      const cached = localStorage.getItem('nylas_emails_cache');
      if (cached) {
        return JSON.parse(cached).cache || {};
      }
    } catch (e) { /* ignored */ }
    return {};
  });
  // Track which account/folder combos have been fetched this session to avoid redundant fetches
  const [fetchedEmailKeys, setFetchedEmailKeys] = useState(() => {
    // Initialize from localStorage cache - mark those as already fetched
    try {
      const cached = localStorage.getItem('nylas_emails_cache');
      if (cached) {
        const parsed = JSON.parse(cached);
        if (parsed.cache) {
          return new Set(Object.keys(parsed.cache));
        }
      }
    } catch (e) { /* ignored */ }
    return new Set();
  });
  
  // Email account rename state
  const [renamingAccountId, setRenamingAccountId] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  
  const [conversationToDelete, setConversationToDelete] = useState(null);
  const [showAIDraftMessage, setShowAIDraftMessage] = useState(false);
  const [showMsgEmojiPicker, setShowMsgEmojiPicker] = useState(false);
  const [showAIDraftEmail, setShowAIDraftEmail] = useState(false);
  const [aiDraftEmailText, setAiDraftEmailText] = useState('');
  const [aiDraftMessageText, setAiDraftMessageText] = useState('');
  const [generatingAIReply, setGeneratingAIReply] = useState(false);
  const [openMessageDropdown, setOpenMessageDropdown] = useState(null);
  const [openEmailDropdown, setOpenEmailDropdown] = useState(null);
  const [swipeOffset, setSwipeOffset] = useState({});
  const [showEmojiPicker, setShowEmojiPicker] = useState(null);
  const [messageReactions, setMessageReactions] = useState({});
  const [showGroupMembers, setShowGroupMembers] = useState(false);
  const [showAddContactToast, setShowAddContactToast] = useState(null);
  const [flaggedEmails, setFlaggedEmails] = useState({});
  const [pinnedConversations, setPinnedConversations] = useState({});
  const [showCallModal, setShowCallModal] = useState(false);
  const [showFaceTimeModal, setShowFaceTimeModal] = useState(false);
  const [activeCall, setActiveCall] = useState(null);
  const [callDuration, setCallDuration] = useState(0);
  const [isCalling, setIsCalling] = useState(false);
  const [showNewCallModal, setShowNewCallModal] = useState(false);
  const [activeMessagesCategory, setActiveMessagesCategory] = useState('all');
  const [selectedCallDetail, setSelectedCallDetail] = useState(null);  // For expanded call detail view
  const [callOutcomes, setCallOutcomes] = useState({});  // Track call outcomes by call_sid
  const [scheduledCountdown, setScheduledCountdown] = useState(0); // For forcing re-render of countdown
  
  // Default categories for each type
  const defaultMessageCategories = ['urgent', 'follow-up', 'client', 'report', 'meeting', 'travel', 'general', 'other'];
  const defaultCallCategories = ['family', 'friends', 'business', 'favorites'];
  const defaultEmailFolders = ['inbox', 'sent', 'drafts', 'flagged', 'archive', 'trash', 'spam'];
  const defaultEmailCategories = []; // Empty by default - user adds their own categories
  
  // Use category tabs hooks for unified category management (refactored)
  // Now syncs to database for persistent storage across deployments
  const {
    categories: messageCategories,
    setCategories: setMessageCategories,
    addCategory: addMessageCategory,
    removeCategory: removeMessageCategory,
    reorderCategories: reorderMessageCategories,
    editingCategory: editingMsgCategory,
    editValue: editMsgValue,
    setEditValue: setEditMsgValue,
    startEditing: startEditingMsg,
    saveEditing: saveEditingMsg,
    cancelEditing: cancelEditingMsg
  } = useCategoryTabs('message_categories', defaultMessageCategories, { userId, syncToDatabase: true });
  
  const {
    categories: callCategories,
    setCategories: setCallCategories,
    addCategory: addCallCategory,
    removeCategory: removeCallCategory,
    reorderCategories: reorderCallCategories,
    editingCategory: editingCallCategory,
    editValue: editCallValue,
    setEditValue: setEditCallValue,
    startEditing: startEditingCall,
    saveEditing: saveEditingCall,
    cancelEditing: cancelEditingCall
  } = useCategoryTabs('call_categories', defaultCallCategories, { userId, syncToDatabase: true });
  
  const {
    categories: emailFolders,
    setCategories: setEmailFolders,
    addCategory: addEmailFolder,
    removeCategory: removeEmailFolder,
    reorderCategories: reorderEmailFolders,
    editingCategory: editingEmailFolder,
    editValue: editFolderValue,
    setEditValue: setEditFolderValue,
    startEditing: startEditingFolder,
    saveEditing: saveEditingFolder,
    cancelEditing: cancelEditingFolder
  } = useCategoryTabs('email_folders', defaultEmailFolders, { userId, syncToDatabase: true });
  
  // Email categories - stored per email account for independent category tabs
  const [emailCategoriesByAccount, setEmailCategoriesByAccount] = useState({});
  const [emailKeywordsByAccount, setEmailKeywordsByAccount] = useState({});
  const [emailAssignmentsByAccount, setEmailAssignmentsByAccount] = useState({});
  
  // Get current account's categories (or defaults)
  const currentAccountCategories = emailCategoriesByAccount[activeEmailAccount] || defaultEmailCategories;
  const currentAccountKeywords = emailKeywordsByAccount[activeEmailAccount] || {};
  const currentAccountAssignments = emailAssignmentsByAccount[activeEmailAccount] || {};
  
  // Load categories for the active email account from database
  useEffect(() => {
    const loadAccountCategories = async () => {
      if (!userId || !activeEmailAccount) return;
      
      try {
        // Load categories for this account
        const catRes = await fetch(`${window.location.origin}/api/user-profile/settings/email_categories_${activeEmailAccount}?user_id=${userId}`);
        if (catRes.ok) {
          const data = await catRes.json();
          if (data.exists && data.value) {
            setEmailCategoriesByAccount(prev => ({
              ...prev,
              [activeEmailAccount]: data.value
            }));
          }
        }
        
        // Load keywords for this account
        const kwRes = await fetch(`${window.location.origin}/api/email-categories/keywords?user_id=${userId}_${activeEmailAccount}`);
        if (kwRes.ok) {
          const data = await kwRes.json();
          if (data.success && data.keywords) {
            setEmailKeywordsByAccount(prev => ({
              ...prev,
              [activeEmailAccount]: data.keywords
            }));
          }
        }
        
        // Load assignments for this account
        const assignRes = await fetch(`${window.location.origin}/api/email-categories/assignments?user_id=${userId}_${activeEmailAccount}`);
        if (assignRes.ok) {
          const data = await assignRes.json();
          if (data.success && data.assignments) {
            setEmailAssignmentsByAccount(prev => ({
              ...prev,
              [activeEmailAccount]: data.assignments
            }));
          }
        }
      } catch (error) {
        console.error('Error loading account categories:', error);
      }
    };
    
    loadAccountCategories();
  }, [userId, activeEmailAccount]);
  
  // Save categories for current account
  const saveAccountCategories = async (categories) => {
    if (!userId || !activeEmailAccount) return;
    
    setEmailCategoriesByAccount(prev => ({
      ...prev,
      [activeEmailAccount]: categories
    }));
    
    try {
      await fetch(`${window.location.origin}/api/user-profile/settings/email_categories_${activeEmailAccount}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          key: `email_categories_${activeEmailAccount}`,
          value: categories,
          user_id: userId
        })
      });
    } catch (error) {
      console.error('Error saving account categories:', error);
    }
  };
  
  // Save keywords for current account
  const saveAccountKeywords = async (keywords) => {
    if (!userId || !activeEmailAccount) return;
    
    setEmailKeywordsByAccount(prev => ({
      ...prev,
      [activeEmailAccount]: keywords
    }));
    
    try {
      await fetch(`${window.location.origin}/api/email-categories/keywords?user_id=${userId}_${activeEmailAccount}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(keywords)
      });
    } catch (error) {
      console.error('Error saving account keywords:', error);
    }
  };
  
  // Save assignments for current account
  const saveAccountAssignments = async (assignments) => {
    if (!userId || !activeEmailAccount) return;
    
    setEmailAssignmentsByAccount(prev => ({
      ...prev,
      [activeEmailAccount]: assignments
    }));
    
    try {
      await fetch(`${window.location.origin}/api/email-categories/assignments/bulk?user_id=${userId}_${activeEmailAccount}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ assignments })
      });
    } catch (error) {
      console.error('Error saving account assignments:', error);
    }
  };
  
  // Wrapper functions for category management (account-specific)
  const addEmailCategory = (category) => {
    if (!currentAccountCategories.includes(category)) {
      const updated = [...currentAccountCategories, category];
      saveAccountCategories(updated);
      return true;
    }
    return false;
  };
  
  const removeEmailCategory = (category) => {
    const updated = currentAccountCategories.filter(c => c !== category);
    saveAccountCategories(updated);
  };
  
  const reorderEmailCategories = (newOrder) => {
    saveAccountCategories(newOrder);
  };
  
  // Alias for compatibility
  const emailCategories = currentAccountCategories;
  const setEmailCategories = saveAccountCategories;
  const emailCategoryKeywords = currentAccountKeywords;
  const setEmailCategoryKeywords = (keywords) => {
    setEmailKeywordsByAccount(prev => ({
      ...prev,
      [activeEmailAccount]: keywords
    }));
  };
  const saveEmailCategoryKeywords = saveAccountKeywords;
  const emailCategoryAssignments = currentAccountAssignments;
  const setEmailCategoryAssignments = (assignments) => {
    setEmailAssignmentsByAccount(prev => ({
      ...prev,
      [activeEmailAccount]: assignments
    }));
  };
  
  // Custom keywords for categories (used for auto-categorization)
  const [customCategoryKeywords, setCustomCategoryKeywords] = useState(() => {
    try {
      const saved = localStorage.getItem('custom_category_keywords');
      return saved ? JSON.parse(saved) : {};
    } catch { return {}; }
  });
  
  // Persistent message category assignments (messageId -> categoryKey)
  // This persists the scan results so they survive page refreshes
  const [messageCategoryAssignments, setMessageCategoryAssignments] = useState(() => {
    try {
      const saved = localStorage.getItem('message_category_assignments');
      return saved ? JSON.parse(saved) : {};
    } catch { return {}; }
  });

  // Account-specific scanning state
  const [scanningEmails, setScanningEmails] = useState(false);
  const [newCategoryKeywords, setNewCategoryKeywords] = useState('');

  // Scan emails for the CURRENT account and categorize based on keywords
  const scanAndCategorizeEmails = async () => {
    // Get emails for the current active account directly (don't rely on external variable)
    let accountEmails = [];
    
    if (activeEmailAccount === 'outlook') {
      accountEmails = outlookEmails || [];
    } else {
      const activeAccount = emailAccounts.find(acc => acc.id === activeEmailAccount);
      if (activeAccount && activeAccount.type === 'gmail') {
        accountEmails = gmailEmails || [];
      } else if (activeAccount && activeAccount.type === 'nylas') {
        accountEmails = nylasEmails || [];
      }
    }
    
    // Get current categories for this account
    const categoriesToUse = emailCategoriesByAccount[activeEmailAccount] || defaultEmailCategories;
    const keywordsToUse = emailKeywordsByAccount[activeEmailAccount] || {};
    
    console.log('📧 SCAN DEBUG ================================');
    console.log('📧 Active account:', activeEmailAccount);
    console.log('📧 Email count:', accountEmails.length);
    console.log('📧 Categories:', categoriesToUse);
    console.log('📧 Keywords config:', keywordsToUse);
    console.log('📧 First email sample:', accountEmails[0] ? {
      id: accountEmails[0].id,
      subject: accountEmails[0].subject?.substring(0, 50),
      from: accountEmails[0].from
    } : 'NO EMAILS');
    console.log('📧 ==========================================');
    
    if (!accountEmails.length) {
      toast.error('No emails to scan for this account. Make sure emails are loaded.');
      return;
    }
    
    // Build complete keyword map: combine saved keywords with auto-generated ones
    const completeKeywordMap = {};
    
    // First, add all email categories with at least their name as a keyword
    for (const category of categoriesToUse) {
      if (category === 'all' || category === 'other') continue;
      
      // Start with saved keywords or empty array
      const savedKeywords = keywordsToUse[category] || [];
      
      // Auto-generate additional keywords from category name
      const autoKeywords = generateKeywordsFromCategory(formatCategoryName(category));
      
      // Also add the raw category key as a keyword
      const categoryNameKeywords = [
        category.toLowerCase(),
        formatCategoryName(category).toLowerCase()
      ];
      
      // Combine all keywords, remove duplicates
      const allKeywords = [...new Set([
        ...savedKeywords.map(k => k.toLowerCase().trim()),
        ...autoKeywords.map(k => k.toLowerCase().trim()),
        ...categoryNameKeywords
      ])].filter(k => k && k.length >= 2);
      
      completeKeywordMap[category] = allKeywords;
      console.log(`📁 Category "${category}" → ${allKeywords.length} keywords:`, allKeywords.slice(0, 8).join(', '));
    }
    
    if (Object.keys(completeKeywordMap).length === 0) {
      toast.error('No categories to scan. Create some category tabs first (other than "all" and "other").');
      return;
    }
    
    setScanningEmails(true);
    const newAssignments = {};
    let categorizedCount = 0;
    let otherCount = 0;
    
    // Simple keyword matching helper
    const textContainsKeyword = (text, keyword) => {
      if (!text || !keyword) return false;
      return text.toLowerCase().includes(keyword.toLowerCase());
    };
    
    // Helper to extract domain from email
    const getDomain = (emailAddr) => {
      if (!emailAddr) return '';
      const match = emailAddr.match(/@([^@]+)$/);
      return match ? match[1].toLowerCase() : '';
    };
    
    try {
      for (let i = 0; i < accountEmails.length; i++) {
        const email = accountEmails[i];
        const emailId = email.id;
        if (!emailId) continue;
        
        // Extract all searchable text - handle various email formats
        const subject = (email.subject || '').toLowerCase();
        const fromEmail = (
          email.from?.[0]?.email || 
          email.from?.email || 
          (typeof email.from === 'string' ? email.from : '')
        ).toLowerCase();
        const fromName = (
          email.from?.[0]?.name || 
          email.from?.name || 
          ''
        ).toLowerCase();
        const fromDomain = getDomain(fromEmail);
        const snippet = (email.snippet || email.preview || email.body_preview || '').toLowerCase();
        const body = (email.body || email.text_body || email.html_body || email.bodyPreview || '').toLowerCase();
        const labels = (email.labels || email.folders || email.categories || [])
          .map(l => typeof l === 'string' ? l.toLowerCase() : (l?.name || '').toLowerCase())
          .join(' ');
        
        // Debug first 5 emails
        if (i < 5) {
          console.log(`📧 Email #${i+1}:`, {
            subject: subject.substring(0, 60),
            from: fromEmail,
            fromName: fromName,
            domain: fromDomain
          });
        }
        
        // Score each category
        let bestCategory = null;
        let bestScore = 0;
        let bestMatches = [];
        
        for (const [categoryId, keywords] of Object.entries(completeKeywordMap)) {
          if (!keywords || keywords.length === 0) continue;
          
          let score = 0;
          let matchedKeywords = [];
          
          for (const kw of keywords) {
            if (!kw || kw.length < 2) continue;
            
            // Subject matches (highest priority)
            if (textContainsKeyword(subject, kw)) {
              score += 25;
              matchedKeywords.push(`SUBJ:${kw}`);
            }
            
            // Sender email matches
            if (textContainsKeyword(fromEmail, kw)) {
              score += 20;
              matchedKeywords.push(`FROM:${kw}`);
            }
            
            // Sender domain matches (e.g., "emergent" in "emergentagent.com")
            if (textContainsKeyword(fromDomain, kw)) {
              score += 15;
              matchedKeywords.push(`DOMAIN:${kw}`);
            }
            
            // Sender name matches
            if (textContainsKeyword(fromName, kw)) {
              score += 12;
              matchedKeywords.push(`NAME:${kw}`);
            }
            
            // Labels/folders match
            if (textContainsKeyword(labels, kw)) {
              score += 10;
              matchedKeywords.push(`LABEL:${kw}`);
            }
            
            // Snippet/preview matches
            if (textContainsKeyword(snippet, kw)) {
              score += 8;
              matchedKeywords.push(`SNIP:${kw}`);
            }
            
            // Body matches
            if (body && textContainsKeyword(body, kw)) {
              score += 3;
              matchedKeywords.push(`BODY:${kw}`);
            }
          }
          
          // Bonus for multiple matches
          if (matchedKeywords.length >= 2) {
            score += matchedKeywords.length * 5;
          }
          
          if (score > bestScore) {
            bestScore = score;
            bestCategory = categoryId;
            bestMatches = matchedKeywords;
          }
        }
        
        // Assign to best matching category (minimum score of 3)
        if (bestCategory && bestScore >= 3) {
          newAssignments[emailId] = bestCategory;
          categorizedCount++;
          if (i < 10) {
            console.log(`✅ [${bestScore}] "${subject.substring(0,40)}..." → ${bestCategory} (${bestMatches.join(', ')})`);
          }
        } else {
          newAssignments[emailId] = 'other';
          otherCount++;
          if (i < 10) {
            console.log(`❌ [${bestScore}] "${subject.substring(0,40)}..." → other (no match)`);
          }
        }
      }
      
      // Save to account-specific state and database
      setEmailAssignmentsByAccount(prev => ({
        ...prev,
        [activeEmailAccount]: newAssignments
      }));
      
      // Save to database with account-specific key
      const response = await fetch(`${window.location.origin}/api/email-categories/assignments/bulk?user_id=${userId}_${activeEmailAccount}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ assignments: newAssignments })
      });
      
      if (response.ok) {
        const totalScanned = categorizedCount + otherCount;
        toast.success(`Scanned ${totalScanned} emails for ${activeEmailAccount}: ${categorizedCount} categorized, ${otherCount} in Other`);
        
        if (otherCount > categorizedCount) {
          toast.info('Tip: Add more specific keywords to your categories for better matching', { duration: 5000 });
        }
      } else {
        toast.error('Failed to save categorization');
      }
    } catch (error) {
      console.error('Error scanning emails:', error);
      toast.error('Error scanning emails');
    } finally {
      setScanningEmails(false);
    }
  };

  // Update single email category for current account
  const updateEmailCategory = async (emailId, category) => {
    const newAssignments = { ...emailCategoryAssignments, [emailId]: category };
    setEmailAssignmentsByAccount(prev => ({
      ...prev,
      [activeEmailAccount]: newAssignments
    }));
    
    try {
      await fetch(`${window.location.origin}/api/email-categories/assignments?user_id=${userId}_${activeEmailAccount}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email_id: emailId, category })
      });
    } catch (error) {
      console.error('Error updating email category:', error);
    }
  };
  
  // State for editing category keywords
  const [editingKeywordsCategory, setEditingKeywordsCategory] = useState(null);
  const [keywordsInputValue, setKeywordsInputValue] = useState('');
  const [scanningCategory, setScanningCategory] = useState(null);
  
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [categoryModalType, setCategoryModalType] = useState('messages'); // 'messages', 'calls', 'emails', 'emailCategories'
  const [newCategoryName, setNewCategoryName] = useState('');
  // Legacy editing state - now managed by hooks, kept for backward compatibility
  const [editingCategory, setEditingCategory] = useState(null);
  const [editCategoryValue, setEditCategoryValue] = useState('');
  
  // Generate keywords from category name using AI-like expansion
  const generateKeywordsFromCategory = (categoryName) => {
    if (!categoryName || typeof categoryName !== 'string') return [];
    const name = categoryName.toLowerCase().replace(/-/g, ' ');
    const baseKeywords = [name];
    
    // Common keyword expansions based on category patterns
    const keywordExpansions = {
      'finance': ['payment', 'bank', 'invoice', 'statement', 'transaction', 'credit', 'debit', 'account', 'balance', 'transfer', 'wire', 'ach', 'financial', 'budget', 'expense', 'tax', 'receipt'],
      'shopping': ['order', 'purchase', 'buy', 'cart', 'checkout', 'delivery', 'shipping', 'tracking', 'amazon', 'ebay', 'store', 'product', 'item', 'sale', 'discount'],
      'social': ['facebook', 'instagram', 'twitter', 'linkedin', 'tiktok', 'snapchat', 'friend', 'follow', 'like', 'comment', 'share', 'post', 'notification'],
      'work': ['project', 'task', 'deadline', 'assignment', 'report', 'presentation', 'office', 'team', 'manager', 'colleague', 'workplace', 'job', 'career'],
      'health': ['doctor', 'appointment', 'medical', 'prescription', 'pharmacy', 'insurance', 'hospital', 'clinic', 'wellness', 'fitness', 'healthcare', 'patient'],
      'education': ['school', 'university', 'course', 'class', 'assignment', 'homework', 'exam', 'grade', 'student', 'teacher', 'professor', 'lecture', 'degree'],
      'entertainment': ['movie', 'music', 'concert', 'show', 'ticket', 'event', 'streaming', 'netflix', 'spotify', 'youtube', 'game', 'play'],
      'food': ['restaurant', 'delivery', 'order', 'menu', 'reservation', 'doordash', 'ubereats', 'grubhub', 'recipe', 'dining'],
      'sports': ['game', 'match', 'score', 'team', 'player', 'league', 'championship', 'workout', 'gym', 'fitness', 'training'],
      'news': ['breaking', 'headline', 'update', 'alert', 'newsletter', 'digest', 'daily', 'weekly', 'report', 'announcement'],
      'promotions': ['sale', 'discount', 'offer', 'deal', 'coupon', 'promo', 'limited time', 'special', 'exclusive', 'save', 'off'],
      'bills': ['bill', 'due', 'payment', 'utility', 'electric', 'water', 'gas', 'internet', 'phone', 'rent', 'mortgage', 'subscription'],
      'personal': ['friend', 'family', 'birthday', 'party', 'invitation', 'wedding', 'anniversary', 'holiday', 'vacation', 'plans'],
      'legal': ['contract', 'agreement', 'legal', 'attorney', 'lawyer', 'court', 'law', 'lawsuit', 'settlement', 'document'],
      'real-estate': ['property', 'house', 'apartment', 'listing', 'rent', 'lease', 'mortgage', 'realtor', 'showing', 'closing'],
      'investment': ['stock', 'invest', 'portfolio', 'dividend', 'trade', 'market', 'fund', 'retirement', '401k', 'ira', 'crypto', 'bitcoin'],
      'insurance': ['policy', 'claim', 'coverage', 'premium', 'deductible', 'insurance', 'insured', 'benefits'],
      'automotive': ['car', 'vehicle', 'auto', 'service', 'maintenance', 'repair', 'oil change', 'tire', 'dealer', 'registration'],
      'subscription': ['subscribe', 'subscription', 'renew', 'renewal', 'membership', 'plan', 'upgrade', 'downgrade', 'cancel'],
      'security': ['security', 'password', 'login', 'verify', 'authentication', '2fa', 'alert', 'suspicious', 'breach', 'protection']
    };
    
    // Check if the category name matches any known expansion
    for (const [key, keywords] of Object.entries(keywordExpansions)) {
      if (name.includes(key) || key.includes(name)) {
        return [...baseKeywords, ...keywords];
      }
    }
    
    // For custom categories, generate related words
    const words = name.split(' ');
    const related = [];
    words.forEach(word => {
      if (word.length > 2) {
        related.push(word);
        // Add common variations
        if (word.endsWith('s')) related.push(word.slice(0, -1));
        if (word.endsWith('ing')) related.push(word.slice(0, -3));
        if (word.endsWith('ed')) related.push(word.slice(0, -2));
      }
    });
    
    return [...new Set([...baseKeywords, ...related])];
  };
  
  // Messaging Connection State
  const [isMessagingConnected, setIsMessagingConnected] = useState(() => {
    try {
      return localStorage.getItem('messaging_connected') === 'true';
    } catch { return false; }
  });
  const [messagingConnectionType, setMessagingConnectionType] = useState(() => {
    try {
      return localStorage.getItem('messaging_connection_type') || null;
    } catch { return null; }
  });
  const [isConnectPanelCollapsed, setIsConnectPanelCollapsed] = useState(() => {
    try {
      // Default to collapsed (true) unless user explicitly expanded it
      return localStorage.getItem('connect_panel_collapsed') !== 'false';
    } catch { return true; }
  });
  const [showMessagingConnectionModal, setShowMessagingConnectionModal] = useState(false);
  const [showMacSyncModal, setShowMacSyncModal] = useState(false);
  const [twilioAccountSid, setTwilioAccountSid] = useState('');
  const [twilioAuthToken, setTwilioAuthToken] = useState('');
  const [twilioPhoneNumber, setTwilioPhoneNumber] = useState('');
  const [messagingConnectionError, setMessagingConnectionError] = useState('');
  const [connectingMessaging, setConnectingMessaging] = useState(false);
  
  // Handle Twilio connection
  const handleTwilioConnect = async () => {
    if (!twilioAccountSid || !twilioAuthToken || !twilioPhoneNumber) {
      setMessagingConnectionError('Please fill in all Twilio credentials');
      return;
    }
    
    setConnectingMessaging(true);
    setMessagingConnectionError('');
    
    try {
      // Test Twilio connection
      const response = await fetch(`${apiUrl}/api/twilio-sms/test`);
      const data = await response.json();
      
      // Store connection in localStorage
      localStorage.setItem('messaging_connected', 'true');
      localStorage.setItem('messaging_connection_type', 'twilio');
      localStorage.setItem('twilio_phone_number', twilioPhoneNumber);
      
      setIsMessagingConnected(true);
      setMessagingConnectionType('twilio');
      setShowMessagingConnectionModal(false);
    } catch (error) {
      setMessagingConnectionError('Failed to connect to Twilio. Please check your credentials.');
    } finally {
      setConnectingMessaging(false);
    }
  };
  
  // Handle iPhone Shortcuts connection
  const handleShortcutsConnect = () => {
    // For Shortcuts, we just mark as connected since messages sync via webhook
    localStorage.setItem('messaging_connected', 'true');
    localStorage.setItem('messaging_connection_type', 'shortcuts');
    
    setIsMessagingConnected(true);
    setMessagingConnectionType('shortcuts');
    setShowMessagingConnectionModal(false);
  };
  
  // Category Management Functions
  const openCategoryModal = (type) => {
    setCategoryModalType(type);
    setShowCategoryModal(true);
    setNewCategoryName('');
    setNewCategoryKeywords('');
    setEditingCategory(null);
    setEditCategoryValue('');
  };
  
  // Refactored addCategory to use hook methods with keyword generation
  const addCategory = () => {
    if (!newCategoryName || !newCategoryName.trim()) return;
    const categoryKey = (newCategoryName || '').toLowerCase().replace(/\s+/g, '-');
    
    if (categoryModalType === 'messages') {
      if (!messageCategories.includes(categoryKey)) {
        const success = addMessageCategory(categoryKey);
        if (success) {
          // Generate keywords for the new category
          const keywords = generateKeywordsFromCategory(newCategoryName);
          const updatedKeywords = { ...customCategoryKeywords, [categoryKey]: keywords };
          setCustomCategoryKeywords(updatedKeywords);
          localStorage.setItem('custom_category_keywords', JSON.stringify(updatedKeywords));
          toast.success(`Added "${newCategoryName}" category with ${keywords.length} auto-scan keywords`);
        }
      } else {
        toast.error('Category already exists');
      }
    } else if (categoryModalType === 'calls') {
      if (!callCategories.includes(categoryKey)) {
        const success = addCallCategory(categoryKey);
        if (success) {
          toast.success(`Added "${newCategoryName}" category`);
        }
      } else {
        toast.error('Category already exists');
      }
    } else if (categoryModalType === 'emails') {
      if (!emailFolders.includes(categoryKey)) {
        const success = addEmailFolder(categoryKey);
        if (success) {
          toast.success(`Added "${newCategoryName}" folder`);
        }
      } else {
        toast.error('Folder already exists');
      }
    } else if (categoryModalType === 'emailCategories') {
      if (!emailCategories.includes(categoryKey)) {
        const success = addEmailCategory(categoryKey);
        if (success) {
          // Generate keywords from name or use custom keywords
          const autoKeywords = generateKeywordsFromCategory(newCategoryName);
          const customKeywordsArray = newCategoryKeywords.trim() 
            ? newCategoryKeywords.split(',').map(k => k.trim().toLowerCase()).filter(k => k)
            : [];
          const allKeywords = [...new Set([...autoKeywords, ...customKeywordsArray])];
          
          // Save to local state and database
          const updatedKeywords = { ...emailCategoryKeywords, [categoryKey]: allKeywords };
          setEmailCategoryKeywords(updatedKeywords);
          saveEmailCategoryKeywords(updatedKeywords);
          
          // Also save to localStorage as backup
          const updatedCustomKeywords = { ...customCategoryKeywords, [categoryKey]: allKeywords };
          setCustomCategoryKeywords(updatedCustomKeywords);
          localStorage.setItem('custom_category_keywords', JSON.stringify(updatedCustomKeywords));
          
          toast.success(`Added "${newCategoryName}" category with ${allKeywords.length} keywords`);
        }
      } else {
        toast.error('Category already exists');
      }
    }
    setNewCategoryName('');
    setNewCategoryKeywords('');
  };
  
  // Refactored updateCategory to work with hook's category structure
  const updateCategory = (oldKey, newName) => {
    if (!newName || !newName.trim()) return;
    const newKey = (newName || '').toLowerCase().replace(/\s+/g, '-');
    
    if (categoryModalType === 'messages') {
      const updated = messageCategories.map(c => c === oldKey ? newKey : c);
      setMessageCategories(updated);
      if (activeCategory === oldKey) setActiveCategory(newKey);
      
      // Generate keywords for the new category and save
      const keywords = generateKeywordsFromCategory(newName);
      const updatedKeywords = { ...customCategoryKeywords, [newKey]: keywords };
      delete updatedKeywords[oldKey];
      setCustomCategoryKeywords(updatedKeywords);
      localStorage.setItem('custom_category_keywords', JSON.stringify(updatedKeywords));
      
      toast.success(`Category renamed to "${newName}" - ${keywords.length} keywords auto-generated for scanning`);
    } else if (categoryModalType === 'calls') {
      const updated = callCategories.map(c => c === oldKey ? newKey : c);
      setCallCategories(updated);
      if (activeCallCategory === oldKey) setActiveCallCategory(newKey);
    } else if (categoryModalType === 'emails') {
      const updated = emailFolders.map(c => c === oldKey ? newKey : c);
      setEmailFolders(updated);
      localStorage.setItem('email_folders', JSON.stringify(updated));
      if (activeFolder === oldKey) setActiveFolder(newKey);
    } else if (categoryModalType === 'emailCategories') {
      const updated = emailCategories.map(c => c === oldKey ? newKey : c);
      setEmailCategories(updated);
      if (activeCategory === oldKey) setActiveCategory(newKey);
      
      // Update keywords - transfer from old key to new key
      const oldKeywords = emailCategoryKeywords[oldKey] || customCategoryKeywords[oldKey] || [];
      const newKeywords = oldKeywords.length > 0 ? oldKeywords : generateKeywordsFromCategory(newName);
      
      // Update email category keywords (database-persisted)
      const updatedEmailKeywords = { ...emailCategoryKeywords };
      delete updatedEmailKeywords[oldKey];
      updatedEmailKeywords[newKey] = newKeywords;
      setEmailCategoryKeywords(updatedEmailKeywords);
      saveEmailCategoryKeywords(updatedEmailKeywords);
      
      // Also update local keywords as backup
      const updatedCustomKeywords = { ...customCategoryKeywords };
      delete updatedCustomKeywords[oldKey];
      updatedCustomKeywords[newKey] = newKeywords;
      setCustomCategoryKeywords(updatedCustomKeywords);
      localStorage.setItem('custom_category_keywords', JSON.stringify(updatedCustomKeywords));
      
      // Update any email assignments from old category to new category
      const updatedAssignments = {};
      let reassignedCount = 0;
      for (const [emailId, category] of Object.entries(emailCategoryAssignments)) {
        if (category === oldKey) {
          updatedAssignments[emailId] = newKey;
          reassignedCount++;
        } else {
          updatedAssignments[emailId] = category;
        }
      }
      if (reassignedCount > 0) {
        setEmailAssignmentsByAccount(prev => ({
          ...prev,
          [activeEmailAccount]: updatedAssignments
        }));
        // Save updated assignments to database with account-specific key
        fetch(`${window.location.origin}/api/email-categories/assignments/bulk?user_id=${userId}_${activeEmailAccount}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ assignments: updatedAssignments })
        }).catch(err => console.error('Error updating email assignments after rename:', err));
      }
      
      toast.success(`Category renamed to "${newName}" with ${newKeywords.length} keywords`);
    }
    setEditingCategory(null);
    setEditCategoryValue('');
  };
  
  // Refactored deleteCategory to use hook methods
  const deleteCategory = (categoryKey) => {
    // Don't allow deleting default categories
    const protectedMessageCats = ['urgent', 'follow-up', 'client'];
    const protectedCallCats = ['family', 'friends', 'business'];
    const protectedEmailFolders = ['inbox', 'sent', 'drafts', 'trash'];
    const protectedEmailCategories = ['urgent', 'orders', 'meeting'];
    
    if (categoryModalType === 'messages') {
      if (protectedMessageCats.includes(categoryKey)) {
        toast.error('Cannot delete default category');
        return;
      }
      removeMessageCategory(categoryKey);
      if (activeCategory === categoryKey) setActiveCategory('all');
    } else if (categoryModalType === 'calls') {
      if (protectedCallCats.includes(categoryKey)) {
        toast.error('Cannot delete default category');
        return;
      }
      removeCallCategory(categoryKey);
      if (activeCallCategory === categoryKey) setActiveCallCategory('all');
    } else if (categoryModalType === 'emails') {
      if (protectedEmailFolders.includes(categoryKey)) {
        toast.error('Cannot delete default folder');
        return;
      }
      removeEmailFolder(categoryKey);
      if (activeFolder === categoryKey) setActiveFolder('inbox');
    } else if (categoryModalType === 'emailCategories') {
      if (protectedEmailCategories.includes(categoryKey)) {
        toast.error('Cannot delete default category');
        return;
      }
      removeEmailCategory(categoryKey);
      if (activeCategory === categoryKey) setActiveCategory('all');
    }
    toast.success('Category deleted');
  };
  
  // Scan messages for a specific category
  const scanForCategory = async (categoryKey) => {
    setScanningCategory(categoryKey);
    const keywords = customCategoryKeywords[categoryKey] || generateKeywordsFromCategory(categoryKey);
    
    let matchedMessages = 0;
    let matchedConversations = 0;
    const newAssignments = { ...messageCategoryAssignments };
    
    // Create a copy of conversations to update
    const updatedConversations = conversations.map(conv => {
      let conversationHasMatch = false;
      
      // Update messages within this conversation
      const updatedMessages = (conv.messages || []).map(msg => {
        const text = (msg.text || msg.content || msg.preview || '').toLowerCase();
        const msgId = msg.id || msg.message_id || `${conv.id}-${msg.timestamp}`;
        
        // Check if any keyword matches
        const hasMatch = keywords.some(keyword => 
          text.includes(keyword.toLowerCase())
        );
        
        if (hasMatch) {
          matchedMessages++;
          conversationHasMatch = true;
          // Save the assignment persistently
          newAssignments[msgId] = categoryKey;
          // Add the category to the message
          return { ...msg, messageCategory: categoryKey };
        }
        return msg;
      });
      
      if (conversationHasMatch) {
        matchedConversations++;
        return { ...conv, messages: updatedMessages };
      }
      return conv;
    });
    
    // Save assignments to localStorage for persistence
    setMessageCategoryAssignments(newAssignments);
    localStorage.setItem('message_category_assignments', JSON.stringify(newAssignments));
    
    // Update conversations state
    setConversations(updatedConversations);
    
    setScanningCategory(null);
    toast.success(
      `Scan complete! Found ${matchedMessages} messages in ${matchedConversations} conversations matching "${formatCategoryName(categoryKey)}"`,
      { duration: 4000 }
    );
  };
  
  // Save custom keywords for a category
  const saveKeywordsForCategory = (categoryKey, keywordsString) => {
    const keywords = keywordsString
      .split(',')
      .map(k => k.trim().toLowerCase())
      .filter(k => k.length > 0);
    
    // Check if this is for email categories or messages
    if (categoryModalType === 'emailCategories') {
      const updatedKeywords = { ...emailCategoryKeywords, [categoryKey]: keywords };
      setEmailCategoryKeywords(updatedKeywords);
      saveEmailCategoryKeywords(updatedKeywords);
      // Also save to localStorage as backup
      localStorage.setItem('email_category_keywords', JSON.stringify(updatedKeywords));
    } else {
      const updatedKeywords = { ...customCategoryKeywords, [categoryKey]: keywords };
      setCustomCategoryKeywords(updatedKeywords);
      localStorage.setItem('custom_category_keywords', JSON.stringify(updatedKeywords));
    }
    
    setEditingKeywordsCategory(null);
    setKeywordsInputValue('');
    toast.success(`Saved ${keywords.length} keywords for "${formatCategoryName(categoryKey)}"`);
  };
  
  const formatCategoryName = (key) => {
    return key.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };
  
  // Calls Connection State
  const [isCallsConnected, setIsCallsConnected] = useState(() => {
    try {
      return localStorage.getItem('calls_connected') === 'true';
    } catch { return false; }
  });
  const [callsConnectionType, setCallsConnectionType] = useState(() => {
    try {
      return localStorage.getItem('calls_connection_type') || null;
    } catch { return null; }
  });
  const [showCallsConnectionModal, setShowCallsConnectionModal] = useState(false);
  const [twilioCallsAccountSid, setTwilioCallsAccountSid] = useState('');
  const [twilioCallsAuthToken, setTwilioCallsAuthToken] = useState('');
  const [twilioCallsPhoneNumber, setTwilioCallsPhoneNumber] = useState('');
  const [callsConnectionError, setCallsConnectionError] = useState('');
  const [connectingCalls, setConnectingCalls] = useState(false);
  
  // Handle Twilio connection for calls
  const handleTwilioCallsConnect = async () => {
    if (!twilioCallsAccountSid || !twilioCallsAuthToken || !twilioCallsPhoneNumber) {
      setCallsConnectionError('Please fill in all Twilio credentials');
      return;
    }
    
    setConnectingCalls(true);
    setCallsConnectionError('');
    
    try {
      // Test Twilio connection
      const response = await fetch(`${apiUrl}/api/twilio-sms/test`);
      const data = await response.json();
      
      // Store connection in localStorage
      localStorage.setItem('calls_connected', 'true');
      localStorage.setItem('calls_connection_type', 'twilio');
      localStorage.setItem('twilio_calls_phone_number', twilioCallsPhoneNumber);
      
      setIsCallsConnected(true);
      setCallsConnectionType('twilio');
      setShowCallsConnectionModal(false);
    } catch (error) {
      setCallsConnectionError('Failed to connect to Twilio. Please check your credentials.');
    } finally {
      setConnectingCalls(false);
    }
  };
  
  // Handle iPhone Shortcuts connection for calls
  const handleCallsShortcutsConnect = () => {
    // For Shortcuts, we just mark as connected since call logs sync via webhook
    localStorage.setItem('calls_connected', 'true');
    localStorage.setItem('calls_connection_type', 'shortcuts');
    
    setIsCallsConnected(true);
    setCallsConnectionType('shortcuts');
    setShowCallsConnectionModal(false);
  };
  
  // Email Accounts Management State
  const [emailAccounts, setEmailAccounts] = useState([]);
  const [emailAccountsLoaded, setEmailAccountsLoaded] = useState(false);
  
  // Load email accounts from user-specific localStorage when userId is available
  useEffect(() => {
    if (!userId) return;
    
    try {
      const userSpecificKey = `email_accounts_${userId}`;
      const stored = localStorage.getItem(userSpecificKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setEmailAccounts(parsed);
          // Set the first account as active (leftmost tab)
          const savedActiveAccount = localStorage.getItem('activeEmailAccount');
          const firstAccountId = parsed[0].id;
          // Only auto-select first account if saved one doesn't exist in the list
          if (!savedActiveAccount || !parsed.some(acc => acc.id === savedActiveAccount)) {
            setActiveEmailAccount(firstAccountId);
            localStorage.setItem('activeEmailAccount', firstAccountId);
          }
          console.log(`📧 Loaded ${parsed.length} email accounts from localStorage for user ${userId}`);
        }
      }
      // If no user-specific data, ensure we start with empty array (clean slate for new users)
    } catch (e) {
      console.error('Error loading email accounts from localStorage:', e);
    }
  }, [userId]);

  // When email accounts change and there's accounts, ensure active account is the first if current is invalid
  useEffect(() => {
    if (emailAccounts.length > 0) {
      const activeAccountExists = emailAccounts.some(acc => acc.id === activeEmailAccount);
      if (!activeAccountExists) {
        // Select the first account (leftmost)
        const firstAccountId = emailAccounts[0].id;
        setActiveEmailAccount(firstAccountId);
        localStorage.setItem('activeEmailAccount', firstAccountId);
      }
    }
  }, [emailAccounts, activeEmailAccount]);

  const [showAddEmailModal, setShowAddEmailModal] = useState(false);
  const [newEmailName, setNewEmailName] = useState('');
  const [newEmailAddress, setNewEmailAddress] = useState('');
  const [newEmailType, setNewEmailType] = useState('outlook');
  const [newEmailPassword, setNewEmailPassword] = useState('');
  const [addingEmail, setAddingEmail] = useState(false);
  const [addEmailError, setAddEmailError] = useState('');
  
  // Load email account settings from database on mount
  useEffect(() => {
    const loadEmailAccountSettings = async () => {
      if (!userId || emailAccountsLoaded) return;
      
      // Get the hidden/deleted accounts list from localStorage (kept in sync by useNylasAccounts hook)
      let hiddenIds = [];
      try {
        hiddenIds = JSON.parse(localStorage.getItem('deleted_email_tabs') || '[]');
      } catch { hiddenIds = []; }

      try {
        const response = await fetch(`${window.location.origin}/api/email-account-settings/accounts?user_id=${userId}`);
        if (response.ok) {
          const data = await response.json();
          if (data.accounts && data.accounts.length > 0) {
            // Merge database settings with existing accounts, skipping hidden/deleted ones
            setEmailAccounts(prev => {
              const merged = [...prev];
              data.accounts.forEach(dbAccount => {
                // Skip accounts that were explicitly deleted by the user
                if (
                  hiddenIds.includes(dbAccount.email) ||
                  hiddenIds.includes(dbAccount.account_id)
                ) {
                  return;
                }
                const existingIndex = merged.findIndex(acc => acc.id === dbAccount.account_id);
                if (existingIndex >= 0) {
                  // Update existing account name from database
                  merged[existingIndex] = { ...merged[existingIndex], name: dbAccount.name };
                } else {
                  // Add account from database if not in local state
                  merged.push({
                    id: dbAccount.account_id,
                    name: dbAccount.name,
                    email: dbAccount.email,
                    type: dbAccount.type,
                    connected: dbAccount.connected
                  });
                }
              });
              return merged;
            });
            console.log('📧 Loaded email account settings from database');
          }
        }
      } catch (error) {
        console.error('Error loading email account settings:', error);
      }
      setEmailAccountsLoaded(true);
    };
    
    loadEmailAccountSettings();
  }, [userId, emailAccountsLoaded]);
  
  // Save email accounts to localStorage (for quick access) - use user-specific key
  useEffect(() => {
    if (userId) {
      localStorage.setItem(`email_accounts_${userId}`, JSON.stringify(emailAccounts));
    }
  }, [emailAccounts, userId]);
  
  // ============== SMS WebSocket for Real-time Notifications ==============
  const smsWebSocketRef = useRef(null);
  const smsReconnectTimeoutRef = useRef(null);
  const selectedConversationRef = useRef(selectedConversation);
  
  // Keep ref in sync with state
  useEffect(() => {
    selectedConversationRef.current = selectedConversation;
  }, [selectedConversation]);
  
  // Fetch Slack channels for AI drafts
  useEffect(() => {
    const fetchSlackChannels = async () => {
      try {
        const res = await fetch(`${API}/api/slack/channels`);
        if (res.ok) {
          const data = await res.json();
          setSlackChannelsForDrafts(data.channels || []);
        }
      } catch (e) {
        console.log('Slack channels not available');
      }
    };
    fetchSlackChannels();
  }, []);
  
  // Function to fetch conversations (will be used to refresh after new SMS)
  const fetchConversations = useCallback(async () => {
    // Counterfind tenant: inject 7 mockup conversations so the Messages tab
    // looks alive for demos and so VAs can practice triage without piping in
    // real SMS. Skip the live API for counterfind so backend zeros don't
    // overwrite the mockups.
    if (isCounterfind) {
      const cfConversations = _buildCounterfindMockConversations();
      setConversations(cfConversations);
      if (cfConversations.length > 0 && !selectedConversation) {
        setSelectedConversation(cfConversations[0]);
      }
      setLoading(false);
      return;
    }

    // Use demo data if in demo mode
    if (isDemoMode && demoData?.messages) {
      const demoConversations = demoData.messages.map((msg, idx) => ({
        id: msg.id || `demo-conv-${idx}`,
        phone: msg.phone || `+1555000${idx.toString().padStart(4, '0')}`,
        name: msg.from || msg.contact || 'Demo Contact',
        contact_name: msg.from || msg.contact || 'Demo Contact',
        messages: [{
          id: `msg-${idx}`,
          content: msg.preview || msg.content || 'Demo message preview',
          text: msg.preview || msg.content || 'Demo message preview',
          timestamp: msg.time || new Date().toISOString(),
          direction: 'inbound',
          sender: msg.from || msg.contact
        }],
        last_message: msg.preview || msg.content || 'Demo message preview',
        timestamp: msg.time || new Date().toISOString(),
        category: 'general',
        unread_count: msg.unread ? 1 : 0
      }));
      setConversations(demoConversations);
      if (demoConversations.length > 0) {
        setSelectedConversation(demoConversations[0]);
      }
      setLoading(false);
      console.log(`💬 Demo mode: Loaded ${demoConversations.length} demo conversations`);
      return;
    }
    
    try {
      const data = await apiCall('/conversations');
      if (data) {
        // Sort by timestamp/updated_at with most recent first
        const sortedConversations = data.sort((a, b) => {
          // Try multiple timestamp fields for reliability
          const getTime = (conv) => {
            const ts = conv.updated_at || conv.timestamp || conv.last_message_time || 
                      conv.lastMessageTime || conv.messages?.[conv.messages?.length - 1]?.timestamp || 0;
            return new Date(ts).getTime() || 0;
          };
          return getTime(b) - getTime(a);
        });

        // Preserve existing messages arrays — /conversations doesn't return messages
        // but /conversations-fast loaded them. Merging keeps categorizedMessages working.
        setConversations(prev => {
          const prevById = {};
          prev.forEach(c => { prevById[c.id] = c; if (c.phone) prevById[c.phone] = c; });
          return sortedConversations.map(newConv => {
            const existing = prevById[newConv.id] || prevById[newConv.phone];
            return { ...newConv, messages: newConv.messages?.length ? newConv.messages : (existing?.messages || []) };
          });
        });
        
        // Auto-open the most recent conversation if nothing is selected yet
        if (!selectedConversationRef.current && sortedConversations.length > 0) {
          setSelectedConversation(sortedConversations[0]);
          setMobileShowChat(true);
        }
        
        // Update selected conversation if it exists in the new data
        if (selectedConversationRef.current) {
          const updatedSelected = sortedConversations.find(
            c => c.phone === selectedConversationRef.current.phone || c.id === selectedConversationRef.current.id
          );
          if (updatedSelected && updatedSelected.messages?.length !== selectedConversationRef.current.messages?.length) {
            setSelectedConversation(updatedSelected);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching conversations:', error);
    }
  }, [isDemoMode, demoData]);

  // Fetch customers/contacts to build contact images lookup
  const fetchContactImages = useCallback(async () => {
    if (!userId) return;
    try {
      const imageMap = {};
      
      // Fetch from customers API
      const customersResponse = await fetch(`${window.location.origin}/api/customers?user_id=${encodeURIComponent(userId)}`);
      if (customersResponse.ok) {
        const data = await customersResponse.json();
        const customers = data.customers || [];
        
        customers.forEach(customer => {
          // Check all possible image fields
          const avatar = customer.avatar || customer.image_url || customer.profile_image || customer.image;
          if (avatar) {
            // Map by phone number (normalized)
            if (customer.phone) {
              const normalizedPhone = customer.phone.replace(/\D/g, '');
              imageMap[normalizedPhone] = avatar;
              imageMap[customer.phone] = avatar;
            }
            // Map by email
            if (customer.email) {
              imageMap[customer.email.toLowerCase()] = avatar;
            }
            // Map by name (case insensitive)
            if (customer.name) {
              imageMap[customer.name.toLowerCase()] = avatar;
            }
            if (customer.firstName && customer.lastName) {
              imageMap[`${customer.firstName} ${customer.lastName}`.toLowerCase()] = avatar;
            }
          }
        });
      }
      
      // Also fetch from iCloud contacts API
      try {
        const icloudResponse = await fetch(`${window.location.origin}/api/icloud-contacts/stored?user_id=${encodeURIComponent(userId)}`);
        if (icloudResponse.ok) {
          const icloudData = await icloudResponse.json();
          const contacts = icloudData.contacts || icloudData || [];
          
          (Array.isArray(contacts) ? contacts : []).forEach(contact => {
            const avatar = contact.profile_image || contact.avatar || contact.image_url || contact.image;
            if (avatar) {
              // Map by phone numbers — iCloud phones stored as {number, type} objects
              const phones = contact.phones || [];
              phones.forEach(phoneObj => {
                // Handle both {number: "..."} and {value: "..."} and plain strings
                const phone = typeof phoneObj === 'string'
                  ? phoneObj
                  : (phoneObj.number || phoneObj.value || phoneObj.phone || '');
                if (phone) {
                  const normalizedPhone = String(phone).replace(/\D/g, '');
                  if (normalizedPhone) imageMap[normalizedPhone] = avatar;
                  imageMap[phone] = avatar;
                }
              });
              // Direct phone field fallback
              if (contact.phone) {
                const normalizedPhone = String(contact.phone).replace(/\D/g, '');
                if (normalizedPhone) imageMap[normalizedPhone] = avatar;
                imageMap[contact.phone] = avatar;
              }
              // Map by name
              const name = contact.displayName || contact.name || 
                           (contact.first_name && contact.last_name ? `${contact.first_name} ${contact.last_name}` : null);
              if (name) {
                imageMap[name.toLowerCase()] = avatar;
              }
              // Map by email
              const emails = contact.emails || [];
              emails.forEach(emailObj => {
                const email = typeof emailObj === 'string'
                  ? emailObj
                  : (emailObj.value || emailObj.email || '');
                if (email) imageMap[email.toLowerCase()] = avatar;
              });
            }
          });
        }
      } catch (icloudErr) {
        console.log('Could not fetch iCloud contacts for images:', icloudErr);
      }
      
      setContactImages(imageMap);
      console.log(`📸 Loaded ${Object.keys(imageMap).length} contact images`);
    } catch (error) {
      console.error('Error fetching contact images:', error);
    }
  }, [userId]);

  // Fetch contact images on mount
  useEffect(() => {
    fetchContactImages();
  }, [fetchContactImages]);

  // Helper to get contact avatar from various identifiers
  const getContactAvatar = useCallback((conversation) => {
    if (!conversation) return null;
    
    // Check if conversation already has an avatar (check all possible field names)
    if (conversation.user_avatar || conversation.avatar || conversation.image || conversation.profile_image) {
      return conversation.user_avatar || conversation.avatar || conversation.image || conversation.profile_image;
    }
    
    // Try to find avatar from contactImages map
    const phone = conversation.phone?.replace(/\D/g, '') || '';
    const name = conversation.name?.toLowerCase() || '';
    const email = conversation.email?.toLowerCase() || '';
    
    return contactImages[phone] || contactImages[conversation.phone] || 
           contactImages[name] || contactImages[email] || null;
  }, [contactImages]);
  
  // Helper function to handle conversation click - extracted from inline JSX
  const handleConversationClick = useCallback(async (conv) => {
    isManualSelection.current = true;
    const convWithLoading = { ...conv, messagesLoading: true };
    setSelectedConversation(convWithLoading);
    setMobileShowChat(true);
    setConversationSearchQuery('');
    setShowConversationSearch(false);
    
    // Fetch messages if not loaded
    if (!conv.messages || conv.messages.length === 0) {
      try {
        const chatId = conv.chat_id || conv.phone;
        const messages = await apiCall(`/conversations-fast/${conv.id}/messages?chat_id=${encodeURIComponent(chatId)}&limit=100`);
        if (messages && Array.isArray(messages)) {
          const updatedConv = { ...conv, messages, messagesLoading: false };
          setSelectedConversation(updatedConv);
          setConversations(prevConvs => prevConvs.map(c => c.id === conv.id ? updatedConv : c));
          setTimeout(() => {
            messagesContainerRef.current?.scrollTo({ top: messagesContainerRef.current.scrollHeight });
            messagesEndRef.current?.scrollIntoView({ behavior: 'auto', block: 'end' });
          }, 100);
        } else {
          setSelectedConversation(prev => ({ ...prev, messagesLoading: false }));
        }
      } catch (err) {
        console.log('Could not fetch messages:', err);
        setSelectedConversation(prev => ({ ...prev, messagesLoading: false }));
      }
    } else {
      setSelectedConversation({ ...conv, messagesLoading: false });
      setTimeout(() => {
        messagesContainerRef.current?.scrollTo({ top: messagesContainerRef.current.scrollHeight });
        messagesEndRef.current?.scrollIntoView({ behavior: 'auto', block: 'end' });
      }, 100);
    }
    
    // Mark as read
    if (conv.unread > 0) {
      setConversations(prevConvs => prevConvs.map(c => c.phone === conv.phone ? { ...c, unread: 0 } : c));
      apiCall(`/messages/mark-read/${encodeURIComponent(conv.phone)}`, { method: 'POST' }).catch(() => {});
    }
  }, []);
  
  // Connect to SMS WebSocket for real-time notifications - ONLY ONCE on mount
  useEffect(() => {
    let isConnecting = false;
    
    const connectSMSWebSocket = () => {
      // Prevent multiple simultaneous connection attempts
      if (isConnecting || (smsWebSocketRef.current?.readyState === WebSocket.OPEN)) {
        return;
      }
      
      isConnecting = true;
      
      // Build WebSocket URL from current location
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsUrl = `${protocol}//${window.location.host}/api/webhooks/sms/ws`;
      
      console.log('📱 Connecting to SMS WebSocket:', wsUrl);
      
      try {
        // Close any existing connection first
        if (smsWebSocketRef.current) {
          smsWebSocketRef.current.close();
        }
        
        smsWebSocketRef.current = new WebSocket(wsUrl);
        
        smsWebSocketRef.current.onopen = () => {
          console.log('📱 SMS WebSocket connected!');
          isConnecting = false;
        };
        
        smsWebSocketRef.current.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            
            // Ignore pong messages
            if (data.type === 'pong') return;
            
            console.log('📱 SMS WebSocket message:', data);
            
            if (data.type === 'new_sms') {
              // Show single toast notification for new SMS
              toast.success(
                <div className="flex flex-col">
                  <span className="font-semibold">{data.sender}</span>
                  <span className="text-sm text-gray-600">{data.preview}</span>
                </div>,
                {
                  duration: 5000,
                  icon: '💬',
                  position: 'top-right',
                  id: `sms-${data.conversation_id}`, // Unique ID prevents duplicates
                }
              );
              
              // Update conversation with new message and move to top INSTANTLY
              setConversations(prev => {
                const now = new Date().toISOString();
                const updated = prev.map(conv => {
                  if (conv.phone === data.phone || conv.id === data.conversation_id) {
                    return {
                      ...conv,
                      lastMessage: data.preview,
                      unread: selectedConversationRef.current?.phone === data.phone ? 0 : (conv.unread || 0) + 1,
                      timestamp: now,
                      updated_at: now,
                      last_message_time: now
                    };
                  }
                  return conv;
                });
                
                // Re-sort to move the updated conversation to top
                return updated.sort((a, b) => {
                  const dateA = new Date(a.updated_at || a.timestamp || a.last_message_time || 0);
                  const dateB = new Date(b.updated_at || b.timestamp || b.last_message_time || 0);
                  return dateB - dateA;
                });
              });
              
              // Also refresh from server to get complete data
              fetchConversations();
            }
          } catch (e) {
            console.error('📱 Error parsing SMS WebSocket message:', e);
          }
        };
        
        smsWebSocketRef.current.onclose = (event) => {
          console.log('📱 SMS WebSocket disconnected:', event.code, event.reason);
          isConnecting = false;
          
          // Only reconnect if not intentionally closed
          if (event.code !== 1000) {
            smsReconnectTimeoutRef.current = setTimeout(() => {
              console.log('📱 Attempting to reconnect SMS WebSocket...');
              connectSMSWebSocket();
            }, 5000);
          }
        };
        
        smsWebSocketRef.current.onerror = (error) => {
          console.error('📱 SMS WebSocket error:', error);
          isConnecting = false;
        };
        
      } catch (error) {
        console.error('📱 Failed to create SMS WebSocket:', error);
        isConnecting = false;
      }
    };
    
    // Connect to WebSocket
    connectSMSWebSocket();
    
    // Send ping every 30 seconds to keep connection alive
    const pingInterval = setInterval(() => {
      if (smsWebSocketRef.current?.readyState === WebSocket.OPEN) {
        smsWebSocketRef.current.send('ping');
      }
    }, 30000);
    
    // Cleanup on unmount
    return () => {
      clearInterval(pingInterval);
      if (smsReconnectTimeoutRef.current) {
        clearTimeout(smsReconnectTimeoutRef.current);
      }
      if (smsWebSocketRef.current) {
        smsWebSocketRef.current.close(1000, 'Component unmounting');
      }
    };
  }, []); // Empty dependency array - only connect once on mount
  
  // Handle adding new email account
  const handleAddEmailAccount = () => {
    if (!newEmailName.trim()) {
      setAddEmailError('Please enter a name for this email account');
      return;
    }
    if (!newEmailAddress.trim()) {
      setAddEmailError('Please enter an email address');
      return;
    }
    // For iCloud, require app-specific password
    if (newEmailType === 'icloud' && !newEmailPassword.trim()) {
      setAddEmailError('Please enter your app-specific password for iCloud');
      return;
    }
    
    const newAccount = {
      id: `${newEmailType}_${Date.now()}`,
      name: newEmailName.trim(),
      type: newEmailType,
      email: newEmailAddress.trim(),
      password: newEmailPassword.trim() || null, // Store password for iCloud
      connected: newEmailType === 'icloud' && newEmailPassword.trim() ? true : false
    };
    
    // Store credentials securely in localStorage for the account
    if (newEmailPassword.trim()) {
      localStorage.setItem(`email_creds_${newAccount.id}`, JSON.stringify({
        email: newEmailAddress.trim(),
        password: newEmailPassword.trim()
      }));
    }
    
    setEmailAccounts(prev => [...prev, newAccount]);
    setShowAddEmailModal(false);
    setNewEmailName('');
    setNewEmailAddress('');
    setNewEmailType('outlook');
    setNewEmailPassword('');
    setAddEmailError('');
  };
  
  // State for delete confirmation modal
  const [showDeleteEmailConfirm, setShowDeleteEmailConfirm] = useState(false);
  const [emailAccountToDelete, setEmailAccountToDelete] = useState(null);
  const [deletingEmailAccount, setDeletingEmailAccount] = useState(false);
  
  // Handle removing email account - show confirmation first
  const handleRemoveEmailAccount = (accountId) => {
    const account = emailAccounts.find(acc => acc.id === accountId);
    if (account) {
      setEmailAccountToDelete(account);
      setShowDeleteEmailConfirm(true);
    }
  };
  
  // Confirm and execute email account removal
  const confirmRemoveEmailAccount = async () => {
    if (!emailAccountToDelete) return;
    
    const accountId = emailAccountToDelete.id;
    const account = emailAccountToDelete;
    
    try {
      setDeletingEmailAccount(true);
      
      // If this is a Nylas account, disconnect from backend (revoke grant)
      if (account.type === 'nylas' && account.email) {
        const token = localStorage.getItem('auth_token') || '';
        const response = await fetch(
          `${window.location.origin}/api/nylas/disconnect?email=${encodeURIComponent(account.email)}`,
          {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${token}` }
          }
        );
        // Treat 404 as success (already disconnected) — only fail on server errors
        if (!response.ok && response.status !== 404) {
          const err = await response.json().catch(() => ({}));
          throw new Error(err.detail || 'Failed to disconnect account from server');
        }
        console.log(`📧 Disconnected Nylas account: ${account.email}`);

        // Persist the hidden state to DB so this account never reappears after redeploy.
        // Uses the grant_id if available, otherwise the email as identifier.
        const hiddenIdentifier = account.grant_id || account.email;
        await hideEmailAccountTab(hiddenIdentifier);
      }

      // Delete from email_account_settings DB so it doesn't get re-added on page load
      if (userId) {
        await fetch(
          `${window.location.origin}/api/email-account-settings/accounts/${encodeURIComponent(accountId)}?user_id=${encodeURIComponent(userId)}`,
          { method: 'DELETE' }
        ).catch(() => {}); // Best-effort — don't block UI on failure
      }
      
      // Remove from local state only after successful backend call
      setEmailAccounts(prev => prev.filter(acc => acc.id !== accountId));
      
      // Clear localStorage cache for this account
      try {
        const cached = localStorage.getItem('nylas_emails_cache');
        if (cached) {
          const parsed = JSON.parse(cached);
          // Remove any cache entries for this account
          if (parsed.cache) {
            const newCache = {};
            for (const key in parsed.cache) {
              if (!key.includes(accountId) && !key.includes(account.email)) {
                newCache[key] = parsed.cache[key];
              }
            }
            parsed.cache = newCache;
            localStorage.setItem('nylas_emails_cache', JSON.stringify(parsed));
          }
        }
        // Also clear the email categories and assignments for this account
        localStorage.removeItem(`email_categories_${accountId}`);
        localStorage.removeItem(`email_keywords_${accountId}`);
        localStorage.removeItem(`email_assignments_${accountId}`);
      } catch (e) {
        console.warn('Failed to clear localStorage cache:', e);
      }
      
      // If the removed account was active, switch to first available
      if (activeEmailAccount === accountId) {
        const remaining = emailAccounts.filter(acc => acc.id !== accountId);
        setActiveEmailAccount(remaining[0]?.id || 'outlook');
      }
      
      toast.success(`Removed ${account.name || account.email}`);
    } catch (error) {
      console.error('Error removing email account:', error);
      toast.error('Failed to remove account');
    } finally {
      setDeletingEmailAccount(false);
      setShowDeleteEmailConfirm(false);
      setEmailAccountToDelete(null);
    }
  };

  // State for disconnecting all emails
  const [disconnectingAllEmails, setDisconnectingAllEmails] = useState(false);

  // Disconnect ALL email accounts for this user
  const handleDisconnectAllEmails = async () => {
    if (!window.confirm('Are you sure you want to disconnect ALL email accounts? This action cannot be undone.')) {
      return;
    }
    
    try {
      setDisconnectingAllEmails(true);
      
      // Call the disconnect-all endpoint
      const response = await fetch(`${window.location.origin}/api/nylas/disconnect-all`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token') || ''}`
        }
      });
      
      const data = await response.json();
      
      if (response.ok) {
        // Remove all Nylas accounts from local state
        setEmailAccounts(prev => prev.filter(acc => acc.type !== 'nylas'));
        
        // Clear Nylas emails
        setNylasEmails([]);
        
        // Reset active account if it was a Nylas account
        const remaining = emailAccounts.filter(acc => acc.type !== 'nylas');
        if (remaining.length > 0) {
          setActiveEmailAccount(remaining[0].id);
        } else {
          setActiveEmailAccount('outlook');
        }
        
        toast.success(`Disconnected ${data.disconnected_count || 0} email account(s)`);
        console.log('📧 Disconnected all email accounts:', data.emails);
      } else {
        throw new Error(data.detail || 'Failed to disconnect accounts');
      }
    } catch (error) {
      console.error('Error disconnecting all emails:', error);
      toast.error('Failed to disconnect all email accounts');
    } finally {
      setDisconnectingAllEmails(false);
    }
  };

  // Rename email account function - saves to database for persistence
  const handleRenameEmailAccount = async (accountId, newName) => {
    if (!newName.trim()) return;
    
    // Get the account details for saving to database
    const account = emailAccounts.find(acc => acc.id === accountId);
    
    // Update local state immediately
    setEmailAccounts(prev => prev.map(acc => 
      acc.id === accountId ? { ...acc, name: newName.trim() } : acc
    ));
    setRenamingAccountId(null);
    setRenameValue('');
    
    // Save to database for persistence across redeployments
    try {
      // Use POST to save complete account info (more reliable than PUT with query params)
      const response = await fetch(
        `${window.location.origin}/api/email-account-settings/accounts?user_id=${userId}`,
        { 
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            accounts: [{
              account_id: accountId,
              name: newName.trim(),
              email: account?.email || null,
              type: account?.type || null,
              connected: account?.connected ?? true
            }]
          })
        }
      );
      
      if (response.ok) {
        console.log('📧 Email account name saved to database:', newName.trim());
        toast.success('Account name saved');
      } else {
        console.error('Failed to save email account name to database');
        toast.error('Failed to save account name');
      }
    } catch (error) {
      console.error('Error saving email account name:', error);
      toast.error('Failed to save account name');
    }
  };

  // Start renaming an account
  const startRenamingAccount = (accountId, currentName) => {
    setRenamingAccountId(accountId);
    setRenameValue(currentName);
  };

  // Reorder email accounts and persist to localStorage
  const handleReorderEmailAccounts = (newOrder) => {
    setEmailAccounts(newOrder);
    // Save to localStorage for persistence
    try {
      const userSpecificKey = `email_accounts_${userId}`;
      localStorage.setItem(userSpecificKey, JSON.stringify(newOrder));
      // Also update the active account to be the first one if the current active isn't in the list
      if (newOrder.length > 0 && !newOrder.some(acc => acc.id === activeEmailAccount)) {
        setActiveEmailAccount(newOrder[0].id);
        localStorage.setItem('activeEmailAccount', newOrder[0].id);
      }
      console.log('📧 Email accounts reordered');
    } catch (e) {
      console.error('Error saving reordered accounts:', e);
    }
  };
  
  // Timer effect for scheduled messages countdown
  useEffect(() => {
    if (showScheduledTracker && scheduledMessages.some(m => m.status === 'pending')) {
      const interval = setInterval(() => {
        setScheduledCountdown(prev => prev + 1);
        
        // Check if any messages should be marked as sent
        setScheduledMessages(prev => prev.map(msg => {
          if (msg.status === 'pending' && new Date(msg.scheduledFor) <= new Date()) {
            return { ...msg, status: 'sent' };
          }
          return msg;
        }));
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [showScheduledTracker, scheduledMessages]);
  
  // Gmail cache version - increment this when categorization logic changes
  const GMAIL_CACHE_VERSION = '6';
  
  // Clear cache if version changed (to re-apply new categorization)
  useEffect(() => {
    const storedVersion = localStorage.getItem('gmailCacheVersion');
    if (storedVersion !== GMAIL_CACHE_VERSION) {
      console.log('📧 Cache version changed, clearing Gmail cache for re-categorization...');
      localStorage.removeItem('gmailEmailsCache');
      localStorage.setItem('gmailCacheVersion', GMAIL_CACHE_VERSION);
      setGmailEmailsCache({});
    }
  }, []);
  
  // Persist Gmail emails cache to localStorage whenever it changes
  useEffect(() => {
    if (Object.keys(gmailEmailsCache).length > 0) {
      try {
        localStorage.setItem('gmailEmailsCache', JSON.stringify(gmailEmailsCache));
        console.log('📧 Gmail emails cache persisted to localStorage');
      } catch (e) {
        console.warn('Failed to persist Gmail cache to localStorage:', e);
      }
    }
  }, [gmailEmailsCache]);

  // Persist activeEmailAccount to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('activeEmailAccount', activeEmailAccount);
    } catch (e) {
      // Ignore storage errors
    }
  }, [activeEmailAccount]);

  // Add Nylas connected accounts to emailAccounts when they load (refactored: uses useNylasAccounts hook)
  useEffect(() => {
    if (nylasConnectedAccounts.length > 0) {
      console.log('📧 Nylas accounts from hook:', nylasConnectedAccounts);
      
      // Add Nylas accounts to emailAccounts if not already present
      setEmailAccounts(prev => {
        const updatedAccounts = [...prev];
        
        nylasConnectedAccounts.forEach(nylasAccount => {
          // Check if this account already exists in emailAccounts
          const existingIndex = updatedAccounts.findIndex(acc => 
            acc.email === nylasAccount.email || 
            (acc.type === 'nylas' && acc.id === `nylas_${nylasAccount.grant_id}`)
          );
          
          if (existingIndex === -1) {
            // Add new Nylas account
            updatedAccounts.push({
              id: `nylas_${nylasAccount.grant_id || nylasAccount.email}`,
              name: nylasAccount.account_name || (nylasAccount.email || '').split('@')[0] || 'Unknown',
              type: 'nylas',
              email: nylasAccount.email,
              provider: nylasAccount.provider,
              connected: true,
              grant_id: nylasAccount.grant_id
            });
            console.log(`📧 Added Nylas account: ${nylasAccount.email}`);
          } else {
            // Update existing account to mark as connected
            updatedAccounts[existingIndex] = {
              ...updatedAccounts[existingIndex],
              connected: true,
              type: 'nylas',
              provider: nylasAccount.provider,
              grant_id: nylasAccount.grant_id
            };
            console.log(`📧 Updated existing account: ${nylasAccount.email}`);
          }
        });
        
        return updatedAccounts;
      });
      
      // Auto-select first Nylas account if no account is currently selected or if default 'outlook' is selected
      const savedAccount = localStorage.getItem('activeEmailAccount');
      if (!savedAccount || savedAccount === 'outlook') {
        const firstAccount = nylasConnectedAccounts[0];
        if (firstAccount) {
          const accountId = `nylas_${firstAccount.grant_id || firstAccount.email}`;
          console.log('📧 Auto-selecting first Nylas account:', accountId);
          setActiveEmailAccount(accountId);
          // Only trigger email sync if not already in session cache or component cache
          const cacheKey = `${accountId}_inbox`;
          if (_nylasSessionCache[cacheKey] && _nylasSessionCache[cacheKey].length > 0) {
            setNylasEmails(_nylasSessionCache[cacheKey]);
            console.log(`📧 Auto-select: loaded ${_nylasSessionCache[cacheKey].length} emails from session cache`);
          } else if (!nylasEmailsCache[cacheKey] || nylasEmailsCache[cacheKey].length === 0) {
            syncNylasEmails('inbox', firstAccount.email, accountId);
            setFetchedEmailKeys(prev => new Set([...prev, cacheKey]));
          }
        }
      }
    }
  }, [nylasConnectedAccounts]);

  // INSTANT EMAIL LOAD: Load cached Nylas emails immediately on mount (or demo emails)
  useEffect(() => {
    // Use demo emails if in demo mode
    if (isDemoMode && demoData?.emails) {
      const demoEmails = demoData.emails.map((email, idx) => ({
        id: email.id || `demo-email-${idx}`,
        message_id: email.id || `demo-email-${idx}`,
        from: email.from || 'Demo Sender',
        from_email: email.from_email || 'demo@example.com',
        to: email.to || 'you@example.com',
        subject: email.subject || 'Demo Email',
        snippet: email.preview || email.body?.substring(0, 100) || '',
        body: email.body || email.preview || '',
        date: email.time || new Date().toISOString(),
        timestamp: email.time || new Date().toISOString(),
        unread: email.unread !== false,
        has_attachments: email.has_attachment || false,
        folder: email.folder || 'inbox',
        category: email.folder === 'sent' ? 'sent' : 'general',
        labels: email.folder === 'sent' ? ['sent'] : ['inbox'],
        source: 'demo'
      }));
      setNylasEmails(demoEmails);
      setActiveEmailAccount('demo_account');
      console.log(`📧 Demo mode: Loaded ${demoEmails.length} demo emails`);
      return;
    }
    
    // Session cache (in-memory) is fastest — survives navigation within the same tab
    const sessionKeys = Object.keys(_nylasSessionCache);
    if (sessionKeys.length > 0) {
      // Find the active account's emails from session cache
      const savedAccount = localStorage.getItem('activeEmailAccount');
      const defaultKey = savedAccount ? `${savedAccount}_inbox` : null;
      const emails = defaultKey ? _nylasSessionCache[defaultKey] : _nylasSessionCache[sessionKeys[0]];
      if (emails && emails.length > 0) {
        setNylasEmails(emails);
        console.log(`📧 INSTANT: Loaded ${emails.length} emails from session cache on mount`);
        return; // Skip localStorage fallback
      }
    }

    // Fallback to localStorage for page reloads
    try {
      const cached = localStorage.getItem('nylas_emails_cache');
      if (cached) {
        const parsed = JSON.parse(cached);
        if (parsed.emails && parsed.emails.length > 0) {
          // Re-categorize emails that might have old/incorrect categories
          const recategorizedEmails = parsed.emails.map(email => {
            // If category is 'Other' or undefined, try to categorize properly
            if (!email.category || email.category === 'Other' || email.category === 'other') {
              return {
                ...email,
                category: categorizeEmail(email)
              };
            }
            return email;
          });
          
          setNylasEmails(recategorizedEmails);
          console.log(`📧 INSTANT: Loaded and re-categorized ${recategorizedEmails.length} cached emails on mount`);
          
          // Restore the active account from cache
          if (parsed.activeAccount) {
            const savedAccount = localStorage.getItem('activeEmailAccount');
            if (!savedAccount || savedAccount === 'outlook') {
              setActiveEmailAccount(parsed.activeAccount);
            }
          }
        }
      }
    } catch (e) {
      console.log('📧 No cached emails to load');
    }
  }, [isDemoMode, demoData]);

  // Load cached emails when switching to email tab
  useEffect(() => {
    if (activeTab === 'email') {
      // First load from localStorage cache for instant display
      try {
        const cached = localStorage.getItem('nylas_emails_cache');
        if (cached) {
          const parsed = JSON.parse(cached);
          if (parsed.emails && parsed.emails.length > 0 && nylasEmails.length === 0) {
            setNylasEmails(parsed.emails);
            console.log(`📧 Tab switch: Loaded ${parsed.emails.length} cached emails`);
          }
        }
      } catch (e) { /* ignored */ }
      
      // Then check if we have cached emails for current account/folder
      const cacheKey = `${activeEmailAccount}_${activeFolder}`;
      if (nylasEmailsCache[cacheKey] && nylasEmailsCache[cacheKey].length > 0) {
        setNylasEmails(nylasEmailsCache[cacheKey]);
        console.log(`📧 Tab switch: Using memory cache for ${cacheKey}`);
      }
    }
  }, [activeTab]);

  // Dashboard messaging functions
  const fetchDashboardUsers = async () => {
    if (!userId) return;
    try {
      const response = await fetch(`${window.location.origin}/api/dashboard-messages/users?user_id=${userId}`);
      const data = await response.json();
      if (data.success) {
        setDashboardUsers(data.users || []);
      }
    } catch (error) {
      console.error('Failed to fetch dashboard users:', error);
    }
  };

  const fetchChatNotifications = async () => {
    if (!userId) return;
    try {
      const response = await fetch(`${window.location.origin}/api/dashboard-messages/notifications?user_id=${userId}`);
      const data = await response.json();
      if (data.success) {
        setChatNotifications(data.notifications || []);
        setUnreadNotifications(data.unread_count || 0);
      }
    } catch (error) {
      console.error('Failed to fetch chat notifications:', error);
    }
  };

  const sendChatInvite = async (recipientId, recipientName) => {
    if (!userId) return;
    setSendingInvite(recipientId);
    try {
      const response = await fetch(`${window.location.origin}/api/dashboard-messages/invite?user_id=${userId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          recipient_id: recipientId,
          message: `Hey! I'd like to chat with you on the dashboard.`
        })
      });
      const data = await response.json();
      if (data.success) {
        toast.success(`Chat invite sent to ${recipientName}!`);
      }
    } catch (error) {
      console.error('Failed to send chat invite:', error);
      toast.error('Failed to send invite');
    } finally {
      setSendingInvite(null);
    }
  };

  const markNotificationRead = async (notificationId) => {
    try {
      await fetch(`${window.location.origin}/api/dashboard-messages/notifications/${notificationId}/read?user_id=${userId}`, {
        method: 'PUT'
      });
      setChatNotifications(prev => prev.map(n => 
        n.id === notificationId ? { ...n, read: true } : n
      ));
      setUnreadNotifications(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark notification read:', error);
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      await fetch(`${window.location.origin}/api/dashboard-messages/notifications/${notificationId}?user_id=${userId}`, {
        method: 'DELETE'
      });
      setChatNotifications(prev => prev.filter(n => n.id !== notificationId));
    } catch (error) {
      console.error('Failed to delete notification:', error);
    }
  };

  const fetchDashboardConversations = async () => {
    if (!userId) return;
    try {
      const response = await fetch(`${window.location.origin}/api/dashboard-messages/conversations?user_id=${userId}`);
      const data = await response.json();
      if (data.success) {
        const convs = data.conversations || [];
        setDashboardConversations(convs);
        if (!selectedConversationRef.current && !selectedDashboardUser && convs.length > 0) {
          const first = convs[0];
          startNewDashboardChat({ id: first.user_id, name: first.user_name, email: '' });
        }
      }
    } catch (error) {
      console.error('Failed to fetch dashboard conversations:', error);
    }
  };

  const fetchMyTeams = async () => {
    if (!userId) return;
    try {
      const res = await fetch(`${window.location.origin}/api/chat-teams/my-teams?user_id=${encodeURIComponent(userId)}`);
      const data = await res.json();
      if (data.success) setMyTeams(data.teams || []);
    } catch { /* ignored */ }
  };

  const fetchTeamMessages = async (teamId) => {
    if (!userId || !teamId) return;
    setDashboardLoading(true);
    try {
      const res = await fetch(`${window.location.origin}/api/chat-teams/${teamId}/messages?user_id=${encodeURIComponent(userId)}`);
      const data = await res.json();
      if (data.success) {
        setTeamMessages(data.messages || []);
        setSelectedTeam(data.team);
        setSelectedDashboardUser(null); // clear DM selection
      }
    } catch { /* ignored */ }
    finally { setDashboardLoading(false); }
  };

  const sendTeamMessage = async () => {
    if (!userId || !selectedTeam || !teamMessageInput.trim()) return;
    const text = teamMessageInput.trim();
    setTeamMessageInput('');
    try {
      await fetch(`${window.location.origin}/api/chat-teams/${selectedTeam.team_id}/messages?user_id=${encodeURIComponent(userId)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sender_id: userId, sender_name: authUser?.full_name || authUser?.name || 'Me', message: text })
      });
      fetchTeamMessages(selectedTeam.team_id);
      fetchMyTeams();
    } catch { /* ignored */ }
  };


  const fetchDashboardMessages = async (otherUserId) => {
    if (!userId || !otherUserId) return;
    setDashboardLoading(true);
    try {
      const response = await fetch(`${window.location.origin}/api/dashboard-messages/messages/${otherUserId}?user_id=${userId}`);
      const data = await response.json();
      if (data.success) {
        setDashboardMessages(data.messages || []);
      }
    } catch (error) {
      console.error('Failed to fetch dashboard messages:', error);
    } finally {
      setDashboardLoading(false);
    }
  };

  const sendDashboardMessage = async () => {
    if (!userId || !selectedDashboardUser) return;
    const hasText = dashboardMessageInput.trim();
    const hasAttachment = !!dashboardAttachment;
    if (!hasText && !hasAttachment) return;

    // File size check — base64 inflates by ~37%, Kubernetes proxy limit ~8MB
    if (hasAttachment && dashboardAttachment.size > 6 * 1024 * 1024) {
      toast.error('File too large. Please attach files under 6MB.');
      return;
    }

    try {
      let attachmentData = null;
      let attachmentName = null;
      let attachmentType = null;

      // Convert attachment file to base64 if present
      if (hasAttachment) {
        attachmentData = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (e) => resolve(e.target.result);
          reader.onerror = (err) => reject(new Error('Failed to read file: ' + err));
          reader.readAsDataURL(dashboardAttachment);
        });
        attachmentName = dashboardAttachment.name;
        attachmentType = dashboardAttachment.type || 'application/octet-stream';
      }

      const body = {
        recipient_id: selectedDashboardUser.id || selectedDashboardUser.user_id,
        content: hasText || '',
        ...(attachmentData && { attachment_data: attachmentData, attachment_name: attachmentName, attachment_type: attachmentType })
      };

      const response = await fetch(`${window.location.origin}/api/dashboard-messages/send?user_id=${userId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        const errText = await response.text();
        throw new Error(`Server error ${response.status}: ${errText}`);
      }

      const data = await response.json();
      if (data.success) {
        setDashboardMessages(prev => [...prev, data.message]);
        setDashboardMessageInput('');
        setDashboardAttachment(null);
        if (dashboardAttachmentInputRef.current) dashboardAttachmentInputRef.current.value = '';
        fetchDashboardConversations();
      } else {
        toast.error('Failed to send message. Please try again.');
      }
    } catch (error) {
      console.error('Failed to send dashboard message:', error);
      toast.error(`Send failed: ${error.message || 'Unknown error'}`);
    }
  };

  const selectDashboardConversation = (conv) => {
    setSelectedDashboardUser({
      id: conv.user_id,
      name: conv.user_name,
      email: conv.user_email,
      avatar: conv.user_avatar
    });
    fetchDashboardMessages(conv.user_id);
    setMobileShowChat(true);
  };

  const startNewDashboardChat = (user) => {
    setSelectedTeam(null);
    setSelectedDashboardUser(user);
    fetchDashboardMessages(user.id);
    setMobileShowChat(true);
  };

  // Load dashboard data when switching to dashboard tab or all tab
  useEffect(() => {
    if (activeTab === 'messages' && userId) {
      fetchMyTeams(); // Always load teams on messages tab
      if (activeMessagesCategory === 'dashboard') {
        fetchDashboardUsers();
        fetchDashboardConversations();
        fetchChatNotifications();
      } else if (activeMessagesCategory === 'all') {
        fetchDashboardConversations();
      }
    }
  }, [activeTab, activeMessagesCategory, userId]);

  // Check Gmail connection status on mount
  useEffect(() => {
    checkGmailConnectionStatus();
    checkAllGmailAccountsStatus(); // Check all Gmail accounts' status
    
    // Restore Gmail emails from cache if available
    try {
      const cached = localStorage.getItem('gmailEmailsCache');
      if (cached) {
        const parsedCache = JSON.parse(cached);
        if (parsedCache['inbox'] && parsedCache['inbox'].length > 0) {
          setGmailEmails(parsedCache['inbox']);
          console.log(`📧 Restored ${parsedCache['inbox'].length} Gmail inbox emails from localStorage`);
        }
      }
    } catch (e) {
      console.warn('Failed to restore Gmail cache from localStorage:', e);
    }
    
    // Check if we just returned from Gmail OAuth
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('gmail_connected') === 'true') {
      const connectedAccountId = urlParams.get('account') || 'creatrhq';
      
      // Map backend account_id to frontend emailAccounts id
      const accountIdMapping = {
        'creatrhq': 'gmail',
        'wdjwood': 'wdjwood',
        'darren': 'darren',
        'creatrverse': 'creatrverse',
        'kallie': 'kallie'
      };
      const frontendAccountId = accountIdMapping[connectedAccountId] || connectedAccountId;
      
      // Update the connected status for the correct account
      setEmailAccounts(prev => prev.map(acc => 
        acc.id === frontendAccountId ? { ...acc, connected: true } : acc
      ));
      
      setGmailConnected(true);
      setActiveTab('email');
      setActiveEmailAccount(frontendAccountId);
      
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
      // Refresh Gmail status
      checkGmailConnectionStatus();
    } else if (urlParams.get('gmail_error') === 'true') {
      const errorAccount = urlParams.get('account') || 'unknown';
      alert(`Failed to connect Gmail account (${errorAccount}). Please try again.`);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    
    // Check if we just returned from Nylas OAuth
    if (urlParams.get('nylas_connected') === 'true') {
      const email = urlParams.get('email') || '';
      const provider = urlParams.get('provider') || '';
      
      // Add the new Nylas account to emailAccounts
      const newAccountId = `nylas_${Date.now()}`;
      setEmailAccounts(prev => {
        // Check if account with this email already exists
        const existing = prev.find(acc => acc.email === email);
        if (existing) {
          return prev.map(acc => 
            acc.email === email ? { ...acc, connected: true, type: 'nylas', provider } : acc
          );
        }
        // Add new account
        return [...prev, {
          id: newAccountId,
          name: (email || '').split('@')[0] || 'Unknown',
          type: 'nylas',
          email: email,
          provider: provider,
          connected: true
        }];
      });
      
      setActiveTab('email');
      setActiveEmailAccount(newAccountId);
      
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
      
      // Show success message
      alert(`Successfully connected ${email} (${provider})`);
      
      // Trigger email fetch for the newly connected account after a short delay
      // to allow state to update
      setTimeout(() => {
        console.log(`📧 Auto-fetching emails for newly connected account: ${email}`);
        syncNylasEmails('inbox', email, newAccountId);
      }, 1000);
      
      // Also refresh the Nylas accounts list
      if (refetchNylasAccounts) {
        refetchNylasAccounts();
      }
    } else if (urlParams.get('nylas_error') === 'true') {
      const errorMessage = urlParams.get('message') || 'Unknown error';
      alert(`Failed to connect email: ${errorMessage}`);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);
  
  // Map email addresses to backend account IDs
  const getGmailAccountId = (email) => {
    const emailToAccountId = {
      'creatrhq@gmail.com': 'creatrhq',
      'darren@creatrverse.com': 'darren',
      'wdjwood@gmail.com': 'wdjwood',
      'creatrverse@gmail.com': 'creatrverse',
      'kalliestockton@creatrverse.com': 'kallie'
    };
    return emailToAccountId[email?.toLowerCase()] || 'creatrhq';
  };

  // Get current Gmail account ID based on active account
  const getCurrentGmailAccountId = () => {
    const activeAccount = emailAccounts.find(acc => acc.id === activeEmailAccount);
    if (activeAccount && activeAccount.type === 'gmail') {
      return getGmailAccountId(activeAccount.email);
    }
    // Default to creatrhq for the default gmail account
    const defaultGmail = emailAccounts.find(acc => acc.id === 'gmail');
    return getGmailAccountId(defaultGmail?.email);
  };
  
  // Get active Nylas account email
  const getActiveNylasAccountEmail = () => {
    const activeAccount = emailAccounts.find(acc => acc.id === activeEmailAccount);
    if (activeAccount && activeAccount.type === 'nylas') {
      return activeAccount.email;
    }
    // Try to find any Nylas account
    const nylasAccount = emailAccounts.find(acc => acc.type === 'nylas');
    return nylasAccount?.email || '';
  };

  // Check Gmail connection status for all Gmail accounts
  const checkGmailConnectionStatus = async () => {
    try {
      // Check status for the currently active Gmail account
      const accountId = getCurrentGmailAccountId();
      const response = await fetch(`${window.location.origin}/api/gmail/status?account_id=${accountId}`);
      const data = await response.json();
      setGmailConnected(data.connected === true);
      if (data.email) {
        setGmailEmail(data.email);
      }
      console.log('📧 Gmail connection status:', data);
      
      // Also update the emailAccounts state with the connection status
      if (data.connected) {
        setEmailAccounts(prev => prev.map(acc => {
          if (acc.type === 'gmail' && getGmailAccountId(acc.email) === accountId) {
            return { ...acc, connected: true };
          }
          return acc;
        }));
      }
    } catch (error) {
      console.error('Failed to check Gmail status:', error);
      setGmailConnected(false);
    }
  };
  
  // Check all Gmail accounts' connection status on mount
  const checkAllGmailAccountsStatus = async () => {
    try {
      // Get status for all accounts
      const response = await fetch(`${window.location.origin}/api/gmail/status`);
      const data = await response.json();
      
      if (data.accounts) {
        setEmailAccounts(prev => prev.map(acc => {
          if (acc.type === 'gmail') {
            const backendAccountId = getGmailAccountId(acc.email);
            const accountStatus = data.accounts[backendAccountId];
            if (accountStatus) {
              return { ...acc, connected: accountStatus.connected };
            }
          }
          return acc;
        }));
      }
    } catch (error) {
      console.error('Failed to check all Gmail accounts status:', error);
    }
  };
  
  // Connect Gmail account - now supports any configured account
  const connectGmail = async (accountEmail = null) => {
    setGmailLoading(true);
    try {
      // Determine which account to connect
      let accountId;
      if (accountEmail) {
        accountId = getGmailAccountId(accountEmail);
      } else {
        // Use the currently active account or default
        const activeAccount = emailAccounts.find(acc => acc.id === activeEmailAccount);
        if (activeAccount && activeAccount.type === 'gmail') {
          accountId = getGmailAccountId(activeAccount.email);
        } else {
          const defaultGmail = emailAccounts.find(acc => acc.id === 'gmail');
          accountId = getGmailAccountId(defaultGmail?.email);
        }
      }
      
      console.log('🔗 Connecting Gmail account:', accountId);
      const response = await fetch(`${window.location.origin}/api/gmail/auth/url?account_id=${accountId}`);
      const data = await response.json();
      if (data.auth_url) {
        // Redirect to Google OAuth
        window.location.href = data.auth_url;
      } else {
        throw new Error('No auth URL returned');
      }
    } catch (error) {
      console.error('Failed to get Gmail auth URL:', error);
      alert('Failed to connect Gmail. Please try again.');
      setGmailLoading(false);
    }
  };
  
  // Sync Gmail emails - accepts optional accountId to avoid stale closure issues
  const syncGmailEmails = async (folder = 'inbox', overrideAccountId = null, frontendAccountId = null) => {
    if (gmailLoading) return;
    
    setGmailLoading(true);
    
    try {
      // Map folder names to Gmail labels
      const labelMap = {
        'inbox': 'INBOX',
        'sent': 'SENT',
        'drafts': 'DRAFT',
        'spam': 'SPAM',
        'trash': 'TRASH',
        'starred': 'STARRED',
        'flagged': 'STARRED',  // Gmail uses STARRED for flagged
        'archive': 'all',      // Gmail "All Mail" for archived emails
        'important': 'IMPORTANT'
      };
      
      const label = labelMap[folder] || 'INBOX';
      // Use override if provided, otherwise get from current state
      const accountId = overrideAccountId || getCurrentGmailAccountId();
      // Frontend account ID for caching (e.g., 'wdjwood' not 'gmail')
      const feAccountId = frontendAccountId || activeEmailAccount;
      
      console.log(`📧 Fetching Gmail emails for ${feAccountId}/${folder}`);
      const response = await fetch(`${window.location.origin}/api/gmail/messages?account_id=${accountId}&label_id=${label}&max_results=250`);
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to fetch Gmail emails');
      }
      
      const data = await response.json();
      const messages = data.messages || [];
      
      // Helper function to parse "Name <email>" format
      const parseFromField = (fromStr) => {
        if (!fromStr) return { name: 'Unknown', email: '' };
        // Match "Name <email>" format
        const match = fromStr.match(/^(.+?)\s*<(.+?)>$/);
        if (match) {
          return { name: match[1].trim(), email: match[2].trim() };
        }
        // If it's just an email
        if (fromStr.includes('@')) {
          return { name: fromStr.split('@')[0], email: fromStr };
        }
        return { name: fromStr, email: '' };
      };
      
      // Transform Gmail messages to match the standard email structure
      const transformedEmails = messages.map((msg, index) => {
        const fromParsed = parseFromField(msg.from);
        return {
          id: msg.id || `gmail-${index}`,
          from: fromParsed.name,
          email: fromParsed.email,
          avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(fromParsed.name || 'U')}&background=random&size=100`,
          subject: msg.subject || '(No Subject)',
          preview: msg.snippet || msg.body_preview || msg.body?.substring(0, 150) || '',
          body: msg.body || msg.snippet || '',
          time: formatGmailTime(msg.date),
          date: msg.date,
          category: categorizeEmail(msg),
          folder: folder,
          read: !msg.is_unread,
          starred: msg.is_starred,
          hasAttachments: msg.has_attachments,
          source: 'gmail',
          thread: [{
            from: fromParsed.name,
            avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(fromParsed.name || 'U')}&background=random&size=100`,
            time: formatGmailTime(msg.date),
            body: msg.body || msg.snippet || ''
          }]
        };
      });
      
      console.log(`📧 Gmail: Fetched ${transformedEmails.length} emails for ${feAccountId}/${folder}`);
      
      // Update cache and state - use account-specific cache key
      const cacheKey = `${feAccountId}_${folder}`;
      setGmailEmailsCache(prev => ({
        ...prev,
        [cacheKey]: transformedEmails
      }));
      setGmailEmails(transformedEmails);
      setGmailConnected(true);
      
      // Also update account connected status
      setEmailAccounts(prev => prev.map(acc => {
        if (acc.id === feAccountId) {
          return { ...acc, connected: true };
        }
        return acc;
      }));
      
    } catch (error) {
      console.error('❌ Failed to sync Gmail emails:', error);
      // If token expired, mark as disconnected
      if (error.message.includes('invalid_grant') || error.message.includes('Token')) {
        setGmailConnected(false);
        // Mark the specific account as disconnected
        setEmailAccounts(prev => prev.map(acc => {
          if (acc.id === feAccountId) {
            return { ...acc, connected: false };
          }
          return acc;
        }));
      }
    } finally {
      setGmailLoading(false);
    }
  };
  
  // Sync Nylas emails for universal email provider support
  const syncNylasEmails = async (folder = 'inbox', accountEmail = null, frontendAccountId = null) => {
    const feAccountId = frontendAccountId || activeEmailAccount;
    const cacheKey = `${feAccountId}_${folder}`;
    
    // INSTANT: Load from cache first if available
    if (nylasEmailsCache[cacheKey] && nylasEmailsCache[cacheKey].length > 0) {
      console.log(`📧 Instant load from cache: ${nylasEmailsCache[cacheKey].length} emails`);
      setNylasEmails(nylasEmailsCache[cacheKey]);
    }
    
    // Don't fetch if already loading
    if (nylasLoading) return;
    
    setNylasLoading(true);
    
    try {
      const emailParam = accountEmail ? `&email=${encodeURIComponent(accountEmail)}` : '';
      const authToken = localStorage.getItem('auth_token');
      
      console.log(`📧 Fetching Nylas emails for ${feAccountId}/${folder}`);
      const response = await fetch(`${window.location.origin}/api/nylas/messages?folder=${folder}&limit=250${emailParam}`, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to fetch Nylas emails');
      }
      
      const data = await response.json();
      const messages = data.messages || [];
      
      // Transform to common email format matching Outlook/Gmail structure
      const transformedEmails = messages.map(msg => {
        const fromEmail = msg.from?.email || 'unknown@email.com';
        const fromName = msg.from?.name || fromEmail.split('@')[0].replace(/[._-]/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Unknown';
        
        // Format the date - keep both raw ISO and formatted for display
        const msgDate = msg.date ? new Date(msg.date * 1000) : new Date();
        const formattedTime = formatEmailTime(msgDate.toISOString());
        const rawISODate = msgDate.toISOString(); // Keep raw date for thread view
        
        return {
          id: msg.id,
          threadId: msg.thread_id,
          from: fromName,
          email: fromEmail,
          subject: msg.subject || '(No Subject)',
          preview: msg.snippet || '',
          snippet: msg.snippet || '',
          time: formattedTime,
          date: rawISODate,
          rawDate: rawISODate, // Raw ISO date for full formatting in thread view
          to: msg.to?.map(t => t.email) || [],
          toNames: msg.to?.map(t => t.name || t.email) || [],
          cc: msg.cc?.map(c => c.email) || [],
          ccNames: msg.cc?.map(c => c.name || c.email) || [],
          unread: msg.unread,
          isRead: !msg.unread,
          starred: msg.starred,
          hasAttachments: msg.has_attachments,
          attachments: msg.attachments || [],
          folder: folder,
          source: 'nylas',
          category: categorizeEmail({
            subject: msg.subject || '',
            from: msg.from?.name || '',
            from_email: msg.from?.email || '',
            body: msg.body || msg.snippet || '',
            snippet: msg.snippet || ''
          }),
          body: msg.body || msg.snippet || '',
          bodyType: 'html',
          // Thread structure for email detail view - use raw date for proper formatting
          thread: [{
            id: msg.id,
            from: fromName,
            email: fromEmail,
            time: rawISODate, // Use raw ISO date in thread so it can be formatted properly
            date: rawISODate,
            body: msg.body || msg.snippet || '',
            bodyType: 'html',
            to: msg.to?.map(t => t.email) || [],
            cc: msg.cc?.map(c => c.email) || [],
            attachments: msg.attachments || []
          }]
        };
      });
      
      console.log(`📧 Nylas: Fetched ${transformedEmails.length} emails for ${feAccountId}/${folder}`);
      
      // Update cache and state
      const cacheKey = `${feAccountId}_${folder}`;
      
      // Save to module-level session cache (no size limits, survives remount)
      _nylasSessionCache[cacheKey] = transformedEmails;
      
      const newCache = {
        ...nylasEmailsCache,
        [cacheKey]: transformedEmails
      };
      setNylasEmailsCache(newCache);
      setNylasEmails(transformedEmails);
      
      // Persist to localStorage — strip body/thread to avoid 5MB limit
      try {
        const emailsForStorage = transformedEmails.map(({ body, thread, ...rest }) => rest);
        const cacheForStorage = {};
        Object.keys(newCache).forEach(k => {
          cacheForStorage[k] = newCache[k].map(({ body, thread, ...rest }) => rest);
        });
        localStorage.setItem('nylas_emails_cache', JSON.stringify({
          cache: cacheForStorage,
          emails: emailsForStorage,
          lastUpdated: Date.now(),
          activeAccount: feAccountId,
          activeFolder: folder
        }));
        console.log('📧 Saved emails to localStorage cache (bodies stripped)');
      } catch (e) {
        console.log('📧 Could not save to localStorage:', e.message);
      }
      
      // AI Categorization - run in background to improve categorization
      try {
        const emailsForAI = messages.slice(0, 30).map(msg => ({
          email_id: msg.id,
          subject: msg.subject || '',
          sender: msg.from?.email || '',
          sender_name: msg.from?.name || '',
          body_preview: msg.snippet || ''
        }));
        
        console.log('🤖 Starting AI bulk categorization for', emailsForAI.length, 'Nylas emails...');
        const aiCategories = await aiEmailAPI.categorizeBulk(emailsForAI);
        
        if (aiCategories.categories && aiCategories.categories.length > 0) {
          // Update emails with AI categories
          const categoryMap = {};
          aiCategories.categories.forEach(cat => {
            categoryMap[cat.email_id] = cat.category;
          });
          
          setNylasEmails(prev => {
            const updated = prev.map(email => {
              if (categoryMap[email.id]) {
                return { ...email, category: categoryMap[email.id] };
              }
              return email;
            });
            // Update module-level session cache
            _nylasSessionCache[cacheKey] = updated;
            // Update component cache state
            setNylasEmailsCache(cache => ({ ...cache, [cacheKey]: updated }));
            
            // Update localStorage with AI-categorized emails (bodies stripped)
            try {
              const updatedForStorage = updated.map(({ body, thread, ...rest }) => rest);
              localStorage.setItem('nylas_emails_cache', JSON.stringify({
                cache: { [cacheKey]: updatedForStorage },
                emails: updatedForStorage,
                lastUpdated: Date.now(),
                activeAccount: feAccountId,
                activeFolder: folder
              }));
            } catch (e) { /* ignore */ }
            
            return updated;
          });
          console.log('✅ AI categorization complete -', aiCategories.count, 'Nylas emails categorized');
        }
      } catch (aiErr) {
        console.warn('AI categorization failed for Nylas, using basic categorization:', aiErr);
      }
      
    } catch (error) {
      console.error('❌ Failed to sync Nylas emails:', error);
    } finally {
      setNylasLoading(false);
    }
  };
  
  // Format Gmail timestamp
  const formatGmailTime = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      
      // Check if the date is valid
      if (isNaN(date.getTime())) return '';
      
      const now = new Date();
      const today = new Date(now.toLocaleString('en-US', { timeZone: 'America/Chicago' }));
      const emailDate = new Date(date.toLocaleString('en-US', { timeZone: 'America/Chicago' }));
      
      // Format time in 12-hour CST format
      const timeStr = date.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true,
        timeZone: 'America/Chicago'
      }).toLowerCase();
      
      // Check if same day
      const isToday = today.toDateString() === emailDate.toDateString();
      
      if (isToday) {
        return timeStr;
      }
      
      // Check if yesterday
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      if (yesterday.toDateString() === emailDate.toDateString()) {
        return `Yesterday ${timeStr}`;
      }
      
      // Otherwise show date and time
      const dateStrFormatted = date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric',
        timeZone: 'America/Chicago'
      });
      return `${dateStrFormatted} ${timeStr}`;
    } catch {
      return '';
    }
  };
  
  // Helper function to clean email HTML and extract body content
  const cleanEmailHtml = (html) => {
    if (!html) return '';
    
    let cleaned = html;
    
    // Extract body content if full HTML document
    const bodyMatch = cleaned.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
    if (bodyMatch) {
      cleaned = bodyMatch[1];
    }
    
    // Remove style tags and their content (including embedded CSS)
    cleaned = cleaned.replace(/<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi, '');
    
    // Remove script tags
    cleaned = cleaned.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
    
    // Remove head section
    cleaned = cleaned.replace(/<head[^>]*>[\s\S]*?<\/head>/gi, '');
    
    // Remove HTML comments
    cleaned = cleaned.replace(/<!--[\s\S]*?-->/g, '');
    
    // Remove meta, title, link tags
    cleaned = cleaned.replace(/<meta[^>]*>/gi, '');
    cleaned = cleaned.replace(/<title[^>]*>[\s\S]*?<\/title>/gi, '');
    cleaned = cleaned.replace(/<link[^>]*>/gi, '');
    
    // Remove DOCTYPE
    cleaned = cleaned.replace(/<!DOCTYPE[^>]*>/gi, '');
    
    // Remove html/body tags
    cleaned = cleaned.replace(/<\/?html[^>]*>/gi, '');
    cleaned = cleaned.replace(/<\/?body[^>]*>/gi, '');
    
    // Remove CSS rules that appear as plain text (common in poorly formatted emails)
    // Matches patterns like: #outlook a { padding:0; } or body { margin:0; }
    cleaned = cleaned.replace(/^\s*[-#.\w[\]@:,\s]+\s*\{[^}]*\}\s*$/gm, '');
    cleaned = cleaned.replace(/[#.@][\w-]+(?:\s*,\s*[#.@]?[\w-]+)*\s*\{[^}]*\}/g, '');
    
    // Remove @media queries that might be in the text
    cleaned = cleaned.replace(/@media[^{]*\{[\s\S]*?\}\s*\}/gi, '');
    cleaned = cleaned.replace(/@font-face[^{]*\{[^}]*\}/gi, '');
    cleaned = cleaned.replace(/@import[^;]*;/gi, '');
    
    // Clean up multiple newlines/spaces
    cleaned = cleaned.replace(/\n{3,}/g, '\n\n').trim();
    
    return cleaned;
  };
  
  // Helper function to parse email body and split quoted messages into separate parts
  const parseEmailThread = (body, originalFrom, originalEmail, originalTime) => {
    if (!body) return [{ from: originalFrom, email: originalEmail, time: originalTime, body: '', isQuoted: false, isHtml: false }];
    
    // Check if this is rich HTML content (newsletters, formatted emails with tables/images)
    // These should be rendered as HTML, not stripped
    const isRichHtml = body.includes('<table') || 
                       body.includes('<img') || 
                       body.includes('<td') ||
                       body.includes('<!DOCTYPE') ||
                       body.includes('<html');
    
    // First clean the email HTML to remove CSS and extract body content
    const cleanedBody = cleanEmailHtml(body);
    
    // Check for quote markers to detect if this is a thread
    const hasQuoteMarkers = cleanedBody.includes('hmail-quote-container') || 
                           cleanedBody.includes('gmail_quote_container') ||
                           cleanedBody.includes('gmail_quote') ||
                           cleanedBody.match(/On\s+\w+,?\s+\w+\s+\d{1,2},?\s+\d{4}.*wrote:/i);
    
    // If it's rich HTML without quote markers, preserve the HTML and return as single message
    if (isRichHtml && !hasQuoteMarkers) {
      return [{
        from: originalFrom,
        email: originalEmail,
        time: originalTime,
        body: cleanedBody, // Preserve the cleaned HTML
        isQuoted: false,
        isHtml: true // Flag to indicate this should be rendered as HTML
      }];
    }
    
    const messages = [];
    
    // First try to split by Gmail's blockquote structure
    // Gmail wraps quotes in: <div class="hmail-attr">On ... wrote:</div><blockquote>...</blockquote>
    const gmailQuotePattern = /<div[^>]*class="[^"]*hmail-attr[^"]*"[^>]*>([^<]*(?:On[^<]*wrote:|From:[^<]*))<\/div>\s*<blockquote[^>]*class="[^"]*hmail-quote[^"]*"[^>]*>([\s\S]*?)(?=<div[^>]*class="[^"]*hmail-attr|$)/gi;
    
    // Also handle gmail_quote pattern
    const gmailQuote2Pattern = /<div[^>]*class="[^"]*gmail_attr[^"]*"[^>]*>([^<]*(?:On[^<]*wrote:|From:[^<]*))<\/div>\s*<div[^>]*class="[^"]*gmail_quote[^"]*"[^>]*>([\s\S]*?)(?=<div[^>]*class="[^"]*gmail_attr|$)/gi;
    
    let workingBody = cleanedBody;
    let lastIndex = 0;
    let foundQuotes = false;
    
    // Try Gmail hmail-quote pattern first
    const quoteMatches = [];
    let match;
    
    // Find all quote containers
    const containerPattern = /<div[^>]*class="[^"]*(?:hmail-quote-container|gmail_quote_container)[^"]*"[^>]*>([\s\S]*?)(?=<div[^>]*class="[^"]*(?:hmail-quote-container|gmail_quote_container)|$)/gi;
    
    while ((match = containerPattern.exec(cleanedBody)) !== null) {
      quoteMatches.push({
        fullMatch: match[0],
        content: match[1],
        index: match.index
      });
    }
    
    if (quoteMatches.length > 0) {
      foundQuotes = true;
      
      // Get the main message (before any quote containers)
      const firstQuoteIndex = quoteMatches[0].index;
      const mainContent = cleanedBody.substring(0, firstQuoteIndex).trim();
      
      if (mainContent) {
        // Clean HTML and extract text
        const cleanMain = mainContent
          .replace(/<br\s*\/?>/gi, '\n')
          .replace(/<\/div>/gi, '\n')
          .replace(/<div[^>]*>/gi, '')
          .replace(/<[^>]+>/g, '')
          .replace(/&nbsp;/g, ' ')
          .replace(/&lt;/g, '<')
          .replace(/&gt;/g, '>')
          .replace(/\n{3,}/g, '\n\n')
          .trim();
        
        if (cleanMain) {
          messages.push({
            from: originalFrom,
            email: originalEmail,
            time: originalTime,
            body: cleanMain,
            isQuoted: false
          });
        }
      }
      
      // Process each quoted section
      for (const quoteMatch of quoteMatches) {
        const quoteContent = quoteMatch.content;
        
        // Extract the "On ... wrote:" attribution - handle both raw text and within div
        let attrText = '';
        const attrMatch = quoteContent.match(/<div[^>]*class="[^"]*(?:hmail-attr|gmail_attr)[^"]*"[^>]*>([\s\S]*?)<\/div>/i);
        if (attrMatch) {
          attrText = attrMatch[1]
            .replace(/<[^>]+>/g, '')
            .replace(/&nbsp;/g, ' ')
            .replace(/&lt;/g, '<')
            .replace(/&gt;/g, '>')
            .trim();
        }
        
        let senderName = 'Previous message';
        let senderEmail = '';
        
        if (attrText) {
          // Extract sender name and email from "On Wed, Feb 4, 2026 at 4:22 AM CREATRVERSE <creatrhq@gmail.com> wrote:"
          // Pattern: everything after time and before < or "wrote:"
          const senderMatch = attrText.match(/(?:On\s+\w+,?\s+\w+\s+\d{1,2},?\s+\d{4}\s+at\s+\d{1,2}:\d{2}\s*(?:AM|PM)?\s+)?(.+?)(?:\s*<([^>]+)>)?\s*wrote:/i);
          if (senderMatch) {
            senderName = senderMatch[1]?.trim() || 'Previous message';
            senderEmail = senderMatch[2]?.trim() || '';
          }
        }
        
        // Extract the quoted message body (inside blockquote or gmail_quote div)
        // First get content after the attr div
        let quotedBodyHtml = quoteContent;
        if (attrMatch) {
          const attrEndIndex = quoteContent.indexOf(attrMatch[0]) + attrMatch[0].length;
          quotedBodyHtml = quoteContent.substring(attrEndIndex);
        }
        
        // Look for blockquote or extract remaining content
        const blockquoteMatch = quotedBodyHtml.match(/<blockquote[^>]*>([\s\S]*?)<\/blockquote>/i);
        let quotedBody = '';
        
        if (blockquoteMatch) {
          quotedBody = blockquoteMatch[1];
        } else {
          // Use the remaining content
          quotedBody = quotedBodyHtml;
        }
        
        // Clean the body - but check for nested quote containers first
        const hasNestedQuotes = quotedBody.includes('hmail-quote-container') || quotedBody.includes('gmail_quote_container');
        
        if (hasNestedQuotes) {
          // Split nested quotes into separate messages recursively
          const nestedParsed = parseEmailThread(quotedBody, senderName, senderEmail, '');
          messages.push(...nestedParsed.map(m => ({ ...m, isQuoted: true })));
        } else {
          // Clean and add as single quoted message
          quotedBody = quotedBody
            .replace(/<br\s*\/?>/gi, '\n')
            .replace(/<\/div>/gi, '\n')
            .replace(/<div[^>]*>/gi, '')
            .replace(/<[^>]+>/g, '')
            .replace(/&nbsp;/g, ' ')
            .replace(/&lt;/g, '<')
            .replace(/&gt;/g, '>')
            .replace(/\n{3,}/g, '\n\n')
            .trim();
          
          // Don't add if it's just another nested quote marker or empty
          if (quotedBody && quotedBody.length > 5 && !quotedBody.match(/^On\s+\w+,?\s+\w+\s+\d/)) {
            messages.push({
              from: senderName,
              email: senderEmail,
              time: '',
              body: quotedBody,
              isQuoted: true
            });
          }
        }
      }
    }
    
    // If no Gmail quote structure found, try plain text patterns
    if (!foundQuotes) {
      const plainTextBody = body
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<\/div>/gi, '\n')
        .replace(/<div[^>]*>/gi, '')
        .replace(/<[^>]+>/g, '')
        .replace(/&nbsp;/g, ' ')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .trim();
      
      // Split by multiple quote patterns
      // This regex captures:
      // 1. "From: [Name] <email>\nDate/Sent: [date]\nTo: [name]" format (Outlook/Mac Mail)
      // 2. "On [Day], [Date] at [Time] [Name] <email> wrote:" format (Gmail)
      
      const combinedPattern = /(?:(?:^|\n)From:\s*([^\n<]+?)(?:\s*<([^>]+)>)?\s*\n\s*(?:Date|Sent):\s*[^\n]+\n\s*To:\s*[^\n]+(?:\n\s*Subject:\s*[^\n]+)?\s*\n)|(?:On\s+(?:Mon|Tue|Wed|Thu|Fri|Sat|Sun)(?:day)?,?\s+(?:Jan(?:uary)?|Feb(?:ruary)?|Mar(?:ch)?|Apr(?:il)?|May|Jun(?:e)?|Jul(?:y)?|Aug(?:ust)?|Sep(?:t(?:ember)?)?|Oct(?:ober)?|Nov(?:ember)?|Dec(?:ember)?)\s+\d{1,2},?\s+\d{4}\s+at\s+\d{1,2}:\d{2}\s*(?:AM|PM)?\s*,?\s*([^\n<]+?)(?:\s*<([^>\n]+)>)?\s*wrote:)/gi;
      
      // Find all matches and their positions
      const matches = [];
      let match;
      combinedPattern.lastIndex = 0;
      
      while ((match = combinedPattern.exec(plainTextBody)) !== null) {
        // Extract sender from either capture group (From: pattern or On wrote: pattern)
        const senderName = (match[1] || match[3] || 'Previous message').trim();
        const senderEmail = (match[2] || match[4] || '').trim();
        
        matches.push({
          index: match.index,
          length: match[0].length,
          senderName,
          senderEmail
        });
      }
      
      if (matches.length > 0) {
        // First section is the main message (before first quote)
        const firstQuoteIndex = matches[0].index;
        const mainContent = plainTextBody.substring(0, firstQuoteIndex).trim();
        
        if (mainContent && mainContent.length > 5) {
          messages.push({
            from: originalFrom,
            email: originalEmail,
            time: originalTime,
            body: mainContent,
            isQuoted: false
          });
        }
        
        // Process each quoted section
        for (let i = 0; i < matches.length; i++) {
          const currentMatch = matches[i];
          const nextMatch = matches[i + 1];
          
          // Get content after this quote header until next quote or end
          const startPos = currentMatch.index + currentMatch.length;
          const endPos = nextMatch ? nextMatch.index : plainTextBody.length;
          const quotedContent = plainTextBody.substring(startPos, endPos).trim();
          
          if (quotedContent && quotedContent.length > 5) {
            // Check if sender is "me" (CREATRVERSE/creatrhq)
            const isSentByMe = (currentMatch.senderName || '').toLowerCase().includes('creatrverse') ||
                               (currentMatch.senderEmail || '').toLowerCase().includes('creatrhq');
            
            messages.push({
              from: isSentByMe ? 'You' : currentMatch.senderName,
              email: currentMatch.senderEmail,
              time: '',
              body: quotedContent,
              isQuoted: true
            });
          }
        }
      }
    }
    
    // If still no messages parsed, return original as single message
    if (messages.length === 0) {
      // Check if this looks like rich HTML that should be preserved
      const shouldPreserveHtml = cleanedBody.includes('<table') || 
                                  cleanedBody.includes('<img') || 
                                  cleanedBody.includes('<td') ||
                                  cleanedBody.includes('<a href');
      
      if (shouldPreserveHtml) {
        messages.push({
          from: originalFrom,
          email: originalEmail,
          time: originalTime,
          body: cleanedBody,
          isQuoted: false,
          isHtml: true
        });
      } else {
        const cleanBody = cleanedBody
          .replace(/<br\s*\/?>/gi, '\n')
          .replace(/<\/div>/gi, '\n')
          .replace(/<div[^>]*>/gi, '')
          .replace(/<[^>]+>/g, '')
          .replace(/&nbsp;/g, ' ')
          .replace(/&lt;/g, '<')
          .replace(/&gt;/g, '>')
          .replace(/\n{3,}/g, '\n\n')
          .trim();
        
        messages.push({
          from: originalFrom,
          email: originalEmail,
          time: originalTime,
          body: cleanBody || cleanedBody,
          isQuoted: false,
          isHtml: false
        });
      }
    }
    
    return messages.filter(m => m.body && m.body.trim().length > 0);
  };
  
  // Helper function to detect if sender is a person vs company
  const isPersonalSender = (from, email) => {
    const fromLower = (from || '').toLowerCase();
    const emailLower = (email || '').toLowerCase();
    
    // Company indicators
    const companyIndicators = [
      'noreply', 'no-reply', 'donotreply', 'do-not-reply', 'notifications',
      'support', 'team', 'info@', 'hello@', 'contact@', 'sales@', 'marketing@',
      'newsletter', 'updates', 'billing@', 'accounts@', 'service@', 'help@',
      '.com>', 'inc', 'llc', 'corp', 'ltd', 'company', 'enterprise', 'solutions',
      'automated', 'system', 'alert', 'notification'
    ];
    
    // If email contains company indicators, it's not personal
    if (companyIndicators.some(ind => emailLower.includes(ind) || fromLower.includes(ind))) {
      return false;
    }
    
    // Check if sender name looks like a person (has first and last name pattern)
    const nameParts = fromLower.split(' ').filter(p => p.length > 1);
    if (nameParts.length >= 2 && !companyIndicators.some(ind => nameParts.some(p => p.includes(ind)))) {
      return true;
    }
    
    return false;
  };
  
  // Categorize email based on content
  const categorizeEmail = (email) => {
    const subject = (email.subject || '').toLowerCase();
    const from = (email.from || email.from_email || '').toLowerCase();
    const fromEmail = (email.email || email.from_email || '').toLowerCase();
    const body = (email.body || email.snippet || email.preview || '').toLowerCase();
    // Handle 'to' field - can be array of objects or string
    const to = Array.isArray(email.to) 
      ? email.to.map(t => (t.email || t.name || t || '')).join(' ').toLowerCase()
      : (email.to || '').toLowerCase();
    
    // Combine all text for comprehensive scanning
    const allText = `${subject} ${from} ${body} ${to}`;
    
    // Check for dollar amounts (e.g., $10, $99.99, $1,000.00)
    const hasDollarAmount = /\$\d+(?:,\d{3})*(?:\.\d{2})?/.test(subject + body);
    
    // URGENT - Check first, highest priority
    const urgentKeywords = [
      'urgent', 'asap', 'immediately', 'critical', 'emergency', 'time-sensitive',
      'action required', 'immediate attention', 'priority', 'deadline', 'overdue',
      'need help', 'please respond', 'waiting for', 'as soon as possible', 'deadline today'
    ];
    if (urgentKeywords.some(keyword => allText.includes(keyword))) {
      return 'urgent';
    }
    
    // TRAVEL - Flights, bookings, itineraries, boarding passes
    const travelKeywords = [
      'flight', 'booking confirmation', 'itinerary', 'boarding pass', 'check-in',
      'airline', 'airport', 'departure', 'arrival', 'layover', 'gate',
      'seat assignment', 'e-ticket', 'travel confirmation', 'trip confirmation',
      'hotel reservation', 'hotel booking', 'car rental', 'rental confirmation',
      'united airlines', 'american airlines', 'delta', 'southwest', 'jetblue',
      'british airways', 'lufthansa', 'emirates', 'frontier', 'spirit airlines',
      'alaska airlines', 'hawaiian airlines', 'air france', 'klm',
      'booking.com', 'expedia', 'kayak', 'priceline', 'tripadvisor',
      'marriott', 'hilton', 'hyatt', 'airbnb', 'vrbo',
      'tsa precheck', 'global entry', 'passport', 'visa application',
      'fly', 'nonstop', 'round trip', 'one way', 'class of service',
      'baggage', 'carry-on', 'checked bag', 'travel insurance'
    ];
    const travelSenders = [
      'airline', 'airlines', 'booking', 'expedia', 'kayak', 'travel',
      'marriott', 'hilton', 'hyatt', 'airbnb', 'hertz', 'avis', 'enterprise'
    ];
    if (travelKeywords.some(keyword => allText.includes(keyword)) ||
        travelSenders.some(sender => from.includes(sender) || fromEmail.includes(sender))) {
      return 'travel';
    }
    
    // ORDERS - Receipt/Invoice/Order related OR contains dollar amounts
    const orderKeywords = [
      'receipt', 'invoice', 'order', 'purchase', 'transaction', 'payment received',
      'order confirmation', 'order shipped', 'order delivered', 'tracking number',
      'your order', 'order #', 'invoice #', 'receipt #', 'payment confirmation',
      'billing statement', 'subscription renewed', 'charge', 'refund', 'paid',
      'total:', 'amount:', 'price', 'cost', 'fee', 'payment', 'shipped',
      'delivery', 'out for delivery', 'has shipped', 'amazon', 'ebay', 'paypal'
    ];
    if (hasDollarAmount || orderKeywords.some(keyword => allText.includes(keyword))) {
      return 'orders';
    }
    
    // MEETING - Tours, meetings, calls, scheduling
    const meetingKeywords = [
      'meeting', 'schedule', 'calendar', 'appointment', 'call', 'zoom', 'teams',
      'tour', 'showing', 'viewing', 'walkthrough', 'site visit', 'property tour',
      'phone call', 'video call', 'conference', 'webinar', 'demo', 'presentation',
      'reschedule', 'agenda', 'invite', 'invitation', 'rsvp', 'confirm attendance',
      'availability', 'book a time', 'schedule a call', 'let\'s meet', 'meet up',
      'catch up', 'touch base', 'check in', 'sync up', 'huddle', 'standup',
      'google meet', 'microsoft teams', 'webex', 'gotomeeting'
    ];
    if (meetingKeywords.some(keyword => allText.includes(keyword))) {
      return 'meeting';
    }
    
    // CLIENT - Business/client communications (not orders)
    const clientKeywords = [
      'contract', 'agreement', 'proposal', 'quote', 'estimate', 'project',
      'customer', 'client', 'deal', 'opportunity', 'partnership', 'collaboration',
      'account update', 'service update', 'renewal notice', 'subscription',
      'inquiry', 'interested in', 'property', 'listing', 'looking to buy',
      'looking to sell', 'offer', 'closing', 'escrow', 'commission'
    ];
    if (clientKeywords.some(keyword => allText.includes(keyword))) {
      return 'client';
    }
    
    // REPORT - Automated/newsletter/notification emails
    const reportKeywords = [
      'report', 'analytics', 'summary', 'digest', 'newsletter', 'update',
      'notification', 'automated', 'weekly', 'monthly', 'daily report',
      'performance', 'metrics', 'statistics', 'insights', 'dashboard',
      'status update', 'changelog', 'release notes', 'security alert',
      'unsubscribe', 'marketing', 'promotion'
    ];
    if (reportKeywords.some(keyword => allText.includes(keyword))) {
      return 'report';
    }
    
    // CHECK CUSTOM CATEGORIES - scan for user-defined category keywords
    const savedKeywords = localStorage.getItem('custom_category_keywords');
    if (savedKeywords) {
      try {
        const customKeywords = JSON.parse(savedKeywords);
        for (const [category, keywords] of Object.entries(customKeywords)) {
          if (Array.isArray(keywords) && keywords.some(kw => allText.includes(kw.toLowerCase()))) {
            return category;
          }
        }
      } catch (e) {
        console.error('Error parsing custom keywords:', e);
      }
    }
    
    // Default to 'other' for uncategorized emails
    return 'other';
  };
  
  // Scan and recategorize all emails based on current category tabs/keywords
  const scanAndRecategorizeEmails = async () => {
    setIsScanning(true);
    
    try {
      // Get emails for the CURRENT active account only
      let emailsToScan = [];
      
      if (activeEmailAccount === 'outlook') {
        emailsToScan = Array.isArray(outlookEmails) ? outlookEmails : [];
      } else {
        const activeAccount = emailAccounts.find(acc => acc.id === activeEmailAccount);
        if (activeAccount && activeAccount.type === 'gmail') {
          emailsToScan = Array.isArray(gmailEmails) ? gmailEmails : [];
        } else if (activeAccount && activeAccount.type === 'nylas') {
          emailsToScan = Array.isArray(nylasEmails) ? nylasEmails : [];
        }
      }
      
      if (emailsToScan.length === 0) {
        toast.info('No emails to scan. Make sure emails are loaded for this account.');
        setIsScanning(false);
        return;
      }
      
      // Get the categories for THIS account
      const accountCategories = emailCategoriesByAccount[activeEmailAccount] || defaultEmailCategories;
      const accountKeywords = emailKeywordsByAccount[activeEmailAccount] || {};
      
      console.log('📧 SCAN DEBUG ================================');
      console.log('📧 Active account:', activeEmailAccount);
      console.log('📧 Emails to scan:', emailsToScan.length);
      console.log('📧 Account categories:', accountCategories);
      console.log('📧 Account keywords:', accountKeywords);
      
      // Build keyword map from account-specific categories
      const categoryKeywordMap = {};
      
      for (const category of accountCategories) {
        if (category === 'all' || category === 'other') continue;
        
        // Get saved keywords for this category
        const savedKeywords = accountKeywords[category] || [];
        
        // Auto-generate keywords from category name
        const autoKeywords = generateKeywordsFromCategory(formatCategoryName(category));
        
        // Combine all keywords
        const allKeywords = [...new Set([
          category.toLowerCase(),
          formatCategoryName(category).toLowerCase(),
          ...savedKeywords.map(k => k.toLowerCase().trim()),
          ...autoKeywords.map(k => k.toLowerCase().trim())
        ])].filter(k => k && k.length >= 2);
        
        categoryKeywordMap[category] = allKeywords;
        console.log(`📁 Category "${category}" → ${allKeywords.length} keywords:`, allKeywords.slice(0, 10).join(', '));
      }
      
      if (Object.keys(categoryKeywordMap).length === 0) {
        toast.error('No categories to scan. Create some category tabs first (other than "all" and "other").');
        setIsScanning(false);
        return;
      }
      
      console.log('📧 ==========================================');
      toast.info(`Scanning ${emailsToScan.length} emails across ${Object.keys(categoryKeywordMap).length} categories...`);
      
      // Categorization function
      const categorizeWithKeywords = (email) => {
        const subject = (email.subject || '').toLowerCase();
        const fromName = (email.from || '').toLowerCase();
        const fromEmail = (email.email || email.from_email || '').toLowerCase();
        const body = (email.body || email.snippet || email.preview || '').toLowerCase();
        const to = Array.isArray(email.to) 
          ? email.to.map(t => (t.email || t.name || t || '')).join(' ').toLowerCase()
          : (email.to || '').toLowerCase();
        
        // Combine all text
        const allText = `${subject} ${fromName} ${fromEmail} ${body} ${to}`;
        
        // Score each category
        let bestCategory = 'other';
        let bestScore = 0;
        
        for (const [category, keywords] of Object.entries(categoryKeywordMap)) {
          let score = 0;
          
          for (const kw of keywords) {
            if (!kw || kw.length < 2) continue;
            
            // Subject match = highest priority
            if (subject.includes(kw)) score += 25;
            // From email match
            if (fromEmail.includes(kw)) score += 20;
            // From name match
            if (fromName.includes(kw)) score += 15;
            // Body/snippet match
            if (body.includes(kw)) score += 5;
          }
          
          if (score > bestScore) {
            bestScore = score;
            bestCategory = category;
          }
        }
        
        return { category: bestScore >= 5 ? bestCategory : 'other', score: bestScore };
      };
      
      // Categorize all emails
      const newAssignments = {};
      const counts = {};
      
      for (let i = 0; i < emailsToScan.length; i++) {
        const email = emailsToScan[i];
        if (!email.id) continue;
        
        try {
          const result = categorizeWithKeywords(email);
          newAssignments[email.id] = result.category;
          counts[result.category] = (counts[result.category] || 0) + 1;
          
          // Log first 10 for debugging
          if (i < 10) {
            const subject = (email.subject || '').substring(0, 50);
            console.log(`${result.score >= 5 ? '✅' : '❌'} [${result.score}] "${subject}..." → ${result.category}`);
          }
        } catch (e) {
          console.warn('Error categorizing email:', e);
          newAssignments[email.id] = 'other';
          counts['other'] = (counts['other'] || 0) + 1;
        }
      }
      
      // Save assignments to account-specific state
      setEmailAssignmentsByAccount(prev => ({
        ...prev,
        [activeEmailAccount]: newAssignments
      }));
      
      // Save to database
      try {
        await fetch(`${window.location.origin}/api/email-categories/assignments/bulk?user_id=${userId}_${activeEmailAccount}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ assignments: newAssignments })
        });
      } catch (e) {
        console.warn('Could not save assignments to database:', e);
      }
      
      // Update category counts
      setCategoryCounts(counts);
      
      // Show results
      const categoryList = Object.entries(counts)
        .filter(([_, count]) => count > 0)
        .sort((a, b) => b[1] - a[1])
        .map(([cat, count]) => `${cat}: ${count}`)
        .join(', ');
      
      toast.success(`✅ Scan complete! ${emailsToScan.length} emails categorized. ${categoryList || 'All in Other'}`);
      console.log('✅ Scan complete! Category counts:', counts);
      
    } catch (error) {
      console.error('❌ Error scanning emails:', error);
      toast.error(`Failed to scan emails: ${error.message || 'Unknown error'}`);
    } finally {
      setIsScanning(false);
    }
  };
  
  // Helper to check if active account is a Gmail account
  const isActiveAccountGmail = () => {
    const activeAccount = emailAccounts.find(acc => acc.id === activeEmailAccount);
    return activeAccount && activeAccount.type === 'gmail';
  };

  // Fetch Gmail emails when any Gmail account tab is selected
  useEffect(() => {
    const activeAccount = emailAccounts.find(acc => acc.id === activeEmailAccount);
    const isGmailAccount = activeAccount && activeAccount.type === 'gmail';
    const isNylasAccount = activeAccount && activeAccount.type === 'nylas';
    
    if (isGmailAccount) {
      // Check if account is connected
      const isConnected = activeAccount.connected || 
        (activeEmailAccount === 'gmail' && gmailConnected) ||
        (activeEmailAccount === 'wdjwood'); // wdjwood is a known connected account
      
      if (isConnected) {
        // Get the backend account ID for this Gmail account
        const backendAccountId = getGmailAccountId(activeAccount.email);
        
        // Check if we have cached emails for this folder and account
        const cacheKey = `${activeEmailAccount}_${activeFolder}`;
        if (gmailEmailsCache[cacheKey] && gmailEmailsCache[cacheKey].length > 0) {
          setGmailEmails(gmailEmailsCache[cacheKey]);
          console.log(`📧 Using cached ${gmailEmailsCache[cacheKey].length} Gmail emails for ${activeEmailAccount}/${activeFolder}`);
          // Don't fetch again if we already have cached data
        } else if (!fetchedGmailKeys.has(cacheKey) && !gmailLoading) {
          console.log(`📧 Syncing Gmail emails for ${activeEmailAccount}/${activeFolder} (backend: ${backendAccountId})`);
          // Pass both backend accountId and frontend accountId to avoid stale closures
          syncGmailEmails(activeFolder, backendAccountId, activeEmailAccount);
          setFetchedGmailKeys(prev => new Set([...prev, cacheKey]));
        }
      }
    } else if (isNylasAccount && activeAccount.connected) {
      // Fetch Nylas emails — check session cache first (survives component remount)
      const cacheKey = `${activeEmailAccount}_${activeFolder}`;
      if (_nylasSessionCache[cacheKey] && _nylasSessionCache[cacheKey].length > 0) {
        setNylasEmails(_nylasSessionCache[cacheKey]);
        console.log(`📧 Session cache hit: ${_nylasSessionCache[cacheKey].length} Nylas emails for ${activeEmailAccount}/${activeFolder}`);
        // Also sync component-level cache state so further checks pass
        setNylasEmailsCache(prev => ({ ...prev, [cacheKey]: _nylasSessionCache[cacheKey] }));
      } else if (nylasEmailsCache[cacheKey] && nylasEmailsCache[cacheKey].length > 0) {
        setNylasEmails(nylasEmailsCache[cacheKey]);
        console.log(`📧 Using cached ${nylasEmailsCache[cacheKey].length} Nylas emails for ${activeEmailAccount}/${activeFolder}`);
        // Warm up session cache
        _nylasSessionCache[cacheKey] = nylasEmailsCache[cacheKey];
        // Don't fetch again if we already have cached data
      } else if (!fetchedEmailKeys.has(cacheKey) && !nylasLoading) {
        console.log(`📧 Syncing Nylas emails for ${activeEmailAccount}/${activeFolder}`);
        syncNylasEmails(activeFolder, activeAccount.email, activeEmailAccount);
        setFetchedEmailKeys(prev => new Set([...prev, cacheKey]));
      }
    }
  }, [activeEmailAccount, activeFolder, gmailConnected, emailAccounts]);
  
  // Handle URL params for compose email (from Lead Generation page)
  useEffect(() => {
    const compose = searchParams.get('compose');
    const to = searchParams.get('to');
    const subject = searchParams.get('subject');
    const body = searchParams.get('body');
    
    if (compose === 'true') {
      // Switch to email tab and open compose modal
      setActiveTab('email');
      setEmailTo(to ? decodeURIComponent(to) : '');
      setEmailSubject(subject ? decodeURIComponent(subject) : '');
      setEmailBody(body ? decodeURIComponent(body) : '');
      setEmailCc('');
      setEmailBcc('');
      setShowEmailComposeModal(true);
      
      // Clear URL params after processing
      setSearchParams({});
    }
    
    // Handle SMS compose from Lead Generation page
    const sms = searchParams.get('sms');
    const phone = searchParams.get('phone');
    const message = searchParams.get('message');
    
    if (sms === 'true') {
      // Switch to messages tab and open compose modal
      setActiveTab('messages');
      setComposeRecipient(phone ? decodeURIComponent(phone) : '');
      setComposeMessage(message ? decodeURIComponent(message) : '');
      setShowComposeModal(true);
      
      // Clear URL params after processing
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);
  
  // Meeting modal state
  const [showAddToMeetingModal, setShowAddToMeetingModal] = useState(false);
  const [showNewMeetingModal, setShowNewMeetingModal] = useState(false);
  const [meetingConversation, setMeetingConversation] = useState(null);
  const [existingMeetings, setExistingMeetings] = useState([]);
  const [selectedMeetingId, setSelectedMeetingId] = useState('');
  const [newMeetingTitle, setNewMeetingTitle] = useState('');
  const [newMeetingDate, setNewMeetingDate] = useState('');
  const [newMeetingNotes, setNewMeetingNotes] = useState('');
  const [activeCallDropdown, setActiveCallDropdown] = useState(null);
  const [activeMeetingPlatform, setActiveMeetingPlatform] = useState(null);
  const [isMeetingActive, setIsMeetingActive] = useState(false);
  const [isMicMuted, setIsMicMuted] = useState(false);
  const [isCameraOff, setIsCameraOff] = useState(false);
  const [mediaStream, setMediaStream] = useState(null);
  const [emailReplyInput, setEmailReplyInput] = useState('');
  const [deletedMessages, setDeletedMessages] = useState({});
  const [selectedContact, setSelectedContact] = useState('');
  const [showContactDropdown, setShowContactDropdown] = useState(false);
  const [showCommWidgetModal, setShowCommWidgetModal] = useState(false);
  const [commWidgetModalType, setCommWidgetModalType] = useState('');
  const [composeRecipient, setComposeRecipient] = useState('');
  const [composeMessage, setComposeMessage] = useState('');
  const [composeSending, setComposeSending] = useState(false);
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [showContactSuggestions, setShowContactSuggestions] = useState(false);
  
  // State for compose phone selection (personal, business, twilio)
  const [composeFromPhone, setComposeFromPhone] = useState(() => {
    try {
      return localStorage.getItem('compose_default_phone') || 'personal';
    } catch { return 'personal'; }
  });
  
  // User's phone numbers - stored in localStorage
  const [userPhoneNumbers, setUserPhoneNumbers] = useState(() => {
    try {
      const stored = localStorage.getItem('user_phone_numbers');
      return stored ? JSON.parse(stored) : {
        personal: '',
        business: '',
        twilio: localStorage.getItem('twilio_phone_number') || ''
      };
    } catch { 
      return { personal: '', business: '', twilio: '' }; 
    }
  });
  const [showPhoneSettingsModal, setShowPhoneSettingsModal] = useState(false);
  
  // State for searching within a conversation
  const [conversationSearchQuery, setConversationSearchQuery] = useState('');
  const [showConversationSearch, setShowConversationSearch] = useState(false);
  
  // Helper function to format timestamp intelligently
  // - Within 24 hours: show time (e.g., "2:30 PM")
  // - Within past week: show day name (e.g., "Monday")
  // - Older than a week: show date (e.g., "01/12/26")
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    
    // Ensure timestamp is a string
    const ts = String(timestamp);
    
    const now = new Date();
    let messageDate = null;
    
    // Check if it's already a time format like "08:45 AM" or "2:30 PM" - return as is (it's today)
    if (/^\d{1,2}:\d{2}\s*(AM|PM|am|pm)$/i.test(ts.trim())) {
      return ts.trim().toLowerCase();
    }
    
    // If already in MM/DD/YY format, check if it's today
    if (/^\d{2}\/\d{2}\/\d{2}$/.test(ts)) {
      const [month, day, year] = ts.split('/');
      const fullYear = 2000 + parseInt(year);
      
      // Check if it's today - if so, return "Today" or current time
      const today = new Date();
      if (parseInt(month) === today.getMonth() + 1 && 
          parseInt(day) === today.getDate() && 
          fullYear === today.getFullYear()) {
        // It's today but we don't have the time, so just return "Today"
        return 'Today';
      }
      
      messageDate = new Date(fullYear, parseInt(month) - 1, parseInt(day));
    }
    
    // Handle "Dec 20, 2025 at 1:08 AM" format
    if (!messageDate && ts.includes(' at ')) {
      const [datePart, timePart] = ts.split(' at ');
      const monthNames = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
      const dateMatch = datePart.match(/(\w+)\s+(\d{1,2}),?\s+(\d{4})/i);
      if (dateMatch) {
        const monthStr = dateMatch[1].toLowerCase().slice(0, 3);
        const monthIndex = monthNames.indexOf(monthStr);
        if (monthIndex !== -1) {
          // Parse time part (e.g., "1:08 AM" or "2:30 PM")
          const timeMatch = timePart?.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
          let hours = 0, minutes = 0;
          if (timeMatch) {
            hours = parseInt(timeMatch[1]);
            minutes = parseInt(timeMatch[2]);
            const isPM = timeMatch[3].toUpperCase() === 'PM';
            if (isPM && hours !== 12) hours += 12;
            if (!isPM && hours === 12) hours = 0;
          }
          messageDate = new Date(parseInt(dateMatch[3]), monthIndex, parseInt(dateMatch[2]), hours, minutes);
        }
      }
    }
    
    // Try parsing "Dec 20, 2025" format without time
    if (!messageDate) {
      const monthNames = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
      const dateMatch = ts.match(/(\w+)\s+(\d{1,2}),?\s+(\d{4})/i);
      if (dateMatch) {
        const monthStr = dateMatch[1].toLowerCase().slice(0, 3);
        const monthIndex = monthNames.indexOf(monthStr);
        if (monthIndex !== -1) {
          messageDate = new Date(parseInt(dateMatch[3]), monthIndex, parseInt(dateMatch[2]));
        }
      }
    }
    
    // Try standard Date parsing
    if (!messageDate) {
      const parsed = new Date(ts);
      if (!isNaN(parsed.getTime())) {
        messageDate = parsed;
      }
    }
    
    // Handle "Just now"
    if (!messageDate && ts.toLowerCase().includes('just now')) {
      return now.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        timeZone: 'America/Chicago'
      }).toLowerCase();
    }
    
    // If we still couldn't parse, return the original timestamp
    if (!messageDate) {
      return timestamp;
    }
    
    // Always show the actual time in CST 12-hour format
    const timeStr = messageDate.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: 'America/Chicago'
    }).toLowerCase();
    
    // Check if today
    const todayCST = new Date(now.toLocaleString('en-US', { timeZone: 'America/Chicago' }));
    const messageDateCST = new Date(messageDate.toLocaleString('en-US', { timeZone: 'America/Chicago' }));
    
    if (todayCST.toDateString() === messageDateCST.toDateString()) {
      return timeStr;
    }
    
    // Check if yesterday
    const yesterday = new Date(todayCST);
    yesterday.setDate(yesterday.getDate() - 1);
    if (yesterday.toDateString() === messageDateCST.toDateString()) {
      return `Yesterday ${timeStr}`;
    }
    
    // Otherwise show date and time
    const dateStr = messageDate.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      timeZone: 'America/Chicago'
    });
    return `${dateStr} ${timeStr}`;
  };

  // Format email thread timestamp - always show full date and time in CST 12-hour format
  const formatEmailThreadTime = (timestamp) => {
    if (!timestamp) return '';
    
    const ts = String(timestamp);
    let messageDate = null;
    
    // Check if it's already a relative time like "3d", "5h", "2w" - skip these
    if (/^\d+[hdwm]$/i.test(ts.trim())) {
      // This is a relative time that was pre-formatted - return it as-is
      // or we can return empty to indicate we need a raw date
      return ts;
    }
    
    // Try to parse various timestamp formats
    // ISO format: 2024-12-20T13:45:00Z
    if (ts.includes('T') || ts.includes('Z')) {
      messageDate = new Date(ts);
    }
    
    // Unix timestamp (seconds)
    if (!messageDate && /^\d{10}$/.test(ts)) {
      messageDate = new Date(parseInt(ts) * 1000);
    }
    
    // Unix timestamp (milliseconds)
    if (!messageDate && /^\d{13}$/.test(ts)) {
      messageDate = new Date(parseInt(ts));
    }
    
    // Handle "Dec 20, 2025 at 1:08 AM" format
    if (!messageDate && ts.includes(' at ')) {
      const [datePart, timePart] = ts.split(' at ');
      const monthNames = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
      const dateMatch = datePart.match(/(\w+)\s+(\d{1,2}),?\s+(\d{4})/i);
      if (dateMatch) {
        const monthStr = dateMatch[1].toLowerCase().slice(0, 3);
        const monthIndex = monthNames.indexOf(monthStr);
        if (monthIndex !== -1) {
          const timeMatch = timePart?.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
          let hours = 0, minutes = 0;
          if (timeMatch) {
            hours = parseInt(timeMatch[1]);
            minutes = parseInt(timeMatch[2]);
            const isPM = timeMatch[3].toUpperCase() === 'PM';
            if (isPM && hours !== 12) hours += 12;
            if (!isPM && hours === 12) hours = 0;
          }
          messageDate = new Date(parseInt(dateMatch[3]), monthIndex, parseInt(dateMatch[2]), hours, minutes);
        }
      }
    }
    
    // Try standard Date parsing
    if (!messageDate) {
      const parsed = new Date(ts);
      if (!isNaN(parsed.getTime())) {
        messageDate = parsed;
      }
    }
    
    // If couldn't parse, return original
    if (!messageDate || isNaN(messageDate.getTime())) {
      return ts;
    }
    
    // Format as full date and time in CST (America/Chicago) 12-hour format
    try {
      return messageDate.toLocaleString('en-US', {
        timeZone: 'America/Chicago',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      }) + ' CST';
    } catch (e) {
      // Fallback if timezone not supported
      return messageDate.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    }
  };
  
  // Helper function to get category badge styling - vibrant colors
  const getCategoryBadge = (category) => {
    const badges = {
      'business': { bg: 'bg-blue-500/20', text: 'text-blue-400', label: 'Business' },
      'personal': { bg: 'bg-green-500/20', text: 'text-green-400', label: 'Personal' },
      'urgent': { bg: 'bg-red-500', text: 'text-white', label: 'Urgent' },
      'follow-up': { bg: 'bg-yellow-500', text: 'text-black', label: 'Follow Up' },
      'follow_up': { bg: 'bg-yellow-500', text: 'text-black', label: 'Follow Up' },
      'client': { bg: 'bg-purple-500', text: 'text-white', label: 'Client' },
      'report': { bg: 'bg-cyan-500', text: 'text-white', label: 'Report' },
      'meeting': { bg: 'bg-blue-500', text: 'text-white', label: 'Meeting' },
      'meeting_request': { bg: 'bg-blue-500', text: 'text-white', label: 'Meeting' },
      'travel': { bg: 'bg-orange-500', text: 'text-white', label: 'Travel' },
      'codes': { bg: 'bg-pink-500', text: 'text-white', label: 'Codes' },
      'general': { bg: 'bg-gray-600', text: 'text-gray-200', label: 'General' },
      'other': { bg: 'bg-gray-500', text: 'text-white', label: 'Other' },
    };
    
    // If it's a known badge, return it
    if (badges[category]) {
      return badges[category];
    }
    
    // For custom categories, generate a badge with a distinctive color
    // Use a hash of the category name to pick a consistent color
    const colors = [
      { bg: 'bg-pink-500', text: 'text-white' },
      { bg: 'bg-indigo-500', text: 'text-white' },
      { bg: 'bg-teal-500', text: 'text-white' },
      { bg: 'bg-rose-500', text: 'text-white' },
      { bg: 'bg-emerald-500', text: 'text-white' },
      { bg: 'bg-amber-500', text: 'text-black' },
      { bg: 'bg-violet-500', text: 'text-white' },
      { bg: 'bg-lime-500', text: 'text-black' },
    ];
    
    // Simple hash to pick a color consistently
    let hash = 0;
    const safeCategory = category || 'general';
    for (let i = 0; i < safeCategory.length; i++) {
      hash = ((hash << 5) - hash) + safeCategory.charCodeAt(i);
      hash = hash & hash;
    }
    const colorIndex = Math.abs(hash) % colors.length;
    const color = colors[colorIndex];
    
    // Format the label (capitalize first letter of each word)
    const label = safeCategory
      .split(/[-_]/)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
    
    return { ...color, label };
  };
  
  // Keyword-based category detection — uses word-boundary padding to avoid false positives
  // e.g. 'know' no longer matches 'now', 'recall' no longer matches 'call'
  const detectMessageCategory = (messageContent) => {
    if (!messageContent) return 'general';
    // Pad with spaces so we can check whole-word boundaries
    const c = ' ' + messageContent.toLowerCase() + ' ';
    const has = (...words) => words.some(w => c.includes(` ${w} `) || c.includes(` ${w},`) || c.includes(` ${w}.`) || c.includes(` ${w}!`) || c.includes(` ${w}?`) || c.includes(` ${w}:`));
    const phrase = (p) => c.includes(p);

    // Urgent — specific high-priority signals only
    if (has('urgent', 'asap', 'immediately', 'emergency', 'critical') ||
        phrase('time sensitive') || phrase('right away') || phrase('as soon as possible') ||
        phrase('need this now') || phrase('need it now')) return 'urgent';

    // Meeting — specific scheduling/call terms
    if (has('meeting', 'appointment', 'zoom', 'conference') ||
        phrase('schedule a') || phrase('calendar invite') || phrase('hop on') ||
        phrase('video call') || phrase('phone call') || phrase('on a call') ||
        phrase('set up a call') || phrase('book a time') || phrase('google meet') ||
        phrase('microsoft teams')) return 'meeting';

    // Travel
    if (has('flight', 'airline', 'airport', 'hotel', 'layover', 'passport') ||
        phrase('boarding pass') || phrase('check-in') || phrase('check in') ||
        phrase('booking confirmation') || phrase('travel plans') || phrase('road trip') ||
        phrase('flying out') || phrase('driving to')) return 'travel';

    // Follow-up — specific follow-up phrases
    if (phrase('follow up') || phrase('follow-up') || phrase('following up') ||
        phrase('checking in') || phrase('circling back') || phrase('touch base') ||
        phrase('any updates') || phrase('any update') || phrase('circle back') ||
        phrase('just wanted to check') || phrase('still waiting') || phrase('heard back')) return 'follow-up';

    // Client / Real Estate business terms
    if (has('client', 'buyer', 'seller', 'property', 'listing', 'offer', 'escrow',
             'contract', 'mortgage', 'commission', 'investor', 'prospect', 'closing') ||
        phrase('open house') || phrase('real estate') || phrase('closing date') ||
        phrase('due diligence') || phrase('purchase price') || phrase('down payment') ||
        phrase('multiple offers') || phrase('showing request') || phrase('showing today')) return 'client';

    // Report / Analytics
    if (has('report', 'analytics', 'metrics', 'quarterly', 'monthly', 'revenue') ||
        phrase('numbers are') || phrase('performance review') || phrase('year to date') ||
        phrase('month to date') || phrase('conversion rate') || phrase('click rate')) return 'report';

    return 'general';
  };
  
  // Get the ACCOUNT category (personal/business) for a conversation
  // EVERY conversation MUST have either 'personal' or 'business' badge
  // Based on which terminal account it was synced from
  const getAccountCategory = (conv) => {
    // Check explicit category from Mac sync or contact edit
    if (conv.category) {
      const cat = conv.category.toLowerCase();
      if (cat === 'personal' || cat === 'business') {
        return cat;
      }
    }
    
    // Check phoneType for Mac-synced conversations (camelCase)
    if (conv.phoneType) {
      const pt = conv.phoneType.toLowerCase();
      if (pt === 'personal' || pt === 'business') {
        return pt;
      }
    }
    
    // Check phone_type for Mac-synced conversations (snake_case from backend)
    if (conv.phone_type) {
      const pt = conv.phone_type.toLowerCase();
      if (pt === 'personal' || pt === 'business') {
        return pt;
      }
    }
    
    // Check source - if it's mac_sync, default to personal
    if (conv.source === 'mac_sync') {
      return 'personal';
    }
    
    // Default ALL conversations to 'personal' if no category specified
    return 'personal';
  };
  
  // Get ALL phone types for a conversation (for merged conversations with both Personal and Business)
  const getAccountCategories = (conv) => {
    // Check if backend returned phone_types array (merged conversation)
    const phoneTypes = conv.phone_types;
    if (phoneTypes && Array.isArray(phoneTypes) && phoneTypes.length > 0) {
      const filtered = phoneTypes
        .filter(pt => pt && typeof pt === 'string')
        .map(pt => pt.toLowerCase())
        .filter(pt => pt === 'personal' || pt === 'business');
      // If filtering removes everything, fall back to single category
      if (filtered.length > 0) {
        return filtered;
      }
    }
    
    // Fall back to single category
    const singleCategory = getAccountCategory(conv);
    return [singleCategory || 'personal'];
  };
  
  // Get the badge category for a conversation - ALWAYS returns 'personal' or 'business'
  // No more content-based detection (Meeting, Urgent, etc.) for the main badge
  const getLastCategoryInThread = (conv) => {
    // ALWAYS return either 'personal' or 'business' - no exceptions
    return getAccountCategory(conv);
  };
  
  // Add Contact Modal State
  const [showAddContactModal, setShowAddContactModal] = useState(false);
  const [newContactName, setNewContactName] = useState('');
  const [newContactEmail, setNewContactEmail] = useState('');
  const [newContactPhone, setNewContactPhone] = useState('');
  const [newContactCompany, setNewContactCompany] = useState('');
  const [newContactRole, setNewContactRole] = useState('');
  const [newContactCategory, setNewContactCategory] = useState('Personal');
  const [newContactNotes, setNewContactNotes] = useState('');
  const [contactAutoScanning, setContactAutoScanning] = useState(false);
  const [originalConversationName, setOriginalConversationName] = useState(''); // Track original name for updates

  // Add to Flight modal state
  const [showAddFlightModal, setShowAddFlightModal] = useState(false);
  const [flightForm, setFlightForm] = useState({ airline: '', flight_number: '', departure: '', arrival: '', date: '', time: '', notes: '' });

  // Add to Finances modal state
  const [showAddFinanceModal, setShowAddFinanceModal] = useState(false);
  const [financeForm, setFinanceForm] = useState({ description: '', amount: '', category: 'Other', type: 'expense', date: '' });

  // Compose email controls state
  const [composeAttachments, setComposeAttachments] = useState([]);
  const [generatingComposeDraft, setGeneratingComposeDraft] = useState(false);

  // Contact list for dropdown - will be populated from iCloud
  const [iCloudContacts, setICloudContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  
  // Fallback hardcoded contacts (used if iCloud fetch fails)
  const fallbackContacts = [
    { name: 'Sarah Mitchell', email: 'sarah.mitchell@realestate.com', phone: '+1 (555) 123-4567', type: 'business' },
    { name: 'John Martinez', email: 'john.martinez@realestate.com', phone: '+1 (555) 234-5678', type: 'business' },
    { name: 'Emily Chen', email: 'emily.chen@realestate.com', phone: '+1 (555) 345-6789', type: 'business' },
    { name: 'Alex Rivera', email: 'alex.rivera@realestate.com', phone: '+1 (555) 456-7890', type: 'business' },
  ];
  
  // Fetch iCloud contacts when compose modal opens
  const fetchICloudContacts = async () => {
    try {
      setLoadingContacts(true);
      console.log('📱 Fetching iCloud contacts...');
      const response = await apiCall('/contacts/icloud/all');
      if (response.contacts && response.contacts.length > 0) {
        setICloudContacts(response.contacts);
        console.log(`✅ Loaded ${response.contacts.length} iCloud contacts`);
      } else {
        console.log('⚠️ No iCloud contacts found, using fallback');
        setICloudContacts(fallbackContacts);
      }
    } catch (error) {
      console.error('Error fetching iCloud contacts:', error);
      setICloudContacts(fallbackContacts);
    } finally {
      setLoadingContacts(false);
    }
  };
  
  // Combined contacts list (iCloud + fallback)
  const contactsList = iCloudContacts.length > 0 ? iCloudContacts : fallbackContacts;

  // Pre-fetch iCloud contacts when Compose modal opens
  useEffect(() => {
    if (showComposeModal && iCloudContacts.length === 0 && !loadingContacts) {
      console.log('📱 Pre-fetching iCloud contacts for Compose modal...');
      fetchICloudContacts();
    }
  }, [showComposeModal]);
  
  // Auto-filter contacts when they're loaded and user has a search term
  useEffect(() => {
    if (iCloudContacts.length > 0 && composeRecipient.trim() && showComposeModal) {
      const searchValue = composeRecipient.trim().toLowerCase();
      const filtered = iCloudContacts.filter(contact => {
        const nameMatch = contact.name?.toLowerCase().includes(searchValue);
        const phoneMatch = contact.phone?.includes(composeRecipient.trim());
        return nameMatch || phoneMatch;
      });
      console.log(`🔍 Auto-filtering after contacts loaded: "${composeRecipient}" - Found ${filtered.length} matches from ${iCloudContacts.length} iCloud contacts`);
      setFilteredContacts(filtered);
      setShowContactSuggestions(true);
    }
  }, [iCloudContacts.length, composeRecipient, showComposeModal]);

  // Swipe-to-delete handlers
  const handleTouchStart = (e, id) => {
    const touch = e.touches[0];
    setSwipeOffset({ ...swipeOffset, [id]: { startX: touch.clientX, currentX: 0 } });
  };

  const handleTouchMove = (e, id) => {
    const touch = e.touches[0];
    const startX = swipeOffset[id]?.startX || touch.clientX;
    const diff = touch.clientX - startX;
    // Only allow left swipe (negative diff)
    if (diff < 0) {
      setSwipeOffset({ ...swipeOffset, [id]: { startX, currentX: diff } });
    }
  };

  const handleTouchEnd = (id) => {
    const offset = swipeOffset[id]?.currentX || 0;
    // If swiped more than 100px, delete
    if (offset < -100) {
      handleDeleteConversation(id);
    }
    // Reset
    setSwipeOffset({ ...swipeOffset, [id]: { startX: 0, currentX: 0 } });
  };

  const handleDeleteConversation = async (phoneNumber) => {
    if (!phoneNumber) {
      console.error('No phone number provided for deletion');
      alert('Unable to delete: No phone number found for this conversation');
      return;
    }
    
    if (!window.confirm(`Delete entire conversation with ${phoneNumber}? This cannot be undone.`)) {
      return;
    }
    
    try {
      const response = await apiCall(`/messages/conversation/${encodeURIComponent(phoneNumber)}`, {
        method: 'DELETE'
      });
      
      console.log('✅ Conversation deleted:', response);
      
      // Remove from conversations list by phone number
      setConversations(prev => prev.filter(c => c.phone !== phoneNumber));
      
      // Clear selected conversation if it was the deleted one
      if (selectedConversation?.phone === phoneNumber) {
        setSelectedConversation(null);
      }
      
      // Show success toast
      toast.success(`Conversation with ${phoneNumber} deleted permanently`);
      
      // Refresh conversations to ensure consistency
      const conversationsData = await apiCall('/conversations');
      if (conversationsData && Array.isArray(conversationsData)) {
        const sortedConversations = conversationsData.sort((a, b) => {
          const dateA = new Date(a.updated_at || a.timestamp || 0);
          const dateB = new Date(b.updated_at || b.timestamp || 0);
          return dateB - dateA;
        });
        setConversations(prev => {
          const prevById = {};
          prev.forEach(c => { prevById[c.id] = c; if (c.phone) prevById[c.phone] = c; });
          return sortedConversations.map(nc => ({
            ...nc,
            messages: nc.messages?.length ? nc.messages : (prevById[nc.id] || prevById[nc.phone])?.messages || []
          }));
        });
      }
      
    } catch (error) {
      console.error('Error deleting conversation:', error);
      toast.error('Failed to delete conversation: ' + error.message);
    }
  };

  const handleDeleteEmail = async (emailId) => {
    // Confirm deletion
    if (!window.confirm('Are you sure you want to delete this email? It will be moved to trash.')) {
      return;
    }
    
    try {
      // Only call Gmail API if it's a Gmail email
      if (activeEmailAccount === 'gmail') {
        const accountId = getCurrentGmailAccountId();
        const response = await fetch(`${window.location.origin}/api/gmail/messages/${emailId}?account_id=${accountId}`, {
          method: 'DELETE'
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || 'Failed to delete email');
        }
        
        // Remove from local state and cache
        setGmailEmails(prev => prev.filter(e => e.id !== emailId));
        setGmailEmailsCache(prev => {
          const newCache = { ...prev };
          Object.keys(newCache).forEach(folder => {
            newCache[folder] = newCache[folder].filter(e => e.id !== emailId);
          });
          return newCache;
        });
        
        // Clear selected email if it was the deleted one
        if (selectedEmail?.id === emailId) {
          setSelectedEmail(null);
        }
        
        alert('Email moved to trash successfully');
      } else {
        // For other email accounts, just remove from local state
        console.log('Deleting email:', emailId);
        setSelectedEmail(null);
      }
    } catch (error) {
      console.error('Error deleting email:', error);
      alert(`Failed to delete email: ${error.message}`);
    }
  };

  const handleReplyEmail = (email) => {
    // Set up compose modal for reply
    const replyTo = email.email || email.from;
    const replySubject = email.subject?.startsWith('Re:') ? email.subject : `Re: ${email.subject}`;
    const originalBody = email.body || email.preview || '';
    const replyBody = `\n\n\n--- Original Message ---\nFrom: ${email.from}\nDate: ${email.time}\nSubject: ${email.subject}\n\n${originalBody}`;
    
    setEmailTo(replyTo);
    setEmailSubject(replySubject);
    setEmailBody(replyBody);
    setEmailCc('');
    setEmailBcc('');
    setShowEmailComposeModal(true);
    setOpenEmailDropdown(null);
  };

  const handleDeleteMessage = async (convId, msgIdx) => {
    const message = selectedConversation?.messages?.[msgIdx];
    if (!message) {
      console.error('Message not found');
      return;
    }
    
    // Confirm deletion
    if (!window.confirm('Are you sure you want to delete this message?')) {
      setOpenMessageDropdown(null);
      return;
    }
    
    try {
      // Delete from backend if message has an ID
      if (message.id) {
        await apiCall(`/messages/${message.id}`, { method: 'DELETE' });
        console.log('✅ Message deleted from database:', message.id);
      }
      
      // Update local state - remove message from conversation
      setConversations(prevConvs => 
        prevConvs.map(conv => {
          if (conv.id === convId) {
            const updatedMessages = conv.messages.filter((_, idx) => idx !== msgIdx);
            return { ...conv, messages: updatedMessages };
          }
          return conv;
        })
      );
      
      // Update selected conversation
      if (selectedConversation && selectedConversation.id === convId) {
        setSelectedConversation(prev => ({
          ...prev,
          messages: prev.messages.filter((_, idx) => idx !== msgIdx)
        }));
      }
      
      // Also mark as deleted in UI state (for safety)
      const key = `${convId}-${msgIdx}`;
      setDeletedMessages({
        ...deletedMessages,
        [key]: true
      });
      
      console.log('✅ Message removed from UI');
    } catch (error) {
      console.error('Error deleting message:', error);
      alert('Failed to delete message: ' + error.message);
    }
    
    setOpenMessageDropdown(null);
  };

  // Emoji reaction handlers
  const handleAddReaction = (convId, msgIdx, emoji) => {
    const key = `${convId}-${msgIdx}`;
    setMessageReactions({
      ...messageReactions,
      [key]: emoji
    });
    setShowEmojiPicker(null);
  };

  const handleToggleFlag = (emailId) => {
    setFlaggedEmails({
      ...flaggedEmails,
      [emailId]: !flaggedEmails[emailId]
    });
  };

  const handleTogglePin = (convId) => {
    setPinnedConversations({
      ...pinnedConversations,
      [convId]: !pinnedConversations[convId]
    });
  };

  // Fetch existing meetings from the Meetings page Kanban
  const fetchExistingMeetings = async () => {
    try {
      const response = await apiCall('/excel/get-desktop-data');
      if (response && response.meetings && Array.isArray(response.meetings)) {
        setExistingMeetings(response.meetings);
      } else {
        setExistingMeetings([]);
      }
    } catch (error) {
      console.error('Error fetching meetings:', error);
      setExistingMeetings([]);
    }
  };

  // Handle opening "Add to Meeting" modal
  const handleAddToMeeting = async (conv) => {
    setMeetingConversation(conv);
    await fetchExistingMeetings();
    setShowAddToMeetingModal(true);
    setOpenMessageDropdown(null);
  };

  // Build a `conv`-shaped object from the currently-selected dashboard user
  // so the existing meeting/contact handlers can be reused for in-app DMs.
  const dashboardUserAsConv = () => {
    if (!selectedDashboardUser) return null;
    return {
      id: selectedDashboardUser.id || selectedDashboardUser.user_id,
      name: selectedDashboardUser.name || selectedDashboardUser.user_name || 'Contact',
      phone: '',
      email: selectedDashboardUser.email || selectedDashboardUser.user_email || '',
    };
  };

  // Delete a single dashboard message both server-side and in local state.
  const deleteDashboardMessage = async (messageId) => {
    if (!messageId || !userId) return;
    setOpenMessageDropdown(null);
    try {
      await fetch(
        `${window.location.origin}/api/dashboard-messages/message/${messageId}?user_id=${userId}`,
        { method: 'DELETE' },
      );
      setDashboardMessages(prev => prev.filter(m => m.id !== messageId));
      toast.success('Message deleted');
    } catch (e) {
      console.error(e);
      toast.error('Could not delete message');
    }
  };

  // Open Add Contact modal pre-filled from a dashboard contact.
  const openAddContactFromDashboardUser = ({ clear } = { clear: false }) => {
    if (!selectedDashboardUser) return;
    const name = selectedDashboardUser.name || selectedDashboardUser.user_name || '';
    const email = selectedDashboardUser.email || selectedDashboardUser.user_email || '';
    setNewContactName(clear ? '' : name);
    setNewContactPhone('');
    setNewContactEmail(clear ? '' : email);
    setNewContactCompany('');
    setNewContactRole('');
    setNewContactCategory('Business');
    setNewContactNotes(clear ? '' : `Added from dashboard chat with ${name}`);
    setOriginalConversationName(name);
    setShowAddContactModal(true);
    setOpenMessageDropdown(null);
  };

  // Open Add Contact modal pre-filled from a Twilio / iPhone message conversation.
  const openAddContactFromConversation = ({ clear } = { clear: false }) => {
    if (!selectedConversation) return;
    const name = selectedConversation.name || '';
    const phone = selectedConversation.phone || '';
    const email = selectedConversation.email || '';
    setNewContactName(clear ? '' : name);
    setNewContactPhone(clear ? '' : phone);
    setNewContactEmail(clear ? '' : email);
    setNewContactCompany('');
    setNewContactRole('');
    setNewContactCategory('Business');
    setNewContactNotes(clear ? '' : `Added from message thread with ${name}`);
    setOriginalConversationName(name);
    setShowAddContactModal(true);
    setOpenMessageDropdown(null);
  };

  // Handle adding conversation to existing meeting on Meetings page
  const handleAddToExistingMeeting = async () => {
    if (!selectedMeetingId || !meetingConversation) return;
    
    try {
      // Find the selected meeting
      const selectedMeeting = existingMeetings.find(m => m.id === selectedMeetingId);
      
      // Build notes with attachment info if present
      let notesAddition = `Added from ${meetingConversation.emailSubject ? 'Email' : 'Messages'}: ${meetingConversation.name} (${meetingConversation.phone})`;
      if (meetingConversation.emailSubject) {
        notesAddition += `\nSubject: ${meetingConversation.emailSubject}`;
      }
      if (meetingConversation.attachments && meetingConversation.attachments.length > 0) {
        notesAddition += `\nAttachments: ${meetingConversation.attachments.map(a => a.name || a.filename).join(', ')}`;
      }
      
      // Update the meeting with contact info
      const updateData = {
        contact_name: meetingConversation.name,
        contact_phone: meetingConversation.phone,
        notes: selectedMeeting?.notes 
          ? `${selectedMeeting.notes}\n\n${notesAddition}`
          : `Contact ${notesAddition}`
      };
      
      await apiCall(`/excel/update-meeting/${selectedMeetingId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updateData)
      });
      
      // Upload attachments to the meeting if present
      if (meetingConversation.attachments && meetingConversation.attachments.length > 0) {
        console.log('📎 Uploading', meetingConversation.attachments.length, 'attachments to existing meeting');
        
        for (const att of meetingConversation.attachments) {
          try {
            // Fetch attachment data
            let attachmentData = null;
            let filename = att.name || att.filename || 'attachment';
            let mimeType = att.mimeType || 'application/octet-stream';
            
            if (activeEmailAccount === 'gmail' && att.id) {
              const accountId = getCurrentGmailAccountId();
              const response = await fetch(`${window.location.origin}/api/gmail/messages/${meetingConversation.messageId}/attachments/${att.id}?account_id=${accountId}`);
              if (response.ok) {
                const attData = await response.json();
                attachmentData = attData.data;
              }
            } else if (att.id) {
              const { outlook2EmailAPI } = await import('../services/api');
              const attData = await outlook2EmailAPI.getAttachment(meetingConversation.messageId, att.id);
              attachmentData = attData.contentBytes;
              mimeType = attData.contentType || mimeType;
            }
            
            if (attachmentData) {
              // Convert base64 to blob
              const byteCharacters = atob(attachmentData);
              const byteNumbers = new Array(byteCharacters.length);
              for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
              }
              const byteArray = new Uint8Array(byteNumbers);
              const blob = new Blob([byteArray], { type: mimeType });
              
              // Upload to meeting
              const formData = new FormData();
              formData.append('file', blob, filename);
              
              await fetch(`${window.location.origin}/api/meetings/${selectedMeetingId}/documents`, {
                method: 'POST',
                body: formData
              });
              console.log(`✅ Uploaded "${filename}" to meeting`);
            }
          } catch (attErr) {
            console.error('Error uploading attachment to meeting:', attErr);
          }
        }
      }
      
      const attachmentCount = meetingConversation.attachments?.length || 0;
      const toastMsg = attachmentCount > 0 
        ? `Added ${meetingConversation.name} to meeting with ${attachmentCount} attachment${attachmentCount > 1 ? 's' : ''}!`
        : `Added ${meetingConversation.name} to meeting!`;
      setShowAddContactToast(toastMsg);
      setTimeout(() => setShowAddContactToast(null), 3000);
      setShowAddToMeetingModal(false);
      setSelectedMeetingId('');
      setMeetingConversation(null);
    } catch (error) {
      console.error('Error adding to meeting:', error);
      alert('Failed to add to meeting: ' + error.message);
    }
  };

  // Handle opening "New Meeting" modal
  const handleNewMeeting = (conv) => {
    setMeetingConversation(conv);
    setNewMeetingTitle(`Meeting with ${conv.name}`);
    setNewMeetingNotes(`Contact: ${conv.name}\nPhone: ${conv.phone}`);
    setNewMeetingDate(new Date().toISOString().split('T')[0]);
    setShowNewMeetingModal(true);
    setOpenMessageDropdown(null);
  };

  // Handle creating new meeting - adds to the Meetings page Kanban board
  const handleCreateNewMeeting = async () => {
    if (!newMeetingTitle || !meetingConversation) return;
    
    try {
      const meetingData = {
        title: newMeetingTitle,
        name: newMeetingTitle,
        date: newMeetingDate || new Date().toISOString().split('T')[0],
        time: '10:00',
        notes: newMeetingNotes,
        contact_name: meetingConversation.name,
        contact_phone: meetingConversation.phone,
        phone: meetingConversation.phone,
        pipeline_stage: 'tbd', // Maps to "TBD" column on the Kanban board
        user_id: userId // Add user_id for personal meetings
      };
      
      // Save to personal meetings (this shows in the default Meetings view)
      let meetingId = null;
      try {
        // Pass user_id as BOTH query param (required by backend) AND body field
        const personalResponse = await apiCall(`/meetings/?user_id=${encodeURIComponent(userId)}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...meetingData,
            user_id: userId
          })
        });
        meetingId = personalResponse?.id;
        console.log('✅ Meeting saved to personal meetings:', meetingId);
      } catch (e) {
        console.warn('Could not save to personal meetings:', e);
      }
      
      // Also save to Excel meetings collection for Kanban view
      try {
        const meetingResponse = await apiCall('/excel/add-meeting', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(meetingData)
        });
        if (!meetingId && meetingResponse?.meeting?.id) {
          meetingId = meetingResponse.meeting.id;
        }
        console.log('✅ Meeting saved to excel_meetings');
      } catch (e) {
        console.warn('Could not save to excel meetings:', e);
      }
      
      // If there are attachments from email, upload them to the meeting
      if (meetingConversation.attachments && meetingConversation.attachments.length > 0 && meetingId) {
        console.log('📎 Uploading', meetingConversation.attachments.length, 'attachments to meeting');
        
        for (const att of meetingConversation.attachments) {
          try {
            // Fetch attachment data
            let attachmentData = null;
            let filename = att.name || att.filename || 'attachment';
            let mimeType = att.mimeType || 'application/octet-stream';
            
            if (activeEmailAccount === 'gmail' && att.id) {
              const accountId = getCurrentGmailAccountId();
              const response = await fetch(`${window.location.origin}/api/gmail/messages/${meetingConversation.messageId}/attachments/${att.id}?account_id=${accountId}`);
              if (response.ok) {
                const attData = await response.json();
                attachmentData = attData.data;
              }
            } else if (att.id) {
              const { outlook2EmailAPI } = await import('../services/api');
              const attData = await outlook2EmailAPI.getAttachment(meetingConversation.messageId, att.id);
              attachmentData = attData.contentBytes;
              mimeType = attData.contentType || mimeType;
            }
            
            if (attachmentData) {
              // Convert base64 to blob
              const byteCharacters = atob(attachmentData);
              const byteNumbers = new Array(byteCharacters.length);
              for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
              }
              const byteArray = new Uint8Array(byteNumbers);
              const blob = new Blob([byteArray], { type: mimeType });
              
              // Upload to meeting
              const formData = new FormData();
              formData.append('file', blob, filename);
              
              await fetch(`${window.location.origin}/api/meetings/${meetingResponse.id}/documents`, {
                method: 'POST',
                body: formData
              });
              console.log(`✅ Uploaded "${filename}" to meeting`);
            }
          } catch (attErr) {
            console.error('Error uploading attachment to meeting:', attErr);
          }
        }
      }
      
      const attachmentCount = meetingConversation.attachments?.length || 0;
      const toastMsg = attachmentCount > 0 
        ? `Meeting "${newMeetingTitle}" added with ${attachmentCount} attachment${attachmentCount > 1 ? 's' : ''}!`
        : `Meeting "${newMeetingTitle}" added to Meetings page!`;
      setShowAddContactToast(toastMsg);
      setTimeout(() => setShowAddContactToast(null), 3000);
      setShowNewMeetingModal(false);
      setNewMeetingTitle('');
      setNewMeetingDate('');
      setNewMeetingNotes('');
      setMeetingConversation(null);
    } catch (error) {
      console.error('Error creating meeting:', error);
      alert('Failed to create meeting: ' + error.message);
    }
  };

  // Fetch calls separately for refreshing after updates
  const fetchCalls = async () => {
    // Use demo calls if in demo mode
    if (isDemoMode && demoData?.calls) {
      const demoCalls = demoData.calls.map((call, idx) => ({
        id: call.id || `demo-call-${idx}`,
        call_sid: call.id || `demo-call-${idx}`,
        from: call.direction === 'outbound' ? 'You' : call.contact,
        to: call.direction === 'outbound' ? call.contact : 'You',
        from_number: call.direction === 'outbound' ? '+15551234567' : call.phone,
        to_number: call.direction === 'outbound' ? call.phone : '+15551234567',
        contact_name: call.contact || 'Unknown',
        phone: call.phone,
        direction: call.direction || 'inbound',
        duration: call.duration || '0:00',
        duration_seconds: call.duration ? parseInt(call.duration.split(':')[0]) * 60 + parseInt(call.duration.split(':')[1] || 0) : 0,
        status: call.status || 'completed',
        timestamp: call.time || new Date().toISOString(),
        created_at: call.time || new Date().toISOString(),
        notes: call.notes || '',
        source: 'demo'
      }));
      setCalls(demoCalls);
      console.log(`📞 Demo mode: Loaded ${demoCalls.length} demo calls`);
      return;
    }
    
    try {
      const callsData = await apiCall('/calls');
      // Ensure callsData is an array
      setCalls(Array.isArray(callsData) ? callsData : []);
    } catch (err) {
      console.log('Error fetching calls:', err);
      setCalls([]);
    }
  };

  // Delete a call log
  const deleteCall = async (callSid) => {
    try {
      await apiCall(`/calls/${callSid}`, { method: 'DELETE' });
      // Remove from local state
      setCalls(prevCalls => prevCalls.filter(call => call.call_sid !== callSid));
      setActiveCallDropdown(null);
    } catch (err) {
      console.error('Error deleting call:', err);
      alert('Failed to delete call: ' + (err.message || 'Unknown error'));
    }
  };

  // Sync Outlook emails manually (refresh button)
  const syncOutlookEmails = async () => {
    setOutlookLoading(true);
    try {
      const { outlook2AuthAPI, outlook2EmailAPI } = await import('../services/api');
      const authStatus = await outlook2AuthAPI.status();
      
      if (authStatus.authenticated) {
        setOutlookConnected(true);
        
        try {
          // Fetch emails from current active folder
          const emailResult = await outlook2EmailAPI.syncEmails(activeFolder, 50);
          if (emailResult.emails) {
            // Transform to match expected format - use basic categorization initially
            const transformedEmails = emailResult.emails.map((email, idx) => {
              const fromEmail = email.sender || email.from || 'unknown@email.com';
              const fromName = email.sender_name || fromEmail.split('@')[0].replace(/[._-]/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Unknown';
              
              // Filter out inline attachments (keep only downloadable ones)
              const downloadableAttachments = (email.attachments || []).filter(att => !att.isInline);
              
              return {
                id: email.id || `outlook-${idx}`,
                from: fromName,
                email: fromEmail,
                subject: email.subject || 'No Subject',
                preview: email.body_preview || '',
                time: formatEmailTime(email.received_datetime || email.received_time),
                category: categorizeEmail(email), // Initial basic categorization
                folder: activeFolder,
                isRead: email.is_read ?? true,
                hasAttachments: downloadableAttachments.length > 0 || email.has_attachments,
                attachments: downloadableAttachments,
                body: email.body || email.body_preview || '',
                bodyType: email.body_type || 'html',
                to: email.to || [],
                toNames: email.to_names || [],
                cc: email.cc || [],
                ccNames: email.cc_names || [],
                conversationId: email.conversationId,
                source: 'outlook2',
                thread: [{
                  id: email.id,
                  from: fromName,
                  email: fromEmail,
                  time: formatEmailTime(email.received_datetime || email.received_time),
                  body: email.body || email.body_preview || '',
                  bodyType: email.body_type || 'html',
                  to: email.to || [],
                  cc: email.cc || [],
                  attachments: downloadableAttachments
                }]
              };
            });
            setOutlookEmails(transformedEmails);
            // Cache the emails for this folder
            setOutlookEmailsCache(prev => ({ ...prev, [activeFolder]: transformedEmails }));
            setHasInitiallyFetched(true);
            console.log(`📧 Synced ${transformedEmails.length} Outlook emails from ${activeFolder}`);
            
            // AI Categorization - run in background to improve categorization
            try {
              const emailsForAI = emailResult.emails.slice(0, 20).map(email => ({
                email_id: email.id,
                subject: email.subject || '',
                sender: email.sender || email.from || '',
                sender_name: email.sender_name || '',
                body_preview: email.body_preview || ''
              }));
              
              console.log('🤖 Starting AI bulk categorization for', emailsForAI.length, 'emails...');
              const aiCategories = await aiEmailAPI.categorizeBulk(emailsForAI);
              
              if (aiCategories.categories && aiCategories.categories.length > 0) {
                // Update emails with AI categories
                setOutlookEmails(prev => {
                  const categoryMap = {};
                  aiCategories.categories.forEach(cat => {
                    categoryMap[cat.email_id] = cat.category;
                  });
                  
                  const updated = prev.map(email => {
                    if (categoryMap[email.id]) {
                      return { ...email, category: categoryMap[email.id] };
                    }
                    return email;
                  });
                  // Also update cache
                  setOutlookEmailsCache(cache => ({ ...cache, [activeFolder]: updated }));
                  return updated;
                });
                console.log('✅ AI categorization complete -', aiCategories.count, 'emails categorized');
              }
            } catch (aiErr) {
              console.warn('AI categorization failed, using basic categorization:', aiErr);
            }
          } else if (emailResult.error || emailResult.detail) {
            // API returned an error - likely token expired
            throw new Error(emailResult.detail || emailResult.error || 'Failed to fetch emails');
          }
        } catch (syncErr) {
          console.error('Email sync failed:', syncErr);
          // Token likely expired - disconnect and prompt re-auth
          setOutlookConnected(false);
          // Disconnect and re-auth
          try {
            await outlook2AuthAPI.disconnect();
          } catch (e) { /* ignore */ }
          
          // Open OAuth flow
          const authData = await outlook2AuthAPI.getAuthUrl();
          if (authData.authorization_url) {
            alert('Your Outlook session has expired. Please sign in again.');
            window.open(authData.authorization_url, '_blank', 'width=600,height=700');
          }
        }
      } else {
        // Not authenticated - initiate OAuth flow
        setOutlookConnected(false);
        console.log('Outlook not authenticated - redirecting to connect...');
        
        // Get authorization URL and redirect
        try {
          const authData = await outlook2AuthAPI.getAuthUrl();
          if (authData.authorization_url) {
            window.open(authData.authorization_url, '_blank', 'width=600,height=700');
          }
        } catch (authErr) {
          console.error('Failed to get auth URL:', authErr);
          alert('Please reconnect your Outlook account. Click the sync button again after connecting.');
        }
      }
    } catch (err) {
      console.error('Error syncing Outlook emails:', err);
      setOutlookConnected(false);
    } finally {
      setOutlookLoading(false);
    }
  };

  // Handle selecting an email - fetch thread if available
  const handleSelectEmail = async (email) => {
    console.log('📧 Selected email FULL:', JSON.stringify({
      id: email.id,
      from: email.from,
      source: email.source, 
      conversationId: email.conversationId,
      attachmentsCount: email.attachments?.length,
      threadAttachmentsCount: email.thread?.[0]?.attachments?.length,
      hasSource: 'source' in email
    }));
    
    // Set selected email immediately to show detail view
    setSelectedEmail({
      ...email,
      thread: email.thread || [{
        id: email.id,
        from: email.from,
        email: email.email,
        time: email.time,
        date: email.date || email.rawDate, // Ensure raw date is passed
        rawDate: email.rawDate || email.date,
        body: email.body,
        bodyType: email.bodyType || 'html',
        to: email.to || [],
        cc: email.cc || [],
        attachments: email.attachments || [],
        hasAttachments: email.hasAttachments
      }]
    });
    
    // For Gmail emails, fetch full message details
    if (email.source === 'gmail' && email.id) {
      try {
        console.log('📧 Fetching full Gmail message for id:', email.id);
        const accountId = getCurrentGmailAccountId();
        const response = await fetch(`${window.location.origin}/api/gmail/messages/${email.id}?account_id=${accountId}`);
        
        if (response.ok) {
          const fullMessage = await response.json();
          console.log('📧 Full Gmail message received, body length:', fullMessage.body?.length);
          
          // Parse from field
          const parseFromField = (fromStr) => {
            if (!fromStr) return { name: 'Unknown', email: '' };
            const match = fromStr.match(/^(.+?)\s*<(.+?)>$/);
            if (match) {
              return { name: match[1].trim(), email: match[2].trim() };
            }
            if (fromStr.includes('@')) {
              return { name: fromStr.split('@')[0], email: fromStr };
            }
            return { name: fromStr, email: '' };
          };
          
          const fromParsed = parseFromField(fullMessage.from);
          
          // Transform Gmail attachments to match expected format
          const transformedAttachments = (fullMessage.attachments || []).map(att => ({
            id: att.id,
            name: att.filename || att.name || 'Attachment',
            filename: att.filename || att.name || 'Attachment',
            mimeType: att.mimeType,
            size: att.size,
            partId: att.partId,
            isInline: false
          }));
          
          console.log('📎 Gmail attachments found:', transformedAttachments.length, transformedAttachments);
          
          // Update with full message body and attachments
          setSelectedEmail(prev => ({
            ...prev,
            body: fullMessage.body || prev.body,
            hasAttachments: fullMessage.has_attachments || transformedAttachments.length > 0,
            attachments: transformedAttachments,
            thread: [{
              id: fullMessage.id,
              from: fromParsed.name,
              email: fromParsed.email,
              time: prev.time,
              date: fullMessage.internalDate ? new Date(parseInt(fullMessage.internalDate)).toISOString() : (prev.date || email.date),
              rawDate: fullMessage.internalDate ? new Date(parseInt(fullMessage.internalDate)).toISOString() : (prev.rawDate || email.rawDate),
              body: fullMessage.body || prev.body,
              bodyType: fullMessage.body?.startsWith('<!') || fullMessage.body?.includes('<html') ? 'html' : 'text',
              to: fullMessage.to ? [fullMessage.to] : [],
              cc: [],
              attachments: transformedAttachments,
              hasAttachments: fullMessage.has_attachments || transformedAttachments.length > 0
            }]
          }));
        }
      } catch (err) {
        console.error('Error fetching Gmail message details:', err);
      }
    }
    
    // For Outlook emails with conversationId, fetch the full thread in background
    if (email.conversationId && email.source === 'outlook2') {
      try {
        console.log('📧 Fetching thread for conversationId:', email.conversationId);
        const { outlook2EmailAPI } = await import('../services/api');
        const threadData = await outlook2EmailAPI.getThread(email.conversationId);
        console.log('📧 Thread data received:', threadData);
        
        if (threadData.messages && threadData.messages.length > 0) {
          // Transform thread messages
          const threadMessages = threadData.messages.map(msg => ({
            id: msg.id,
            from: msg.sender_name || msg.from,
            email: msg.from,
            time: formatEmailTime(msg.received_time),
            date: msg.received_time || msg.date,
            rawDate: msg.received_time || msg.date,
            body: msg.body,
            bodyType: msg.body_type,
            to: msg.to || [],
            cc: msg.cc || [],
            attachments: msg.attachments || [],
            hasAttachments: msg.has_attachments || (msg.attachments && msg.attachments.length > 0)
          }));
          
          // Update with full thread data
          setSelectedEmail(prev => ({
            ...prev,
            thread: threadMessages,
            conversationId: email.conversationId
          }));
        }
      } catch (err) {
        console.error('Error fetching email thread:', err);
      }
    }
    
    // For Nylas emails, fetch full message details including attachments
    if (email.source === 'nylas' && email.id) {
      try {
        console.log('📧 Fetching full Nylas message for id:', email.id);
        const accountEmail = email.email || getActiveNylasAccountEmail();
        const authToken = localStorage.getItem('auth_token');
        const response = await fetch(`${window.location.origin}/api/nylas/messages/${email.id}?email=${encodeURIComponent(accountEmail || '')}`, {
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (response.ok) {
          const fullMessage = await response.json();
          console.log('📧 Full Nylas message received, attachments:', fullMessage.attachments?.length);
          
          // Extract attachments from the full message
          const attachments = (fullMessage.attachments || []).map(att => ({
            id: att.id,
            name: att.filename || att.name || 'Attachment',
            filename: att.filename || att.name || 'Attachment',
            mimeType: att.content_type || att.mimeType,
            contentType: att.content_type || att.mimeType,
            size: att.size,
            isInline: att.is_inline || false
          }));
          
          // Update with full message body and attachments
          setSelectedEmail(prev => ({
            ...prev,
            body: fullMessage.body || prev.body,
            hasAttachments: attachments.length > 0,
            attachments: attachments,
            thread: [{
              id: email.id,
              from: prev.thread?.[0]?.from || email.from,
              email: prev.thread?.[0]?.email || email.email,
              time: prev.thread?.[0]?.time || email.time,
              date: fullMessage.date ? new Date(fullMessage.date * 1000).toISOString() : (prev.thread?.[0]?.date || email.date),
              rawDate: fullMessage.date ? new Date(fullMessage.date * 1000).toISOString() : (prev.thread?.[0]?.rawDate || email.rawDate),
              body: fullMessage.body || prev.body,
              bodyType: fullMessage.body?.startsWith('<!') || fullMessage.body?.includes('<html') ? 'html' : 'text',
              to: fullMessage.to?.map(t => t.email) || prev.to || [],
              cc: fullMessage.cc?.map(c => c.email) || prev.cc || [],
              attachments: attachments,
              hasAttachments: attachments.length > 0
            }]
          }));
        }
      } catch (err) {
        console.error('Error fetching Nylas message details:', err);
      }
    }
  };

  // Download attachment
  const handleDownloadAttachment = async (messageId, attachment) => {
    try {
      // Check if this is a Gmail attachment
      if (activeEmailAccount === 'gmail' && attachment.id) {
        // Fetch Gmail attachment
        const accountId = getCurrentGmailAccountId();
        const authToken = localStorage.getItem('auth_token');
        const response = await fetch(`${window.location.origin}/api/gmail/messages/${messageId}/attachments/${attachment.id}?account_id=${accountId}`, {
          headers: {
            'Authorization': `Bearer ${authToken}`
          }
        });
        
        if (!response.ok) {
          const errorText = await response.text();
          console.error('Gmail attachment error:', errorText);
          throw new Error('Failed to fetch Gmail attachment');
        }
        
        const attData = await response.json();
        
        if (attData.data) {
          // Create download link from base64 data
          const link = document.createElement('a');
          link.href = `data:${attachment.mimeType || 'application/octet-stream'};base64,${attData.data}`;
          link.download = attachment.filename || attachment.name || 'attachment';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          return;
        }
      } else if (activeEmailAccount === 'outlook') {
        // Outlook attachment
        const { outlook2EmailAPI } = await import('../services/api');
        const attData = await outlook2EmailAPI.getAttachment(messageId, attachment.id);
        
        if (attData.contentBytes) {
          // Create download link from base64 data
          const link = document.createElement('a');
          link.href = `data:${attData.contentType};base64,${attData.contentBytes}`;
          link.download = attData.name;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          return;
        }
      } else if (attachment.id) {
        // Nylas attachment - check source or default to Nylas for connected accounts
        const accountEmail = getActiveNylasAccountEmail();
        if (!accountEmail) {
          throw new Error('No active email account found');
        }
        
        const authToken = localStorage.getItem('auth_token');
        const response = await fetch(`${window.location.origin}/api/nylas/attachments/${attachment.id}?message_id=${messageId}&email=${encodeURIComponent(accountEmail)}`, {
          headers: {
            'Authorization': `Bearer ${authToken}`
          }
        });
        
        if (!response.ok) {
          // Clone the response before reading body to avoid "body already used" error
          const errorText = await response.clone().text();
          console.error('Nylas attachment error:', response.status, errorText);
          throw new Error(`Failed to fetch attachment (${response.status})`);
        }
        
        const attData = await response.json();
        
        if (attData.data) {
          // Create download link from base64 data
          const link = document.createElement('a');
          const mimeType = attData.content_type || attachment.mimeType || attachment.contentType || attachment.content_type || 'application/octet-stream';
          link.href = `data:${mimeType};base64,${attData.data}`;
          link.download = attachment.filename || attachment.name || 'attachment';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          console.log('✅ Attachment downloaded successfully');
          return;
        } else {
          throw new Error('No attachment data received');
        }
      } else {
        throw new Error('Unable to download attachment - missing attachment ID');
      }
    } catch (err) {
      console.error('Error downloading attachment:', err);
      alert('Failed to download attachment: ' + (err.message || 'Unknown error'));
    }
  };

  // Add attachment to Documents page
  const handleAddAttachmentToDocuments = async (messageId, attachment) => {
    try {
      // First, fetch the attachment data
      let attachmentData = null;
      let filename = attachment.name || attachment.filename || 'attachment';
      let mimeType = attachment.mimeType || 'application/octet-stream';
      
      if (activeEmailAccount === 'gmail' && attachment.id) {
        const accountId = getCurrentGmailAccountId();
        const response = await fetch(`${window.location.origin}/api/gmail/messages/${messageId}/attachments/${attachment.id}?account_id=${accountId}`);
        if (!response.ok) throw new Error('Failed to fetch attachment');
        const attData = await response.json();
        attachmentData = attData.data; // base64 encoded
      } else if (selectedEmail?.source === 'nylas' && attachment.id) {
        // Nylas attachment
        const accountEmail = getActiveNylasAccountEmail();
        const response = await fetch(`${window.location.origin}/api/nylas/attachments/${attachment.id}?message_id=${messageId}&user_id=${userId}&email=${encodeURIComponent(accountEmail)}`);
        if (!response.ok) throw new Error('Failed to fetch Nylas attachment');
        const attData = await response.json();
        attachmentData = attData.data;
        mimeType = attData.content_type || mimeType;
      } else {
        const { outlook2EmailAPI } = await import('../services/api');
        const attData = await outlook2EmailAPI.getAttachment(messageId, attachment.id);
        attachmentData = attData.contentBytes;
        mimeType = attData.contentType || mimeType;
      }
      
      if (!attachmentData) throw new Error('No attachment data');
      
      // Convert base64 to blob
      const byteCharacters = atob(attachmentData);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: mimeType });
      
      // Create FormData and upload to documents
      const formData = new FormData();
      formData.append('file', blob, filename);
      formData.append('folder', 'Email Attachments');
      formData.append('description', `Attachment from email - ${selectedEmail?.subject || 'No Subject'}`);
      
      const uploadResponse = await fetch(`${window.location.origin}/api/documents/upload`, {
        method: 'POST',
        body: formData
      });
      
      if (!uploadResponse.ok) throw new Error('Failed to upload document');
      
      setShowAddContactToast(`"${filename}" added to Documents!`);
      setTimeout(() => setShowAddContactToast(null), 3000);
    } catch (err) {
      console.error('Error adding attachment to documents:', err);
      alert('Failed to add attachment to documents: ' + err.message);
    }
  };

  // Handle Add to Flight from email message
  const handleAddToFlight = (msg) => {
    const body = msg.parsedBody || msg.body || '';
    const subject = selectedEmail?.subject || '';
    const from = msg.from || selectedEmail?.from || '';
    const today = new Date().toISOString().split('T')[0];
    setFlightForm({
      airline: from || '',
      flight_number: '',
      departure: '',
      arrival: '',
      date: today,
      time: '',
      notes: `From: ${from}\nSubject: ${subject}\n\n${body.substring(0, 300)}`
    });
    setShowAddFlightModal(true);
  };

  const handleSaveFlight = async () => {
    try {
      const res = await fetch(`${window.location.origin}/api/flights/manual`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...flightForm, source: 'email' })
      });
      if (!res.ok) throw new Error('Failed to save flight');
      setShowAddFlightModal(false);
      setShowAddContactToast('Flight added to Travel!');
      setTimeout(() => setShowAddContactToast(null), 3000);
    } catch (err) {
      console.error('Error saving flight:', err);
      alert('Failed to add flight: ' + err.message);
    }
  };

  // Handle Add to Finances from email message
  const handleAddToFinances = (msg) => {
    const body = msg.parsedBody || msg.body || '';
    const subject = selectedEmail?.subject || '';
    const from = msg.from || selectedEmail?.from || '';
    const today = new Date().toISOString().split('T')[0];
    // Try to extract an amount from the email body
    const amountMatch = body.match(/\$[\d,]+\.?\d*/);
    setFinanceForm({
      description: subject || `Payment - ${from}`,
      amount: amountMatch ? amountMatch[0].replace(/[$,]/g, '') : '',
      category: 'Other',
      type: 'expense',
      date: today
    });
    setShowAddFinanceModal(true);
  };

  const handleSaveFinance = async () => {
    try {
      const res = await fetch(`${window.location.origin}/api/finances/transactions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          description: financeForm.description,
          amount: parseFloat(financeForm.amount) || 0,
          category: financeForm.category,
          type: financeForm.type,
          date: financeForm.date
        })
      });
      if (!res.ok) throw new Error('Failed to save transaction');
      setShowAddFinanceModal(false);
      setShowAddContactToast('Transaction added to Finances!');
      setTimeout(() => setShowAddContactToast(null), 3000);
    } catch (err) {
      console.error('Error saving transaction:', err);
      alert('Failed to add transaction: ' + err.message);
    }
  };

  // Handle New Meeting with attachments from email
  const handleNewMeetingWithAttachments = (msg, attachments = []) => {
    const senderName = msg.from || selectedEmail?.from || 'Unknown';
    const senderEmail = msg.email || selectedEmail?.email || '';
    
    // Build notes with attachment info
    let notes = `Contact: ${senderName}\nEmail: ${senderEmail}`;
    if (selectedEmail?.subject) {
      notes += `\nSubject: ${selectedEmail.subject}`;
    }
    if (attachments && attachments.length > 0) {
      notes += `\n\nAttachments (${attachments.length}):`;
      attachments.forEach(att => {
        notes += `\n- ${att.name || att.filename || 'Attachment'} (${formatFileSize(att.size)})`;
      });
    }
    
    setMeetingConversation({ 
      name: senderName, 
      phone: senderEmail, 
      attachments: attachments,
      messageId: msg.id
    });
    setNewMeetingTitle(`Meeting with ${senderName}`);
    setNewMeetingNotes(notes);
    setNewMeetingDate(new Date().toISOString().split('T')[0]);
    setShowNewMeetingModal(true);
    setOpenEmailDropdown(null);
  };

  // Handle Add to Meeting with attachments from email
  const handleAddToMeetingWithAttachments = async (msg, attachments = []) => {
    const senderName = msg.from || selectedEmail?.from || 'Unknown';
    const senderEmail = msg.email || selectedEmail?.email || '';
    
    setMeetingConversation({ 
      name: senderName, 
      phone: senderEmail, 
      attachments: attachments,
      messageId: msg.id,
      emailSubject: selectedEmail?.subject
    });
    await fetchExistingMeetings();
    setShowAddToMeetingModal(true);
    setOpenEmailDropdown(null);
  };

  // Handle Add Contact from email message
  const handleAddContactFromEmail = (msg) => {
    const senderName = msg.from || selectedEmail?.from || 'Unknown';
    const senderEmail = msg.email || selectedEmail?.email || '';
    
    setNewContactName(senderName);
    setNewContactPhone('');
    setNewContactEmail(senderEmail);
    setNewContactCompany('');
    setNewContactRole('');
    setNewContactCategory('Business');
    setNewContactNotes(`Added from email: ${selectedEmail?.subject || 'No Subject'}`);
    setOriginalConversationName(senderName);
    setShowAddContactModal(true);
    setOpenEmailDropdown(null);
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  // State for email reply sending
  const [sendingEmailReply, setSendingEmailReply] = useState(false);

  // Send email reply
  const handleSendEmailReply = async () => {
    if (!emailReplyInput.trim() || !selectedEmail) return;
    
    setSendingEmailReply(true);
    try {
      // Determine the account type
      const currentAccount = emailAccounts.find(acc => acc.id === activeEmailAccount);
      const accountType = currentAccount?.type || 'outlook';
      const accountEmail = currentAccount?.email || '';
      
      // Get the original message ID
      const originalMessageId = selectedEmail.thread?.[0]?.id || selectedEmail.id;
      
      // Format reply body as HTML
      const replyBody = `<div style="font-family: Arial, sans-serif;">${emailReplyInput.replace(/\n/g, '<br>')}</div>`;
      
      if (accountType === 'nylas' && accountEmail) {
        // Reply via Nylas API
        const replyTo = selectedEmail.email || selectedEmail.from;
        const replySubject = selectedEmail.subject?.startsWith('Re:') ? selectedEmail.subject : `Re: ${selectedEmail.subject || ''}`;
        const authToken = localStorage.getItem('auth_token');
        
        const response = await fetch(`${window.location.origin}/api/nylas/send?from_email=${encodeURIComponent(accountEmail)}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {})
          },
          body: JSON.stringify({
            to: [replyTo],
            subject: replySubject,
            body: replyBody,
            reply_to_message_id: originalMessageId
          })
        });
        
        if (!response.ok) {
          const rawText = await response.text();
          let detail = 'Failed to send reply via Nylas';
          try { detail = JSON.parse(rawText)?.detail || detail; } catch { /* ignored */ }
          throw new Error(detail);
        }
        
        console.log('✅ Email reply sent successfully via Nylas');
      } else {
        // Reply via Outlook API (original behavior)
        const { outlook2EmailAPI } = await import('../services/api');
        await outlook2EmailAPI.replyToEmail(originalMessageId, replyBody, false);
        console.log('✅ Email reply sent successfully via Outlook');
      }
      
      setEmailReplyInput('');
      alert('Email sent successfully!');
      
      // Optionally refresh the thread for Outlook accounts
      if (accountType !== 'nylas' && selectedEmail.conversationId) {
        const { outlook2EmailAPI } = await import('../services/api');
        const threadData = await outlook2EmailAPI.getThread(selectedEmail.conversationId);
        if (threadData.messages && threadData.messages.length > 0) {
          const threadMessages = threadData.messages.map(msg => ({
            id: msg.id,
            from: msg.sender_name || msg.from,
            email: msg.from,
            time: formatEmailTime(msg.received_time),
            body: msg.body,
            bodyType: msg.body_type,
            to: msg.to || [],
            cc: msg.cc || [],
            attachments: msg.attachments || [],
            hasAttachments: msg.has_attachments || (msg.attachments && msg.attachments.length > 0)
          }));
          setSelectedEmail({
            ...selectedEmail,
            thread: threadMessages
          });
        }
      }
    } catch (err) {
      console.error('Error sending email reply:', err);
      alert(`Failed to send email: ${err.message || 'Please try again.'}`);
    } finally {
      setSendingEmailReply(false);
    }
  };

  // Handle folder change - use cached emails if available, don't auto-fetch
  const handleFolderChange = (folder) => {
    setActiveFolder(folder);
    setSelectedEmail(null); // Clear selected email when switching folders
    
    // If Outlook is the active account, use cached emails for this folder
    if (activeEmailAccount === 'outlook') {
      // Check if we have cached emails for this folder
      if (outlookEmailsCache[folder]) {
        setOutlookEmails(outlookEmailsCache[folder]);
        console.log(`📧 Using cached ${outlookEmailsCache[folder].length} emails from ${folder}`);
      } else {
        // No cached data for this folder - show empty state
        // User needs to click sync to fetch
        setOutlookEmails([]);
        console.log(`📧 No cached emails for ${folder} - click sync to fetch`);
      }
    }
  };

  // Handle opening "Add Contact" (uses existing Add Contact modal)
  const handleAddContactFromDropdown = (conv) => {
    setNewContactName(conv.name);
    setNewContactPhone(conv.phone);
    setNewContactEmail('');
    setNewContactCompany('');
    setNewContactRole('');
    setNewContactCategory('Personal');
    setNewContactNotes('Added from text message');
    setOriginalConversationName(conv.name);
    setShowAddContactModal(true);
    setOpenMessageDropdown(null);
  };

  const availableEmojis = ['❤️', '👍', '😂', '🔥', '🎉', '👏', '😍', '🙌'];

  // Favorites contacts for quick calling - empty for new users
  // Users can add contacts from their connected accounts
  const favoriteContacts = [];

  // Group members data - empty for new users
  // Users can create groups from their connected contacts
  const groupMembers = {};

  // Get members for selected conversation or return empty array if not a group
  const getCurrentGroupMembers = () => {
    if (!selectedConversation || !conversations[selectedConversation?.id]?.isGroup) return [];
    return groupMembers[selectedConversation?.id] || [];
  };

  // Send SMS message
  // Insert emoji at cursor position in the message textarea
  const insertEmoji = (emoji) => {
    const textarea = messageTextareaRef.current;
    if (!textarea) {
      setMessageInput(prev => prev + emoji);
      return;
    }
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const newVal = messageInput.slice(0, start) + emoji + messageInput.slice(end);
    setMessageInput(newVal);
    // Restore cursor position after React re-render
    requestAnimationFrame(() => {
      textarea.selectionStart = textarea.selectionEnd = start + emoji.length;
      textarea.focus();
    });
  };

  const handleSendMessage = async () => {    if (!messageInput.trim() || !selectedConversation) return;
    
    const messageText = messageInput.trim();
    const now = new Date();
    const timeStamp = now.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true,
      timeZone: 'America/Chicago'
    });
    const dateStamp = now.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: '2-digit',
      timeZone: 'America/Chicago'
    });
    
    try {
      // Determine phone type from conversation
      const phoneType = selectedConversation.phoneType || selectedConversation.phone_type || 'personal';
      
      let response;
      
      // Route based on phone type - personal/business go through Mac, twilio goes direct
      if (phoneType === 'personal' || phoneType === 'business') {
        // Queue for Mac Messages app via outbox
        response = await apiCall('/mac-messages/outbox', {
          method: 'POST',
          body: JSON.stringify({
            recipient: selectedConversation.phone,
            message: messageText,
            phone_type: phoneType,
            user_id: userId
          })
        });
        console.log(`📱 Message queued for Mac Messages (${phoneType})`, response);
        
        // Add optimistic local update
        const newMessage = {
          id: response?.message_id || `sent-${Date.now()}`,
          sender: 'You',
          text: messageText,
          time: timeStamp,
          sent: true,
          pending: true,
          phone_type: phoneType
        };
        
        // Update the selected conversation with the new message immediately
        setSelectedConversation(prev => ({
          ...prev,
          lastMessage: messageText,
          timestamp: new Date().toISOString(),
          messages: [...(prev.messages || []), newMessage]
        }));
        
        // Also update in conversations list
        setConversations(prevConvs => 
          prevConvs.map(c => 
            c.id === selectedConversation.id 
              ? { ...c, lastMessage: messageText, timestamp: new Date().toISOString() }
              : c
          )
        );
        
        // Clear input
        setMessageInput('');
        setIsUserScrolling(false);
        
        // Scroll to bottom
        setTimeout(() => {
          if (messagesContainerRef.current) {
            messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
          }
        }, 100);
        
        toast.success('Message queued! Your Mac script will send it.');
        return; // Exit early
      } else {
        // Twilio - send immediately
        response = await apiCall('/messages/send-sms', {
          method: 'POST',
          body: JSON.stringify({
            phone_number: selectedConversation.phone,
            recipient_name: selectedConversation.name,
            message: messageText
          })
        });
        console.log('✅ Message sent via Twilio:', response);
      }
      
      // Clear input
      setMessageInput('');
      
      // Mark that we're not user scrolling so auto-scroll works
      setIsUserScrolling(false);
      
      // Add sent message to the current conversation locally (optimistic update)
      const newMessage = {
        id: `sent-${Date.now()}`,
        sender: 'You',
        text: messageText,
        time: timeStamp,
        sent: true,
        pending: phoneType === 'personal' || phoneType === 'business'
      };
      
      // Update the selected conversation with the new message
      setSelectedConversation(prev => ({
        ...prev,
        messages: [...(prev.messages || []), newMessage],
        lastMessage: messageText,
        timestamp: dateStamp
      }));
      
      // Update the conversation in the list
      setConversations(prev => prev.map(conv => {
        if (conv.phone === selectedConversation.phone) {
          return {
            ...conv,
            messages: [...(conv.messages || []), newMessage],
            lastMessage: messageText,
            timestamp: dateStamp
          };
        }
        return conv;
      }));
      
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Failed to send message: ' + error.message);
    }
  };

  // Function to build conversations from messages
  const buildConversations = (messagesData) => {
    const conversationsMap = {};
    
    // Ensure messagesData is an array
    if (!Array.isArray(messagesData)) {
      console.warn('buildConversations: messagesData is not an array', messagesData);
      return [];
    }
    
    messagesData.forEach(msg => {
      let phone;
      let contactName;
      
      if (msg.from_name === 'You') {
        // Outgoing message - conversation is with the recipient
        phone = msg.to_phone;
        contactName = msg.subject?.replace('Text to ', '') || phone;
      } else {
        // Incoming message - conversation is with the sender
        if (msg.from_phone) {
          phone = msg.from_phone;
          contactName = msg.from_name;
        } else {
          // Old message without from_phone - skip it
          console.warn('Skipping message without from_phone:', msg.id);
          return;
        }
      }
      
      if (!conversationsMap[phone]) {
        conversationsMap[phone] = {
          id: phone,
          name: contactName,
          phone: phone,
          avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(contactName)}&size=128&background=random&color=fff&bold=true`,
          lastMessage: '',
          time: 'Now',
          unread: 0,
          isPinned: false,
          isOnline: false,
          category: msg.category || 'personal', // Get category from message
          isGroup: false,
          phoneType: msg.category === 'business' ? 'business' : 'personal',
          messages: []
        };
      }
      
      // Update category if message has one (use the most recent one)
      if (msg.category) {
        conversationsMap[phone].category = msg.category;
        conversationsMap[phone].phoneType = msg.category === 'business' ? 'business' : 'personal';
      }
      
      // Add message to conversation
      conversationsMap[phone].messages.push(msg);
      
      // Update unread count
      if (!msg.read && msg.from_name !== 'You') {
        conversationsMap[phone].unread = (conversationsMap[phone].unread || 0) + 1;
      }
    });
    
    // After all messages are added, update lastMessage to be the most recent one
    Object.values(conversationsMap).forEach(conversation => {
      // Sort messages by created_at
      conversation.messages.sort((a, b) => {
        return (a.created_at || '') > (b.created_at || '') ? 1 : -1;
      });
      
      // Set lastMessage to the most recent message
      const lastMsg = conversation.messages[conversation.messages.length - 1];
      if (lastMsg) {
        conversation.lastMessage = lastMsg.preview || lastMsg.content || '';
        conversation.time = lastMsg.timestamp || 'Now';
      }
      
      // Detect conversation category from message content
      // Only action categories: Urgent > Meeting > Follow-up > Client > Report
      const categoryPriority = ['urgent', 'meeting', 'follow-up', 'client', 'report'];
      let detectedCategories = {};
      
      conversation.messages.forEach(msg => {
        const msgContent = msg.content || msg.preview || '';
        const msgCategory = detectMessageCategory(msgContent);
        if (msgCategory) {
          detectedCategories[msgCategory] = (detectedCategories[msgCategory] || 0) + 1;
        }
      });
      
      // Set conversation category to the highest priority action category found
      conversation.category = null; // Default: no category badge
      for (const cat of categoryPriority) {
        if (detectedCategories[cat]) {
          conversation.category = cat;
          break;
        }
      }
    });
    
    // Convert to array and sort by most recent
    return Object.values(conversationsMap).sort((a, b) => {
      return (b.messages[b.messages.length - 1]?.created_at || '') > (a.messages[a.messages.length - 1]?.created_at || '') ? 1 : -1;
    });
  };

  // Fetch real messages and calls from API
  // Re-run when userId becomes available (auth context may load after mount)
  useEffect(() => {
    let isMounted = true;
    let pollInterval = null;
    let isTabVisible = true;
    
    const fetchData = async (isInitialLoad = false) => {
      // Skip polling if tab is not visible (save resources)
      if (!isInitialLoad && !isTabVisible) {
        console.log('⏸️ Skipping poll - tab not visible');
        return;
      }
      
      try {
        // Only show loading on initial load, not on background refreshes
        if (isInitialLoad) {
          setLoading(true);
        }
        
        // Counterfind tenant: inject 7 mockup conversations on the Messages
        // tab. Skip the live API to keep the demo deterministic. The mock
        // builder is the same module-level function used by fetchConversations.
        if (isCounterfind) {
          const cfConversations = _buildCounterfindMockConversations();
          if (isMounted) {
            setConversations(cfConversations);
            if (isInitialLoad && cfConversations.length > 0) {
              setSelectedConversation(cfConversations[0]);
            }
            setLoading(false);
          }
        } else if (isDemoMode && demoData?.messages) {
          const demoConversations = demoData.messages.map((msg, idx) => ({
            id: msg.id || `demo-conv-${idx}`,
            phone: msg.phone || `+1214555${idx.toString().padStart(4, '0')}`,
            name: msg.from || 'Demo Contact',
            contact_name: msg.from || 'Demo Contact',
            messages: [{
              id: `msg-${idx}-1`,
              content: msg.preview || 'Demo message',
              text: msg.preview || 'Demo message',
              timestamp: msg.time || new Date().toISOString(),
              direction: 'inbound',
              sender: msg.from || 'Demo Contact',
              created_at: msg.time || new Date().toISOString()
            }],
            last_message: msg.preview || 'Demo message',
            timestamp: msg.time || new Date().toISOString(),
            category: 'general',
            unread_count: msg.unread ? 1 : 0
          }));
          
          if (isMounted) {
            setConversations(demoConversations);
            console.log(`💬 Demo mode: Loaded ${demoConversations.length} demo conversations`);
            
            // Auto-select first conversation
            if (isInitialLoad && demoConversations.length > 0) {
              setSelectedConversation(demoConversations[0]);
            }
            setLoading(false);
          }
          // Continue to load calls in demo mode - don't return early
        } else {
        
        // Fetch conversations using the fast endpoint (includes messages for categorization)
        // Pass userId so messages are properly loaded; endpoint is tolerant of missing userId
        const userParam = userId ? `&user_id=${encodeURIComponent(userId)}` : '';
        const conversationsData = await apiCall(`/conversations-fast?limit=200${userParam}`);
        
        // Debug: Log message counts
        if (conversationsData && Array.isArray(conversationsData)) {
          const totalMsgs = conversationsData.reduce((sum, c) => sum + (c.messages?.length || 0), 0);
          console.log(`📨 API returned ${conversationsData.length} conversations with ${totalMsgs} total messages`);
          
          // Sample a few conversations to check messages
          conversationsData.slice(0, 3).forEach(c => {
            console.log(`  - ${c.name}: ${c.messages?.length || 0} messages`);
          });
        }
        
        if (!isMounted) return; // Don't update state if unmounted
        
        if (conversationsData && Array.isArray(conversationsData)) {
          // Sort by timestamp with most recent first - use multiple fields for reliability
          const sortedConversations = conversationsData.sort((a, b) => {
            const getTime = (conv) => {
              // Priority: timestamp first (from backend aggregation), then fallbacks
              const ts = conv.timestamp || conv.updated_at || conv.last_message_time || 
                        conv.lastMessageTime || conv.created_at || conv.messages?.[conv.messages?.length - 1]?.timestamp || 0;
              return new Date(ts).getTime() || 0;
            };
            return getTime(b) - getTime(a);
          });
          setConversations(sortedConversations);
          console.log(`🔄 Conversations refreshed: ${sortedConversations.length} conversations`);
          
          // Auto-select first conversation on initial load
          // This ensures the most recent conversation is shown when the page opens
          if (isInitialLoad && sortedConversations.length > 0) {
            // Find the most recent conversation by checking actual message timestamps
            // This matches the sorting logic in filteredConversations
            const getTimestamp = (conv) => {
              const timestamps = [
                conv.timestamp,
                conv.messages?.[conv.messages.length - 1]?.timestamp,
                conv.messages?.[conv.messages.length - 1]?.time,
                conv.messages?.[conv.messages.length - 1]?.created_at,
                conv.last_message_time,
                conv.lastMessageTime,
                conv.updated_at
              ];
              for (const ts of timestamps) {
                if (ts) {
                  const date = new Date(ts);
                  if (!isNaN(date.getTime()) && date.getTime() > 946684800000) {
                    return date.getTime();
                  }
                }
              }
              return 0;
            };
            
            // Sort to find the truly most recent conversation
            const mostRecent = [...sortedConversations].sort((a, b) => {
              return getTimestamp(b) - getTimestamp(a);
            })[0];
            
            console.log('📱 Auto-selecting most recent conversation:', mostRecent?.name);
            isManualSelection.current = true;
            
            // Set conversation and immediately fetch its messages
            const convWithLoading = { ...mostRecent, messagesLoading: true };
            setSelectedConversation(convWithLoading);
            
            // Fetch messages for the auto-selected conversation
            try {
              const chatId = mostRecent.chat_id || mostRecent.phone;
              console.log('📥 Auto-fetching messages for:', chatId);
              const messages = await apiCall(`/conversations-fast/${mostRecent.id}/messages?chat_id=${encodeURIComponent(chatId)}&limit=100`);
              console.log('📨 Auto-fetched messages:', messages?.length);
              if (messages && Array.isArray(messages) && isMounted) {
                const updatedConv = { ...mostRecent, messages, messagesLoading: false };
                setSelectedConversation(updatedConv);
                // Also update in conversations list
                setConversations(prevConvs => 
                  prevConvs.map(c => c.id === mostRecent.id ? updatedConv : c)
                );
              }
            } catch (err) {
              console.log('Could not auto-fetch messages:', err);
              if (isMounted) {
                setSelectedConversation(prev => prev ? { ...prev, messagesLoading: false } : null);
              }
            }
          }
        }
        } // End of else block for non-demo mode conversations
        
        // Fetch calls - use demo data if in demo mode
        if (isDemoMode && demoData?.calls) {
          const demoCalls = demoData.calls.map((call, idx) => {
            // Parse duration string like "8:32" into seconds
            let durationSecs = 0;
            if (call.duration) {
              const parts = call.duration.split(':');
              if (parts.length === 2) {
                durationSecs = parseInt(parts[0], 10) * 60 + parseInt(parts[1], 10);
              }
            }
            
            return {
              id: call.id || `demo-call-${idx}`,
              call_sid: call.id || `demo-call-${idx}`,
              from: call.direction === 'outbound' ? 'You' : call.contact,
              to: call.direction === 'outbound' ? call.contact : 'You',
              from_phone: call.direction === 'outbound' ? '+15551234567' : call.phone,
              to_phone: call.direction === 'outbound' ? call.phone : '+15551234567',
              contact_name: call.contact || 'Unknown',
              phone: call.phone,
              direction: call.direction || 'inbound',
              duration: durationSecs,
              duration_seconds: durationSecs,
              status: call.status || 'completed',
              timestamp: call.time || new Date().toISOString(),
              created_at: call.time || new Date().toISOString(),
              notes: call.notes || '',
              source: 'demo'
            };
          });
          if (isMounted) {
            setCalls(demoCalls);
            console.log(`📞 Demo mode: Loaded ${demoCalls.length} demo calls`);
          }
        } else {
          try {
            const callsData = await apiCall('/calls');
            if (isMounted) {
              setCalls(Array.isArray(callsData) ? callsData : []);
            }
          } catch (err) {
            console.log('Calls endpoint not available yet');
            if (isMounted) {
              setCalls([]);
            }
          }
        }
        
        // Outlook2 emails are now fetched by the auto-fetch useEffect when Email tab is active
        // This prevents overwriting emails when switching folders
        
      } catch (error) {
        console.error('Error fetching data:', error);
        // Don't clear messages on error - keep showing existing data
        // Only clear on initial load failure
        if (isInitialLoad && isMounted) {
          setMessages([]);
        }
      } finally {
        if (isMounted && isInitialLoad) {
          setLoading(false);
        }
      }
    };
    
    // Handle tab visibility changes
    const handleVisibilityChange = () => {
      isTabVisible = !document.hidden;
      if (isTabVisible && isMounted) {
        console.log('👁️ Tab visible - refreshing messages');
        fetchData(false); // Immediately refresh when tab becomes visible
      }
    };
    
    // Add visibility change listener
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Initial fetch
    fetchData(true);
    
    // Auto-refresh every 3 seconds for real-time message updates
    pollInterval = setInterval(() => {
      if (isMounted) {
        fetchData(false);
      }
    }, 3000);
    
    // Cleanup function
    return () => {
      isMounted = false;
      if (pollInterval) {
        clearInterval(pollInterval);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [userId, isDemoMode, demoData]); // Re-run when userId becomes available or demo mode changes
  
  // Track if conversation was manually selected by user (vs auto-update)
  const isManualSelection = useRef(false);
  
  // Separate effect to update selectedConversation when conversations change (auto-refresh)
  useEffect(() => {
    if (selectedConversation && conversations.length > 0) {
      // Match by BOTH id AND phone to handle Mac synced conversations
      const updatedSelected = conversations.find(c => 
        c.id === selectedConversation.id || 
        (c.phone && selectedConversation.phone && c.phone === selectedConversation.phone)
      );
      
      // Debug logging
      if (selectedConversation.phone?.includes('4696627370')) {
        console.log('🔍 Checking for update:', {
          found: !!updatedSelected,
          currentMsgs: selectedConversation.messages?.length,
          newMsgs: updatedSelected?.messages?.length,
          currentLast: selectedConversation.messages?.[selectedConversation.messages?.length - 1]?.text?.substring(0, 20),
          newLast: updatedSelected?.messages?.[updatedSelected?.messages?.length - 1]?.text?.substring(0, 20)
        });
      }
      
      if (updatedSelected) {
        // IMPORTANT: Preserve messages that were fetched on-click
        // If current selectedConversation has messages but updatedSelected doesn't,
        // keep the current messages (they were fetched separately)
        if (selectedConversation.messages?.length > 0 && (!updatedSelected.messages || updatedSelected.messages.length === 0)) {
          // Don't overwrite - we have messages that were fetched on click
          console.log('📱 Preserving on-click fetched messages:', selectedConversation.messages?.length);
          return;
        }
        
        // Get any locally sent messages that aren't in the server response yet
        const localSentMessages = (selectedConversation.messages || []).filter(m => 
          m.sent && (m.pending || (typeof m.id === 'string' && m.id.startsWith('sent-')))
        );
        
        // Check if server has newer messages than what we have (excluding our local sent)
        const serverMessages = updatedSelected.messages || [];
        const currentServerMessages = (selectedConversation.messages || []).filter(m => 
          !m.sent || (!m.pending && !(typeof m.id === 'string' && m.id.startsWith('sent-')))
        );
        
        const serverHasNewMessages = serverMessages.length > currentServerMessages.length ||
          (serverMessages.length > 0 && currentServerMessages.length > 0 &&
           serverMessages[serverMessages.length - 1]?.text !== currentServerMessages[currentServerMessages.length - 1]?.text);
        
        if (serverHasNewMessages || localSentMessages.length === 0) {
          // Merge server messages with any pending local sent messages
          const mergedMessages = [...serverMessages];
          
          // Add back local sent messages that aren't in the server response
          for (const localMsg of localSentMessages) {
            const alreadyInServer = serverMessages.some(sm => 
              sm.text === localMsg.text && 
              Math.abs(new Date(sm.timestamp || sm.time).getTime() - new Date(localMsg.time).getTime()) < 60000
            );
            if (!alreadyInServer) {
              mergedMessages.push(localMsg);
            }
          }
          
          // Sort merged messages by timestamp to ensure correct chronological order
          mergedMessages.sort((a, b) => {
            const timeA = new Date(a.timestamp || a.time || 0).getTime();
            const timeB = new Date(b.timestamp || b.time || 0).getTime();
            return timeA - timeB;
          });
          
          if (mergedMessages.length !== selectedConversation.messages?.length || serverHasNewMessages) {
            console.log(`📱 Updating conversation with ${mergedMessages.length} messages (${localSentMessages.length} local sent preserved)`);
            setSelectedConversation(prev => ({
              ...updatedSelected,
              messages: mergedMessages
            }));
          }
        }
      }
    }
  }, [conversations]);

  // Track the last manually selected conversation ID to prevent re-scroll on auto-refresh
  const lastManualSelectionId = useRef(null);

  // Scroll to bottom ONLY when user manually opens a conversation
  useEffect(() => {
    // Only scroll if:
    // 1. We have a conversation
    // 2. It was manually selected (flag is true)
    // 3. The ID is different from last manual selection (prevents duplicate scrolls)
    if (selectedConversation && isManualSelection.current && 
        lastManualSelectionId.current !== selectedConversation.id) {
      
      lastManualSelectionId.current = selectedConversation.id;
      setJustOpenedConversation(true);
      setPreviousMessageCount(selectedConversation?.messages?.length || 0);
      
      // Force scroll to bottom - use setTimeout to ensure DOM is ready
      setTimeout(() => {
        if (messagesContainerRef.current) {
          const container = messagesContainerRef.current;
          container.scrollTop = container.scrollHeight;
        }
        
        if (messagesEndRef.current) {
          messagesEndRef.current.scrollIntoView({ behavior: 'auto', block: 'end' });
        }
      }, 150);
      
      // Double-scroll after messages are rendered
      setTimeout(() => {
        if (messagesContainerRef.current) {
          const container = messagesContainerRef.current;
          container.scrollTop = container.scrollHeight;
        }
      }, 300);
      
      // Reset flags after scroll
      const timer = setTimeout(() => {
        setJustOpenedConversation(false);
        isManualSelection.current = false;
      }, 500);
      
      return () => {
        clearTimeout(timer);
      };
    }
  }, [selectedConversation]); // Watch the whole object, but only act on ID change + manual flag

  // Smart auto-scroll: ONLY scroll on NEW messages when user is near bottom
  useEffect(() => {
    if (!messagesContainerRef.current || !messagesEndRef.current || !selectedConversation) {
      return;
    }

    // NEVER scroll if conversation was just opened (still in grace period)
    if (justOpenedConversation) {
      return;
    }

    const container = messagesContainerRef.current;
    const messageCount = selectedConversation?.messages?.length || 0;
    
    // Only proceed if message count actually increased (new message)
    if (messageCount <= previousMessageCount) {
      setPreviousMessageCount(messageCount);
      return;
    }
    
    // Check if user is near the bottom (within 100px)
    const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 100;
    
    // Only scroll if user was already at bottom AND not manually scrolling
    if (isNearBottom && !isUserScrolling) {
      requestAnimationFrame(() => {
        container.scrollTop = container.scrollHeight;
      });
    }
    
    setPreviousMessageCount(messageCount);
  }, [selectedConversation?.messages?.length]);

  // Track when user manually scrolls
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 100;
      setIsUserScrolling(!isNearBottom);
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [selectedConversation]);

  // Persist Outlook email state to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('outlookEmails', JSON.stringify(outlookEmails));
    } catch (e) { console.warn('Failed to save outlookEmails to localStorage:', e); }
  }, [outlookEmails]);

  useEffect(() => {
    try {
      localStorage.setItem('outlookEmailsCache', JSON.stringify(outlookEmailsCache));
    } catch (e) { console.warn('Failed to save outlookEmailsCache to localStorage:', e); }
  }, [outlookEmailsCache]);

  useEffect(() => {
    try {
      localStorage.setItem('outlookConnected', String(outlookConnected));
    } catch (e) { console.warn('Failed to save outlookConnected to localStorage:', e); }
  }, [outlookConnected]);

  useEffect(() => {
    try {
      localStorage.setItem('outlookHasInitiallyFetched', String(hasInitiallyFetched));
    } catch (e) { console.warn('Failed to save hasInitiallyFetched to localStorage:', e); }
  }, [hasInitiallyFetched]);

  // Auto-fetch Outlook emails ONLY ONCE per session, not when switching tabs/pages
  // Use a ref to track if we've already attempted a fetch this session
  const hasFetchAttempted = useRef((() => {
    try {
      return localStorage.getItem('outlookFetchAttempted') === 'true';
    } catch { return false; }
  })());

  useEffect(() => {
    const fetchOutlookEmails = async () => {
      // Skip if we've already attempted fetch OR if we have cached data
      if (hasFetchAttempted.current) {
        console.log('📧 Skipping fetch - already attempted this session');
        return;
      }
      
      // Only auto-fetch when first visiting email tab
      if (activeTab === 'email' && activeEmailAccount === 'outlook' && !outlookLoading && activeFolder === 'inbox') {
        // Check if we have any cached emails (from localStorage or state)
        const hasCachedEmails = Object.keys(outlookEmailsCache).length > 0 || outlookEmails.length > 0;
        if (hasCachedEmails) {
          console.log('📧 Using persisted emails from localStorage');
          hasFetchAttempted.current = true;
          localStorage.setItem('outlookFetchAttempted', 'true');
          setHasInitiallyFetched(true);
        } else if (!hasInitiallyFetched) {
          console.log('📧 Initial fetch of Outlook emails...');
          hasFetchAttempted.current = true;
          localStorage.setItem('outlookFetchAttempted', 'true');
          await syncOutlookEmails();
        }
      }
    };
    fetchOutlookEmails();
  }, [activeTab, activeEmailAccount]);

  // When switching back to email tab, restore cached emails for current folder
  useEffect(() => {
    if (activeTab === 'email' && activeEmailAccount === 'outlook') {
      if (outlookEmailsCache[activeFolder]) {
        setOutlookEmails(outlookEmailsCache[activeFolder]);
        console.log(`📧 Restored ${outlookEmailsCache[activeFolder].length} cached emails for ${activeFolder}`);
      }
    }
  }, [activeTab, activeEmailAccount, activeFolder]);

  // Filter conversations by category and phone type
  const filteredConversations = conversations.filter(conv => {
    // Get the persisted category assignment first (from manual tagging)
    // Then fallback to auto-detection
    const convId = conv.id || conv.conversation_id;
    const lastMsgId = conv.messages?.[conv.messages?.length - 1]?.id || 
                      conv.messages?.[conv.messages?.length - 1]?.message_id ||
                      `${convId}-last`;
    
    // Check persisted category first (manual tagging)
    const persistedCategory = messageCategoryAssignments[lastMsgId] || 
                              messageCategoryAssignments[convId] ||
                              conv.messageCategory;
    
    // Fallback to auto-detection from last message content
    const lastMsgText = conv.lastMessage || conv.preview || '';
    const detectedCategory = detectMessageCategory(lastMsgText);
    
    // Use persisted category if available, otherwise detected
    const msgCategory = persistedCategory || detectedCategory;
    const normalizedMsgCategory = msgCategory === 'follow_up' ? 'follow-up' : msgCategory;

    // Filter by message category (all, urgent, general, meeting, follow-up, client, report, travel)
    const categoryMatch = activeCategory === 'all' || normalizedMsgCategory === activeCategory || msgCategory === activeCategory;
    
    // Filter by phone type / contact category (all, personal, business, family, friends)
    // For merged conversations, check if ANY of the phone_types match the filter
    const phoneTypes = getAccountCategories(conv);
    const contactCategory = conv.category?.toLowerCase();
    const phoneTypeMatch = activeMessagesCategory === 'all' || 
      phoneTypes.includes(activeMessagesCategory) || 
      contactCategory === activeMessagesCategory;
    
    // Search filter
    const searchMatch = !searchQuery || (conv.name || '').toLowerCase().includes(searchQuery.toLowerCase()) || 
                       (conv.lastMessage || '').toLowerCase().includes(searchQuery.toLowerCase());
    
    return categoryMatch && phoneTypeMatch && searchMatch;
  }).sort((a, b) => {
    // Always sort by most recent message first
    // Use the actual message timestamp from the backend
    // Conversations WITHOUT dates go to the BOTTOM
    const getTimestamp = (conv) => {
      // Priority order: timestamp (from backend aggregation), then fallbacks
      const timestamps = [
        // First check timestamp (this is the last message time from backend)
        conv.timestamp,
        // Then check last message in messages array
        conv.messages?.[conv.messages.length - 1]?.timestamp,
        conv.messages?.[conv.messages.length - 1]?.time,
        conv.messages?.[conv.messages.length - 1]?.created_at,
        // Last resort: use the sync/update time
        conv.last_message_time,
        conv.lastMessageTime,
        conv.updated_at,
        conv.created_at
      ];
      
      for (const ts of timestamps) {
        if (ts) {
          const date = new Date(ts);
          // Check if it's a valid date and not from before year 2000 (to exclude invalid dates)
          if (!isNaN(date.getTime()) && date.getTime() > 946684800000) {
            return date.getTime();
          }
        }
      }
      // Return null for conversations without valid timestamps
      return null;
    };
    
    const aTime = getTimestamp(a);
    const bTime = getTimestamp(b);
    
    // Put conversations WITHOUT timestamps at the very BOTTOM
    // Both have no timestamp - keep original order
    if (aTime === null && bTime === null) return 0;
    // Only 'a' has no timestamp - 'a' goes after 'b' (to bottom)
    if (aTime === null) return 1;
    // Only 'b' has no timestamp - 'b' goes after 'a' (to bottom)
    if (bTime === null) return -1;
    
    // Both have valid timestamps - sort by newest first
    return bTime - aTime;
  });

  // When a specific category is selected (not 'all'), show individual messages with that category
  const categorizedMessages = React.useMemo(() => {
    if (activeCategory === 'all') return [];
    
    console.log(`🏷️ Category filter: activeCategory="${activeCategory}", conversations=${conversations.length}`);
    
    const messagesWithCategory = [];
    let totalMessagesChecked = 0;
    const seenIds = new Set(); // Prevent duplicates
    
    conversations.forEach(conv => {
      const convMsgs = conv.messages || [];
      totalMessagesChecked += convMsgs.length;
      
      convMsgs.forEach((msg, msgIndex) => {
        const msgContent = msg.text || msg.content || msg.preview || '';
        // Skip empty messages (attachments without text, etc.)
        if (!msgContent.trim()) return;
        const msgId = msg.id || msg.message_id || `${conv.id}-${msg.timestamp}-${msgIndex}`;
        const persistedCategory = messageCategoryAssignments[msgId];
        const explicitCategory = msg.messageCategory;
        const detectedCategory = detectMessageCategory(msgContent);
        const msgCategory = persistedCategory || explicitCategory || detectedCategory;
        
        if (msgCategory === activeCategory) {
          // Create unique ID for this message entry
          const uniqueId = `cat-${conv.id}-${msgId}-${msgIndex}`;
          
          // Skip if we've already added this message
          if (seenIds.has(uniqueId)) return;
          seenIds.add(uniqueId);
          
          messagesWithCategory.push({
            id: uniqueId,
            messageId: msgId,
            conversationId: conv.id,
            conversationName: conv.name,
            conversationPhone: conv.phone,
            avatar: conv.avatar,
            content: msgContent,
            time: msg.time || msg.timestamp || 'Now',
            category: msgCategory,
            isOwn: msg.from_name === 'You' || msg.sent === true || msg.is_from_me === true,
            originalMessage: msg,
            originalConversation: conv
          });
        }
      });
    });
    
    console.log(`🏷️ Category results: checked ${totalMessagesChecked} messages, found ${messagesWithCategory.length} matching "${activeCategory}"`);
    
    // Sort by most recent first
    return messagesWithCategory.sort((a, b) => {
      const aTime = a.originalMessage?.created_at || a.originalMessage?.timestamp || '';
      const bTime = b.originalMessage?.created_at || b.originalMessage?.timestamp || '';
      return bTime > aTime ? 1 : -1;
    });
  }, [conversations, activeCategory, messageCategoryAssignments]);

  // Messages for all conversations with real photos

  // Email list - Empty by default for new users. Real emails come from connected accounts (Nylas, Outlook, Gmail)
  const emailsByAccount = {
    // All sample/mockup data removed - new users see a blank slate
    // Emails are populated from:
    // - nylasEmails (Nylas connected accounts)
    // - outlookEmails (Outlook/Microsoft accounts)  
    // - gmailEmails (Gmail accounts)
  };

  // Get emails for active account - use REAL emails only from connected sources (Outlook, Gmail, Nylas)
  const emailsForAccount = (() => {
    // Demo mode - return demo emails directly
    if (isDemoMode && activeEmailAccount === 'demo_account') {
      console.log('📧 Using demo emails, count:', nylasEmails.length);
      return nylasEmails;
    }
    
    if (activeEmailAccount === 'outlook') {
      // Always use Outlook emails when Outlook tab is active
      console.log('📧 Using outlookEmails, count:', outlookEmails.length, 'first email source:', outlookEmails[0]?.source);
      return outlookEmails;
    }
    
    // Check if active account is a Gmail-type account
    const activeAccount = emailAccounts.find(acc => acc.id === activeEmailAccount);
    if (activeAccount && activeAccount.type === 'gmail') {
      // Use real Gmail emails when any Gmail account tab is active
      console.log(`📧 Using gmailEmails for ${activeEmailAccount}, count:`, gmailEmails.length);
      return gmailEmails;
    }
    
    // Check if active account is a Nylas-connected account
    if (activeAccount && activeAccount.type === 'nylas') {
      // Use Nylas emails for connected accounts
      console.log(`📧 Using nylasEmails for ${activeEmailAccount}, count:`, nylasEmails.length);
      return nylasEmails;
    }
    
    return emailsByAccount[activeEmailAccount] || [];
  })();

  // Apply category assignments to emails
  const emailsWithCategories = emailsForAccount.map(email => ({
    ...email,
    category: emailCategoryAssignments[email.id] || email.category || 'all'
  }));

  // Filter emails by category, folder, and search query
  const filteredEmails = emailsWithCategories.filter(email => {
    const categoryMatch = activeCategory === 'all' || email.category === activeCategory;
    // For flagged folder: show emails that are flagged (via state or isFlagged prop)
    // For other folders: match by folder
    const isFlagged = flaggedEmails[email.id] || email.isFlagged;
    const folderMatch = activeFolder === 'flagged'
      ? isFlagged
      : (!email.folder || email.folder === activeFolder || email.folder === 'flagged');
    
    // Search filter - check subject, from, preview/body
    const searchMatch = !emailSearchQuery || (() => {
      const query = emailSearchQuery.toLowerCase();
      const subject = (email.subject || '').toLowerCase();
      const from = (email.from || '').toLowerCase();
      const emailAddr = (email.email || '').toLowerCase();
      const preview = (email.preview || email.body || '').toLowerCase();
      return subject.includes(query) || from.includes(query) || emailAddr.includes(query) || preview.includes(query);
    })();
    
    return categoryMatch && folderMatch && searchMatch;
  });

  // Pagination for emails
  const totalEmailPages = Math.ceil(filteredEmails.length / emailsPerPage);
  const paginatedEmails = filteredEmails.slice(
    (emailCurrentPage - 1) * emailsPerPage,
    emailCurrentPage * emailsPerPage
  );

  // Reset to page 1 when changing folders/accounts or search query
  useEffect(() => {
    setEmailCurrentPage(1);
  }, [activeFolder, activeEmailAccount, activeCategory, emailSearchQuery]);

  // Update category counts when emails or assignments change
  useEffect(() => {
    const allEmails = [...nylasEmails, ...outlookEmails, ...gmailEmails];
    if (allEmails.length === 0) {
      setCategoryCounts({});
      return;
    }
    
    const counts = {};
    allEmails.forEach(email => {
      // Use assignment first, then email's own category, then 'other'
      const cat = emailCategoryAssignments[email.id] || email.category || 'other';
      counts[cat] = (counts[cat] || 0) + 1;
    });
    setCategoryCounts(counts);
  }, [nylasEmails, outlookEmails, gmailEmails, emailCategoryAssignments]);

  return (
    <div className="h-screen bg-black text-white p-3 md:p-6 overflow-hidden flex flex-col">
      {/* Incoming Call Popup - renders on top of everything */}
      <IncomingCallPopup
        incomingCall={incomingCall}
        activeCall={voiceActiveCall}
        isConnected={voiceConnected}
        isMuted={isMuted}
        onAccept={acceptCall}
        onReject={rejectCall}
        onIgnore={ignoreCall}
        onEndCall={endVoiceCall}
        onToggleMute={toggleMute}
      />
      
      {/* Page Header - Using Refactored Component */}
      <PageHeader 
        title="Communications" 
        voiceReady={voiceReady} 
      />

      {/* Main Chat Widget - Dark Theme */}
      <div className="bg-black border border-gray-400 rounded-xl overflow-hidden hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300 flex flex-col flex-1 min-h-0">
        {/* Tab Selector - Using Refactored Component */}
        <TabHeader activeTab={activeTab} onTabChange={setActiveTab} />

        <div className="flex flex-col md:flex-row flex-1 overflow-hidden">
          {/* Messages Tab Content */}
          {activeTab === 'messages' && (
            <>
              {/* Left Sidebar - Conversation List - Hidden on mobile when chat is open */}
              <div className={`${mobileShowChat ? 'hidden md:flex' : 'flex'} w-full md:w-96 bg-black border-b md:border-b-0 md:border-r border-gray-400/20 flex-col flex-1 md:flex-initial overflow-hidden`}>
                {/* Messaging Connection Panel - Using Refactored Component */}
                <MessagingConnectionPanel
                  isMessagingConnected={isMessagingConnected}
                  messagingConnectionType={messagingConnectionType}
                  isConnectPanelCollapsed={isConnectPanelCollapsed}
                  setIsConnectPanelCollapsed={setIsConnectPanelCollapsed}
                  setShowMessagingConnectionModal={setShowMessagingConnectionModal}
                  setShowMacSyncModal={setShowMacSyncModal}
                  onDisconnect={() => {
                    localStorage.removeItem('messaging_connected');
                    localStorage.removeItem('messaging_connection_type');
                    setIsMessagingConnected(false);
                    setMessagingConnectionType(null);
                  }}
                />
                
                {/* Search Bar + Compose Button - Using Refactored Component */}
                <div className="p-3 md:p-4 flex-shrink-0 border-b border-gray-400/10">
                  <SearchBar
                    value={searchQuery}
                    onChange={setSearchQuery}
                    onClear={() => setSearchQuery('')}
                    onCompose={() => {
                      // Don't fetch contacts immediately - wait for user to search
                      setFilteredContacts([]);
                      setShowContactSuggestions(false);
                      setComposeRecipient('');
                      setComposeMessage('');
                      setShowComposeModal(true);
                    }}
                    placeholder="Search"
                    className="mb-2 md:mb-3"
                  />
                  
                  {/* Phone Type Tabs - Using Refactored Component */}
                  <PhoneTypeTabs
                    activePhoneType={activeMessagesCategory}
                    onPhoneTypeChange={(cat) => {
                      setActiveMessagesCategory(cat);
                      if (cat === 'dashboard') {
                        fetchDashboardUsers();
                        fetchDashboardConversations();
                      }
                    }}
                  />
                </div>

                {/* Dashboard Messages Content - When Dashboard tab is selected */}
                {activeMessagesCategory === 'dashboard' ? (
                  <div className="flex-1 overflow-hidden flex flex-col">
                    {/* Dashboard Category Filter Bar */}
                    <div className="flex-shrink-0 px-3 py-2 overflow-x-auto">
                      <div className="flex gap-1.5 min-w-max">
                        {['all', 'urgent', 'meeting', 'follow-up', 'client', 'report', 'travel', 'general'].map((cat) => {
                          const isActive = dashboardCategoryFilter === cat;
                          const labels = { all: 'All', urgent: 'Urgent', meeting: 'Meeting', 'follow-up': 'Follow Up', client: 'Client', report: 'Report', travel: 'Travel', general: 'General' };
                          return (
                            <button
                              key={cat}
                              onClick={() => setDashboardCategoryFilter(cat)}
                              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all whitespace-nowrap border ${
                                isActive
                                  ? 'bg-white text-black border-white'
                                  : 'bg-black text-white border-gray-400 hover:bg-gray-900'
                              }`}
                            >
                              {labels[cat]}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Dashboard Users & Conversations */}
                    <div className="flex-1 overflow-y-auto">
                      {/* Recent Dashboard Conversations */}
                      {dashboardConversations.filter(conv => {
                        if (dashboardCategoryFilter === 'all') return true;
                        const cat = detectMessageCategory(conv.last_message || '');
                        return cat === dashboardCategoryFilter;
                      }).length > 0 && (
                        <div className="border-b border-gray-400/20">
                          <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase">Recent Chats</div>
                          {dashboardConversations.filter(conv => {
                            if (dashboardCategoryFilter === 'all') return true;
                            const cat = detectMessageCategory(conv.last_message || '');
                            return cat === dashboardCategoryFilter;
                          }).map((conv) => {
                            const convCategory = detectMessageCategory(conv.last_message || '');
                            const convBadge = getCategoryBadge(convCategory);
                            return (
                            <div
                              key={conv.id}
                              onClick={() => selectDashboardConversation(conv)}
                              className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors border-b border-gray-800 ${
                                selectedDashboardUser?.id === conv.user_id 
                                  ? 'bg-gray-800' 
                                  : 'hover:bg-gray-900'
                              }`}
                            >
                              {conv.user_avatar ? (
                                <img src={conv.user_avatar} alt={conv.user_name} className="w-10 h-10 rounded-full object-cover flex-shrink-0" />
                              ) : (
                                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center text-white font-semibold flex-shrink-0">
                                  {conv.user_name?.charAt(0)?.toUpperCase() || '?'}
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between">
                                  <span className="font-medium text-white truncate">{conv.user_name}</span>
                                  <div className="flex items-center gap-1.5 ml-2 flex-shrink-0">
                                    {convBadge && (
                                      <span className={`px-1.5 py-0.5 rounded-full text-xs font-semibold ${convBadge.bg} ${convBadge.text}`}>
                                        {convBadge.label}
                                      </span>
                                    )}
                                    {conv.unread_count > 0 && (
                                      <span className="px-2 py-0.5 bg-purple-500 text-white text-xs rounded-full">
                                        {conv.unread_count}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <p className="text-sm text-gray-400 truncate">{conv.last_message}</p>
                              </div>
                            </div>
                            );
                          })}
                        </div>
                      )}
                      
                      {/* My Teams Section */}
                      {myTeams.length > 0 && (
                        <div className="border-b border-gray-400/20">
                          <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase flex items-center justify-between">
                            <button
                              className="flex items-center gap-1 hover:text-gray-300 transition-colors"
                              onClick={() => setTeamsCollapsed(c => !c)}
                            >
                              <svg className={`w-3 h-3 transition-transform duration-200 ${teamsCollapsed ? '-rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                              <span>Teams</span>
                            </button>
                            <span className="text-gray-600">{myTeams.length}</span>
                          </div>
                          {!teamsCollapsed && myTeams.map((team) => {
                            const colorMap = { purple: 'from-purple-500 to-purple-700', blue: 'from-blue-500 to-blue-700', green: 'from-green-500 to-green-700', red: 'from-red-500 to-red-700', orange: 'from-orange-500 to-orange-700', cyan: 'from-cyan-500 to-cyan-700', pink: 'from-pink-500 to-pink-700' };
                            const grad = colorMap[team.color] || colorMap.purple;
                            return (
                              <div
                                key={team.team_id}
                                data-testid={`team-chat-${team.team_id}`}
                                onClick={() => { fetchTeamMessages(team.team_id); setMobileShowChat(true); }}
                                className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors border-b border-gray-800 ${selectedTeam?.team_id === team.team_id ? 'bg-gray-800' : 'hover:bg-gray-900'}`}
                              >
                                <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${grad} flex items-center justify-center text-white font-bold text-sm flex-shrink-0`}>
                                  #
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center justify-between">
                                    <span className="font-medium text-white truncate">{team.name}</span>
                                    {team.unread_count > 0 && (
                                      <span className="px-2 py-0.5 bg-purple-500 text-white text-xs rounded-full ml-2">{team.unread_count}</span>
                                    )}
                                  </div>
                                  <p className="text-xs text-gray-500 truncate">
                                    {team.last_message
                                      ? `${team.last_message_sender ? team.last_message_sender + ': ' : ''}${team.last_message}`
                                      : `${team.member_count || 0} members`}
                                  </p>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {/* All Users */}
                      <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase flex items-center justify-between">
                        <button
                          className="flex items-center gap-1 hover:text-gray-300 transition-colors"
                          onClick={() => setTeamMembersCollapsed(c => !c)}
                        >
                          <svg
                            className={`w-3 h-3 transition-transform duration-200 ${teamMembersCollapsed ? '-rotate-90' : ''}`}
                            fill="none" stroke="currentColor" viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                          <span>Team Members</span>
                        </button>
                        {/* Notification Bell */}
                        <button
                          onClick={() => setShowNotificationsPanel(!showNotificationsPanel)}
                          className="relative p-1.5 hover:bg-gray-800 rounded-lg transition-colors"
                        >
                          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                          </svg>
                          {unreadNotifications > 0 && (
                            <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                              {unreadNotifications > 9 ? '9+' : unreadNotifications}
                            </span>
                          )}
                        </button>
                      </div>
                      
                      {/* Notifications Panel + User List (collapsible) */}
                      {!teamMembersCollapsed && (
                        <>
                      {/* Notifications Panel */}
                      {showNotificationsPanel && (
                        <div className="border-b border-gray-800 bg-gray-900/50">
                          <div className="px-4 py-2 text-xs font-semibold text-gray-400 flex items-center justify-between">
                            <span>Chat Invites</span>
                            <button
                              onClick={() => setShowNotificationsPanel(false)}
                              className="text-gray-500 hover:text-white"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </div>
                          {chatNotifications.length > 0 ? (
                            <div className="max-h-48 overflow-y-auto">
                              {chatNotifications.map((notif) => (
                                <div
                                  key={notif.id}
                                  className={`px-4 py-3 border-b border-gray-800 ${!notif.read ? 'bg-blue-900/20' : ''}`}
                                >
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white text-xs font-semibold">
                                        {notif.sender_name?.charAt(0)?.toUpperCase() || '?'}
                                      </div>
                                      <div>
                                        <p className="text-sm text-white font-medium">{notif.sender_name}</p>
                                        <p className="text-xs text-gray-400">{notif.message}</p>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-1">
                                      <button
                                        onClick={() => {
                                          // Start chat with this user
                                          const user = dashboardUsers.find(u => u.id === notif.sender_id);
                                          if (user) {
                                            startNewDashboardChat(user);
                                            markNotificationRead(notif.id);
                                            setShowNotificationsPanel(false);
                                          }
                                        }}
                                        className="px-2 py-1 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded transition-colors"
                                      >
                                        Chat
                                      </button>
                                      <button
                                        onClick={() => deleteNotification(notif.id)}
                                        className="p-1 hover:bg-gray-700 rounded transition-colors"
                                      >
                                        <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="px-4 py-4 text-center text-gray-500 text-sm">
                              No chat invites yet
                            </div>
                          )}
                        </div>
                      )}
                      
                      {dashboardUsers.length > 0 ? (
                        dashboardUsers.map((user) => (
                          <div
                            key={user.id}
                            className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors border-b border-gray-800 ${
                              selectedDashboardUser?.id === user.id 
                                ? 'bg-gray-800' 
                                : 'hover:bg-gray-900'
                            }`}
                          >
                            <div 
                              className="flex items-center gap-3 flex-1 min-w-0"
                              onClick={() => startNewDashboardChat(user)}
                            >
                              {user.avatar ? (
                                <img src={user.avatar} alt={user.name} className="w-10 h-10 rounded-full object-cover flex-shrink-0" />
                              ) : (
                                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-green-500 to-teal-500 flex items-center justify-center text-white font-semibold flex-shrink-0">
                                  {user.name?.charAt(0)?.toUpperCase() || '?'}
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <span className="font-medium text-white truncate block">{user.name}</span>
                                <span className="text-sm text-gray-400 truncate block">{user.email}</span>
                              </div>
                            </div>
                            {/* Invite Button */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                sendChatInvite(user.id, user.name);
                              }}
                              disabled={sendingInvite === user.id}
                              className="px-2 py-1.5 bg-blue-600/20 hover:bg-blue-600/40 text-blue-400 text-xs rounded-lg transition-colors flex items-center gap-1 disabled:opacity-50"
                              title="Send chat invite notification"
                            >
                              {sendingInvite === user.id ? (
                                <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                              ) : (
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                                </svg>
                              )}
                              Invite
                            </button>
                          </div>
                        ))
                      ) : (
                        <div className="flex flex-col items-center justify-center py-12 text-center px-4">
                          <div className="w-16 h-16 rounded-full bg-gray-800 flex items-center justify-center mb-4">
                            <svg className="w-8 h-8 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                          </div>
                          <p className="text-gray-400 text-sm">No other team members found</p>
                          <p className="text-gray-500 text-xs mt-1">Invite others to chat with them here</p>
                        </div>
                      )}
                        </>
                      )} {/* end !teamMembersCollapsed */}
                    </div>
                  </div>
                ) : (
                  <>
                {/* Category Tabs - Using Refactored Component */}
                <MessageCategoryBar
                  categories={messageCategories}
                  activeCategory={activeCategory}
                  onCategoryChange={(cat) => {
                    setActiveCategory(cat);
                    if (activeCategory !== cat) {
                      setSelectedConversation(null);
                    }
                  }}
                  onEditClick={() => openCategoryModal('messages')}
                  formatLabel={formatCategoryName}
                  onScan={async () => {
                    toast.loading('Scanning messages for categories...');
                    try {
                      const updatedConvs = conversations.map(conv => {
                        const messages = conv.messages || [];
                        let bestCategory = null;
                        let bestScore = 0;
                        
                        for (const cat of messageCategories) {
                          const keywords = customCategoryKeywords[cat] || generateKeywordsFromCategory(cat);
                          for (const msg of messages) {
                            const text = (msg.text || msg.content || '').toLowerCase();
                            for (const keyword of keywords) {
                              if (text.includes(keyword.toLowerCase())) {
                                const score = keyword.length;
                                if (score > bestScore) {
                                  bestScore = score;
                                  bestCategory = cat;
                                }
                              }
                            }
                          }
                        }
                        
                        if (bestCategory && bestCategory !== conv.messageCategory) {
                          return { ...conv, messageCategory: bestCategory };
                        }
                        return conv;
                      });
                      
                      setConversations(updatedConvs);
                      const categorized = updatedConvs.filter(c => c.messageCategory).length;
                      toast.dismiss();
                      toast.success(`Scanned ${conversations.length} conversations. ${categorized} matched categories.`);
                    } catch (err) {
                      toast.dismiss();
                      toast.error('Failed to scan messages');
                    }
                  }}
                />

                {/* Conversation List */}
                <div className="flex-1 overflow-y-auto">
                  {activeCategory === 'all' ? (
                    /* ── ALL TAB: conversation rows + dashboard DMs ── */
                    filteredConversations.length === 0 && dashboardConversations.length === 0 ? (
                      <EmptyState
                        icon="messages"
                        title="No conversations yet"
                        description="Your conversations will appear here"
                      />
                    ) : (
                      <>
                        {/* Dashboard Direct Messages */}
                        {dashboardConversations.length > 0 && (
                          <div>
                            <div className="px-4 py-1.5 text-xs font-semibold text-gray-500 uppercase bg-gray-900/30 border-b border-gray-800">
                              Direct Messages
                            </div>
                            {dashboardConversations.map((conv) => (
                              <div
                                key={conv.id}
                                onClick={() => {
                                  const user = { id: conv.user_id, name: conv.user_name, email: '' };
                                  startNewDashboardChat(user);
                                }}
                                className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors border-b border-gray-800 ${
                                  selectedDashboardUser?.id === conv.user_id ? 'bg-gray-800' : 'hover:bg-gray-900'
                                }`}
                              >
                                {conv.user_avatar ? (
                                  <img src={conv.user_avatar} alt={conv.user_name} className="w-10 h-10 rounded-full object-cover flex-shrink-0" />
                                ) : (
                                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center text-white font-semibold flex-shrink-0">
                                    {conv.user_name?.charAt(0)?.toUpperCase() || '?'}
                                  </div>
                                )}
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center justify-between">
                                    <span className="font-medium text-white truncate">{conv.user_name}</span>
                                    {conv.unread_count > 0 && (
                                      <span className="px-2 py-0.5 bg-purple-500 text-white text-xs rounded-full ml-2">
                                        {conv.unread_count}
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-sm text-gray-400 truncate">{conv.last_message}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* SMS / Phone Conversations */}
                        {filteredConversations.map((conv) => (
                          <ConversationListItemCard
                            key={conv.id}
                            conversation={conv}
                            isSelected={selectedConversation?.id === conv.id}
                            isPinned={pinnedConversations[conv.id]}
                            isDropdownOpen={openMessageDropdown === `conv-${conv.id}`}
                            onSelect={(c) => handleConversationClick(c)}
                            onToggleDropdown={() => setOpenMessageDropdown(openMessageDropdown === `conv-${conv.id}` ? null : `conv-${conv.id}`)}
                            onCloseDropdown={() => setOpenMessageDropdown(null)}
                            onTogglePin={() => handleTogglePin(conv.id)}
                            onAddToMeeting={() => handleAddToMeeting(conv)}
                            onNewMeeting={() => handleNewMeeting(conv)}
                            onAddContact={() => handleAddContactFromDropdown(conv)}
                            onDelete={() => handleDeleteConversation(conv.phone || conv.id)}
                            formatTimestamp={formatTimestamp}
                            getAccountCategories={getAccountCategories}
                            getCategoryBadge={getCategoryBadge}
                            getContactAvatar={getContactAvatar}
                          />
                        ))}
                      </>
                    )
                  ) : (
                    /* ── CATEGORY TABS: each individual message as its own row ── */
                    categorizedMessages.length === 0 ? (
                      <EmptyState
                        icon="messages"
                        title={`No ${activeCategory} messages`}
                        description="Messages in this category will appear here as conversations are scanned"
                      />
                    ) : (
                      categorizedMessages.map((catMsg) => (
                        <CategorizedMessageItem
                          key={catMsg.id}
                          message={catMsg}
                          isSelected={selectedConversation?.id === catMsg.conversationId}
                          onSelect={(msg) => {
                            isManualSelection.current = true;
                            setSelectedConversation(msg.originalConversation);
                            if (msg.originalConversation?.unread > 0) {
                              setConversations(prevConvs =>
                                prevConvs.map(c =>
                                  c.id === msg.originalConversation.id ? { ...c, unread: 0 } : c
                                )
                              );
                              apiCall(`/messages/mark-read/${encodeURIComponent(msg.originalConversation.phone)}`, {
                                method: 'POST'
                              }).catch(() => {});
                            }
                          }}
                          formatTimestamp={formatTimestamp}
                          getCategoryBadge={getCategoryBadge}
                        />
                      ))
                    )
                  )}
                </div>
                  </>
                )}
              </div>

              {/* Right Side - Chat View - Hidden on mobile when list is shown */}
              {(activeMessagesCategory === 'dashboard' || selectedDashboardUser) ? (
                /* Dashboard Chat View */
                <div className={`${mobileShowChat ? 'flex' : 'hidden md:flex'} flex-1 flex-col bg-black overflow-hidden`}>
                  {selectedTeam ? (
                    /* ── Team Group Chat ── */
                    <>
                      {/* Team Chat Header */}
                      <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-400/20 flex-shrink-0">
                        <button
                          onClick={() => { setMobileShowChat(false); setSelectedTeam(null); }}
                          className="md:hidden p-1 hover:bg-gray-800 rounded"
                        >
                          <ChevronLeft size={20} className="text-white" />
                        </button>
                        {(() => {
                          const colorMap = { purple: 'from-purple-500 to-purple-700', blue: 'from-blue-500 to-blue-700', green: 'from-green-500 to-green-700', red: 'from-red-500 to-red-700', orange: 'from-orange-500 to-orange-700', cyan: 'from-cyan-500 to-cyan-700', pink: 'from-pink-500 to-pink-700' };
                          const grad = colorMap[selectedTeam.color] || colorMap.purple;
                          return (
                            <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${grad} flex items-center justify-center text-white font-bold text-lg flex-shrink-0`}>#</div>
                          );
                        })()}
                        <div className="min-w-0 flex-1">
                          <div className="font-semibold text-white truncate" data-testid="team-chat-header-name">{selectedTeam.name}</div>
                          <div className="text-xs text-gray-400">{selectedTeam.member_count || (selectedTeam.member_user_ids?.length || 0)} members</div>
                        </div>
                        <button
                          onClick={() => { setSelectedTeam(null); setTeamMessages([]); }}
                          className="hidden md:flex p-1.5 hover:bg-gray-800 rounded-lg text-gray-400 hover:text-white transition-colors"
                          title="Close"
                        >
                          <X size={18} />
                        </button>
                      </div>

                      {/* Team Messages */}
                      <div className="flex-1 overflow-y-auto p-4 space-y-3">
                        {dashboardLoading ? (
                          <div className="flex items-center justify-center h-full">
                            <Loader2 size={24} className="animate-spin text-purple-500" />
                          </div>
                        ) : teamMessages.length > 0 ? (
                          teamMessages.map((msg) => {
                            const isMe = msg.sender_id === userId;
                            return (
                              <div key={msg.message_id} className={`flex ${isMe ? 'justify-end' : 'justify-start'} gap-2`}>
                                {!isMe && (
                                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0 self-end">
                                    {msg.sender_avatar
                                      ? <img src={msg.sender_avatar} alt={msg.sender_name} className="w-full h-full rounded-full object-cover" />
                                      : (msg.sender_name?.charAt(0)?.toUpperCase() || '?')}
                                  </div>
                                )}
                                <div className={`max-w-[70%] ${isMe ? 'items-end' : 'items-start'} flex flex-col`}>
                                  {!isMe && <span className="text-xs text-gray-500 mb-1 ml-1">{msg.sender_name}</span>}
                                  <div className={`px-4 py-2 rounded-2xl ${isMe ? 'bg-purple-600 text-white rounded-br-md' : 'bg-gray-800 text-white rounded-bl-md'}`}>
                                    <p className="break-words">{msg.message}</p>
                                    <p className={`text-xs mt-1 ${isMe ? 'text-purple-200' : 'text-gray-400'}`}>
                                      {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            );
                          })
                        ) : (
                          <div className="flex flex-col items-center justify-center h-full text-center">
                            <div className="w-16 h-16 rounded-full bg-gray-800 flex items-center justify-center mb-4">
                              <MessageSquare size={32} className="text-gray-600" />
                            </div>
                            <p className="text-gray-400">No messages yet</p>
                            <p className="text-gray-500 text-sm mt-1">Be the first to say something!</p>
                          </div>
                        )}
                      </div>

                      {/* Team Message Input */}
                      <div className="p-4 border-t border-gray-400/20 flex-shrink-0">
                        <div className="flex gap-2">
                          <input
                            data-testid="team-message-input"
                            type="text"
                            value={teamMessageInput}
                            onChange={(e) => setTeamMessageInput(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && sendTeamMessage()}
                            placeholder={`Message #${selectedTeam.name}...`}
                            className="flex-1 px-4 py-2 bg-gray-800 border border-gray-700 rounded-full text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
                          />
                          <button
                            data-testid="team-send-button"
                            onClick={sendTeamMessage}
                            disabled={!teamMessageInput.trim()}
                            className="px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-full text-white transition-colors"
                          >
                            <Send size={18} />
                          </button>
                        </div>
                      </div>
                    </>
                  ) : selectedDashboardUser ? (
                    <>
                      {/* Chat Header */}
                      <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-400/20 flex-shrink-0">
                        <button
                          onClick={() => { setMobileShowChat(false); setSelectedDashboardUser(null); }}
                          className="md:hidden p-1 hover:bg-gray-800 rounded"
                        >
                          <ChevronLeft size={20} className="text-white" />
                        </button>
                        <div className="min-w-0 flex-1">
                          <div className="font-medium text-white truncate">{selectedDashboardUser.name}</div>
                          <div className="text-sm text-gray-400 truncate">{selectedDashboardUser.email}</div>
                        </div>
                      </div>
                      
                      {/* Messages */}
                      <div className="flex-1 overflow-y-auto p-4 space-y-3">
                        {dashboardLoading ? (
                          <div className="flex items-center justify-center h-full">
                            <Loader2 size={24} className="animate-spin text-purple-500" />
                          </div>
                        ) : dashboardMessages.length > 0 ? (
                          dashboardMessages.map((msg) => {
                            const msgCategory = detectMessageCategory(msg.content || '');
                            const msgBadge = getCategoryBadge(msgCategory);
                            const dropdownKey = `dm-${msg.id}`;
                            return (
                            <div
                              key={msg.id}
                              className={`flex ${msg.is_mine ? 'justify-end' : 'justify-start'}`}
                            >
                              <div className={`flex flex-col ${msg.is_mine ? 'items-end' : 'items-start'} max-w-[70%] group relative`}>
                                {/* Category Badge */}
                                {msgBadge && (
                                  <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold mb-1 ${msgBadge.bg} ${msgBadge.text}`}>
                                    {msgBadge.label}
                                  </span>
                                )}
                              <div className="relative">
                              <div
                                className={`px-4 py-2 rounded-2xl ${
                                  msg.is_mine
                                    ? 'bg-purple-600 text-white rounded-br-md'
                                    : 'bg-gray-800 text-white rounded-bl-md'
                                }`}
                              >
                                {/* Attachment rendering */}
                                {msg.attachment_data && (
                                  <div className="mb-2">
                                    {msg.attachment_type?.startsWith('image/') ? (
                                      <img
                                        src={msg.attachment_data}
                                        alt={msg.attachment_name || 'attachment'}
                                        className="max-w-full rounded-lg max-h-64 object-contain cursor-pointer"
                                        onClick={() => window.open(msg.attachment_data, '_blank')}
                                      />
                                    ) : (
                                      <a
                                        href={msg.attachment_data}
                                        download={msg.attachment_name || 'attachment'}
                                        className="flex items-center gap-2 px-3 py-2 bg-black/20 rounded-lg hover:bg-black/30 transition-colors"
                                      >
                                        <Paperclip size={14} />
                                        <span className="text-sm truncate max-w-[200px]">{msg.attachment_name || 'Download file'}</span>
                                      </a>
                                    )}
                                  </div>
                                )}
                                {msg.content && <p className="break-words">{msg.content}</p>}
                                <p className={`text-xs mt-1 ${msg.is_mine ? 'text-purple-200' : 'text-gray-400'}`}>
                                  {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </p>
                              </div>

                              {/* Kebab dropdown trigger (hover) */}
                              <button
                                onClick={() => setOpenMessageDropdown(openMessageDropdown === dropdownKey ? null : dropdownKey)}
                                className={`absolute ${msg.is_mine ? 'left-0 -translate-x-8' : 'right-0 translate-x-8'} top-0 p-1 bg-gray-800 hover:bg-gray-700 rounded-full opacity-0 group-hover:opacity-100 transition-opacity`}
                                data-testid={`dm-message-menu-${msg.id}`}
                              >
                                <MoreVertical size={16} className="text-gray-300" />
                              </button>

                              {/* Dropdown menu */}
                              {openMessageDropdown === dropdownKey && (
                                <div className={`absolute ${msg.is_mine ? 'left-0' : 'right-0'} top-8 w-48 bg-gray-900 border border-gray-400/20 rounded-lg shadow-xl z-20`}>
                                  <button
                                    onClick={() => setOpenMessageDropdown(null)}
                                    className="w-full text-left px-4 py-2.5 hover:bg-gray-800 text-sm text-white flex items-center gap-2 border-b border-gray-400/10"
                                  >
                                    <Reply size={16} className="text-purple-400" />
                                    Reply
                                  </button>
                                  <button
                                    onClick={() => {
                                      const conv = dashboardUserAsConv();
                                      if (conv) handleAddToMeeting(conv);
                                    }}
                                    className="w-full text-left px-4 py-2.5 hover:bg-gray-800 text-sm text-white flex items-center gap-2 border-b border-gray-400/10"
                                  >
                                    <Calendar size={16} className="text-purple-400" />
                                    Add Meeting
                                  </button>
                                  <button
                                    onClick={() => {
                                      const conv = dashboardUserAsConv();
                                      if (conv) handleNewMeeting(conv);
                                    }}
                                    className="w-full text-left px-4 py-2.5 hover:bg-gray-800 text-sm text-white flex items-center gap-2 border-b border-gray-400/10"
                                  >
                                    <CalendarPlus size={16} className="text-purple-400" />
                                    New Meeting
                                  </button>
                                  <button
                                    onClick={() => openAddContactFromDashboardUser({ clear: false })}
                                    className="w-full text-left px-4 py-2.5 hover:bg-gray-800 text-sm text-white flex items-center gap-2 border-b border-gray-400/10"
                                  >
                                    <UserCheck size={16} className="text-purple-400" />
                                    Add Contact
                                  </button>
                                  <button
                                    onClick={() => openAddContactFromDashboardUser({ clear: true })}
                                    className="w-full text-left px-4 py-2.5 hover:bg-gray-800 text-sm text-white flex items-center gap-2 border-b border-gray-400/10"
                                  >
                                    <UserPlus size={16} className="text-purple-400" />
                                    New Contact
                                  </button>
                                  <button
                                    onClick={() => deleteDashboardMessage(msg.id)}
                                    className="w-full text-left px-4 py-2.5 hover:bg-red-900 text-sm text-red-400 flex items-center gap-2"
                                  >
                                    <Trash2 size={16} className="text-red-400" />
                                    Delete Message
                                  </button>
                                </div>
                              )}
                              </div>
                              </div>
                            </div>
                            );
                          })
                        ) : (
                          <div className="flex flex-col items-center justify-center h-full text-center">
                            <div className="w-16 h-16 rounded-full bg-gray-800 flex items-center justify-center mb-4">
                              <MessageCircle size={32} className="text-gray-600" />
                            </div>
                            <p className="text-gray-400">No messages yet</p>
                            <p className="text-gray-500 text-sm mt-1">Start the conversation!</p>
                          </div>
                        )}
                        <div ref={dashboardMessagesEndRef} />
                      </div>
                      
                      {/* Message Input */}
                      <div className="p-4 border-t border-gray-400/20 flex-shrink-0">
                        {/* Attachment preview */}
                        {dashboardAttachment && (
                          <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-800 border border-gray-700 rounded-lg mb-2">
                            <Paperclip size={13} className="text-purple-400" />
                            <span className="text-xs text-gray-300 truncate flex-1">{dashboardAttachment.name}</span>
                            <button onClick={() => setDashboardAttachment(null)} className="p-0.5 hover:bg-gray-700 rounded-full">
                              <X size={12} className="text-gray-400" />
                            </button>
                          </div>
                        )}
                        {/* Emoji picker popup */}
                        <div className="relative">
                          {showDashboardEmojiPicker && (
                            <div
                              ref={dashboardEmojiPickerRef}
                              className="absolute bottom-full mb-2 left-0 z-50 bg-[#111827] border border-gray-700 rounded-xl shadow-2xl p-3 w-72"
                              data-testid="dashboard-emoji-picker"
                            >
                              {[
                                { label: 'Smileys', emojis: ['😀','😂','😍','🥰','😊','😎','🤩','😅','🤣','😭','😤','🥺','😏','🤔','😬','🙄','😴','🤯','🥳','😇'] },
                                { label: 'Gestures', emojis: ['👍','👎','👏','🙌','🤝','👋','✌️','🤞','💪','🤙','👌','🤟','✋','🖐️','☝️','🤜','🤛','🫶','🫂','🤲'] },
                                { label: 'Hearts', emojis: ['❤️','🧡','💛','💚','💙','💜','🖤','🤍','💔','❤️‍🔥','💕','💞','💓','💗','💖','💘','💝','🫀','♥️','❣️'] },
                                { label: 'Objects', emojis: ['🎉','🎊','🔥','✨','⚡','🌟','💫','🎯','🎁','📱','💻','📧','📅','🗓️','⏰','💡','🔑','🏆','💎','🚀'] },
                              ].map(({ label, emojis }) => (
                                <div key={label} className="mb-2">
                                  <p className="text-xs text-gray-500 mb-1 font-medium">{label}</p>
                                  <div className="flex flex-wrap gap-0.5">
                                    {emojis.map(e => (
                                      <button
                                        key={e}
                                        type="button"
                                        onClick={() => {
                                          setDashboardMessageInput(prev => prev + e);
                                          setShowDashboardEmojiPicker(false);
                                        }}
                                        className="text-xl hover:bg-gray-700 rounded p-0.5 transition-colors leading-none"
                                      >
                                        {e}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                          <div className="flex items-center gap-1 bg-gray-800 border border-gray-700 rounded-full px-3 py-1">
                            {/* Attachment */}
                            <button
                              type="button"
                              onClick={() => dashboardAttachmentInputRef.current?.click()}
                              className={`p-1.5 hover:bg-gray-700 rounded-full transition-colors ${dashboardAttachment ? 'text-purple-400' : 'text-gray-400'}`}
                              title="Attach file"
                              data-testid="dashboard-attach-btn"
                            >
                              <Paperclip size={16} />
                            </button>
                            {/* Emoji */}
                            <button
                              type="button"
                              onClick={() => setShowDashboardEmojiPicker(prev => !prev)}
                              className={`p-1.5 hover:bg-gray-700 rounded-full transition-colors ${showDashboardEmojiPicker ? 'text-purple-400' : 'text-gray-400'}`}
                              title="Add emoji"
                              data-testid="dashboard-emoji-btn"
                            >
                              <Smile size={16} />
                            </button>
                            {/* AI Generator */}
                            <button
                              type="button"
                              onClick={async () => {
                                if (!selectedDashboardUser) return;
                                const prevInput = dashboardMessageInput;
                                setDashboardMessageInput('Generating...');
                                try {
                                  const lastMsg = dashboardMessages.filter(m => !m.is_mine).slice(-1)[0];
                                  const messageContent = lastMsg?.content || 
                                    `Please write a professional follow-up message to ${selectedDashboardUser.name || selectedDashboardUser.username}.`;
                                  const contextStr = dashboardMessages.slice(-5)
                                    .map(m => `${m.is_mine ? 'You' : (selectedDashboardUser.name || selectedDashboardUser.username)}: ${m.content}`)
                                    .join('\n');
                                  const result = await aiEmailAPI.generateMessageReply({
                                    sender_name: selectedDashboardUser.name || selectedDashboardUser.username || 'Team Member',
                                    message_content: messageContent,
                                    context: contextStr || `Dashboard internal message to team member ${selectedDashboardUser.name}`,
                                    reply_tone: 'professional'
                                  });
                                  setDashboardMessageInput(result?.reply || result?.text || result?.message || prevInput);
                                } catch {
                                  setDashboardMessageInput(prevInput);
                                }
                              }}
                              className="p-1.5 hover:bg-gray-700 rounded-full transition-colors text-gray-400 hover:text-purple-400"
                              title="Generate AI message"
                              data-testid="dashboard-ai-btn"
                            >
                              <Sparkles size={16} />
                            </button>
                            {/* Divider */}
                            <div className="w-px h-5 bg-gray-700 mx-1 self-end mb-1.5" />
                            {/* Text input — textarea so it grows with content */}
                            <textarea
                              data-testid="dashboard-message-input"
                              value={dashboardMessageInput}
                              onChange={(e) => {
                                setDashboardMessageInput(e.target.value);
                                e.target.style.height = 'auto';
                                e.target.style.height = Math.min(e.target.scrollHeight, 160) + 'px';
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                  e.preventDefault();
                                  sendDashboardMessage();
                                }
                              }}
                              placeholder="Type a message..."
                              rows={2}
                              className="flex-1 bg-transparent text-white placeholder-gray-500 focus:outline-none text-sm py-1 resize-none leading-relaxed"
                              style={{ minHeight: '44px', maxHeight: '160px' }}
                            />
                            {/* Send */}
                            <button
                              data-testid="dashboard-send-button"
                              onClick={sendDashboardMessage}
                              disabled={!dashboardMessageInput.trim() && !dashboardAttachment}
                              className="p-1.5 bg-purple-600 hover:bg-purple-700 disabled:opacity-40 disabled:cursor-not-allowed rounded-full text-white transition-colors ml-1"
                            >
                              <Send size={14} />
                            </button>
                          </div>
                        </div>
                        {/* Hidden inputs */}
                        <input ref={dashboardAttachmentInputRef} type="file" className="hidden"
                          onChange={(e) => { const f = e.target.files?.[0]; if (f) setDashboardAttachment(f); }}
                        />
                      </div>
                    </>
                  ) : (
                    <div className="flex-1 flex flex-col items-center justify-center text-center p-4">
                      <div className="w-20 h-20 rounded-full bg-gray-800 flex items-center justify-center mb-4">
                        <MessageCircle size={40} className="text-gray-600" />
                      </div>
                      <h3 className="text-xl font-semibold text-white mb-2">Dashboard Messages</h3>
                      <p className="text-gray-400 max-w-sm">
                        Select a team or a team member from the list to start a conversation.
                      </p>
                    </div>
                  )}
                </div>
              ) : (
              /* Regular Messages Chat View */
              <div className={`${mobileShowChat ? 'flex' : 'hidden md:flex'} flex-1 flex-col bg-black overflow-hidden`}>
                {!selectedConversation ? (
                  <EmptyState
                    icon="messages"
                    title="Select a conversation"
                    description="Choose a conversation from the list to view messages"
                  />
                ) : (
                  <>
                {/* Chat Header */}
                <div className="px-3 md:px-6 py-3 md:py-4 border-b border-gray-400/20 bg-black flex-shrink-0">
                  <div className="flex items-center justify-between gap-2">
                    {/* Left side - Back button, avatar, name */}
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      {/* Back Button - Mobile Only */}
                      <button
                        onClick={() => setMobileShowChat(false)}
                        className="md:hidden p-1.5 hover:bg-gray-800 rounded-lg transition-colors flex-shrink-0"
                      >
                        <ChevronLeft size={20} className="text-white" />
                      </button>
                      
                      
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1 md:gap-2 flex-wrap">
                          <h3 className="text-sm md:text-lg font-bold text-white truncate">
                            {selectedConversation.name}
                          </h3>
                          {/* Category Badge(s) in Header - Shows Personal, Business, or both if merged */}
                          {(() => {
                            const categories = getAccountCategories(selectedConversation);
                            return categories.map((category, idx) => {
                              const badge = getCategoryBadge(category);
                              return (
                                <span key={idx} className={`hidden sm:inline-block px-2 py-0.5 rounded-full text-[10px] md:text-xs font-medium ${badge.bg} ${badge.text}`}>
                                  {badge.label}
                                </span>
                              );
                            });
                          })()}
                        </div>
                        <p className="text-[10px] md:text-xs text-gray-400 truncate">
                          {selectedConversation.phone}
                        </p>
                      </div>
                    </div>
                    
                    {/* Right side - Action buttons */}
                    <div className="flex items-center gap-1 md:gap-3 flex-shrink-0">
                      {/* Add Contact Button - Icon only on mobile */}
                      {!selectedConversation.isGroup && (
                        <button
                          onClick={async () => {
                            // Pre-fill the form with current conversation data
                            setNewContactName(selectedConversation.name);
                            setNewContactPhone(selectedConversation.phone);
                            setNewContactEmail('');
                            setNewContactCompany('');
                            setNewContactRole('');
                            setNewContactCategory(getLastCategoryInThread(selectedConversation) === 'business' ? 'Business' : 'Personal');
                            setNewContactNotes('Added from text message');
                            setOriginalConversationName(selectedConversation.name);
                            setShowAddContactModal(true);
                            
                            // Auto-scan if the name looks like a phone number (not a real name)
                            const nameIsPhoneNumber = /^[+\d\s()-]+$/.test(selectedConversation.name);
                            if (nameIsPhoneNumber && selectedConversation.phone) {
                              try {
                                setContactAutoScanning(true);
                                const response = await apiCall(`/contacts/scan-icloud/${encodeURIComponent(selectedConversation.phone)}`);
                                
                                if (response.found) {
                                  setNewContactName(response.name || selectedConversation.name);
                                  setNewContactEmail(response.email || '');
                                  setNewContactCompany(response.company || '');
                                  console.log(`✅ Auto-populated contact: ${response.name}`);
                                }
                              } catch (error) {
                                console.error('Auto scan error:', error);
                              } finally {
                                setContactAutoScanning(false);
                              }
                            }
                          }}
                          className="p-1.5 md:px-3 md:py-1.5 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-1 md:gap-2"
                          title="Add to contacts"
                        >
                          <UserPlus size={16} />
                          <span className="hidden md:inline">Add Contact</span>
                        </button>
                      )}
                      <button 
                        onClick={() => setShowConversationSearch(!showConversationSearch)}
                        className={`hidden sm:block p-1.5 md:p-2 hover:bg-gray-900 rounded-full transition-colors ${showConversationSearch ? 'bg-gray-800' : ''}`}
                        title="Search in conversation"
                      >
                        <Search size={18} className={showConversationSearch ? 'text-purple-400' : 'text-gray-400'} />
                      </button>
                      <button className="p-1.5 md:p-2 hover:bg-gray-900 rounded-full transition-colors">
                        <Phone size={18} className="text-gray-400" />
                      </button>
                      <button 
                        onClick={() => handleDeleteConversation(selectedConversation.phone || selectedConversation.id)}
                        className="hidden sm:block p-1.5 md:p-2 hover:bg-red-900 rounded-full transition-colors"
                        title="Delete conversation"
                      >
                        <Trash2 size={18} className="text-red-400 hover:text-red-300" />
                      </button>
                      <button className="p-1.5 md:p-2 hover:bg-gray-900 rounded-full transition-colors">
                        <MoreVertical size={18} className="text-gray-400" />
                      </button>
                    </div>
                  </div>
                  
                  {/* Conversation Search Bar */}
                  {showConversationSearch && (
                    <div className="px-4 py-2 bg-gray-900 border-b border-gray-700">
                      <div className="relative">
                        <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        <input
                          type="text"
                          placeholder="Search messages in this conversation..."
                          value={conversationSearchQuery}
                          onChange={(e) => setConversationSearchQuery(e.target.value)}
                          className="w-full pl-10 pr-10 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 text-sm"
                          autoFocus
                        />
                        {conversationSearchQuery && (
                          <button
                            onClick={() => setConversationSearchQuery('')}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                          >
                            <X size={16} />
                          </button>
                        )}
                      </div>
                      {conversationSearchQuery && (
                        <p className="text-xs text-gray-400 mt-1">
                          {selectedConversation?.messages?.filter(m => 
                            (m.text || m.content || '').toLowerCase().includes(conversationSearchQuery.toLowerCase())
                          ).length || 0} matches found
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {/* Messages Area */}
                <div ref={messagesContainerRef} className="flex-1 overflow-y-auto p-3 md:p-6 space-y-4">
                  {loading && (
                    <LoadingSpinner message="Loading messages..." />
                  )}
                  {!loading && !selectedConversation && (
                    <EmptyState icon="messages" title="Select a conversation" description="Choose from the list on the left" className="py-4" />
                  )}
                  {!loading && selectedConversation && !selectedConversation.messages?.length && selectedConversation.messagesLoading && (
                    <LoadingSpinner message="Loading messages..." />
                  )}
                  {!loading && selectedConversation && selectedConversation.messages?.length === 0 && !selectedConversation.messagesLoading && (
                    <div className="text-center py-8">
                      {selectedConversation.source === 'mac_sync' ? (
                        <div className="space-y-3">
                          <div className="text-gray-400 text-sm">
                            <RefreshCw className="w-8 h-8 mx-auto mb-3 text-gray-500" />
                            <p className="font-medium">Messages not synced for this conversation</p>
                            <p className="text-xs mt-2 text-gray-500">
                              {selectedConversation.lastMessage ? (
                                <>Last message preview: "{selectedConversation.lastMessage.substring(0, 50)}..."</>
                              ) : (
                                <>The message history exists on your Mac but wasn't included in the last sync.</>
                              )}
                            </p>
                          </div>
                          <p className="text-xs text-gray-500">
                            Re-run the sync script with a higher limit to include older messages:<br/>
                            <code className="bg-gray-800 px-2 py-1 rounded text-purple-400 text-[10px]">
                              python3 mac_sync.py --sync --limit 500 --category {selectedConversation.category || 'business'}
                            </code>
                          </p>
                        </div>
                      ) : (
                        <EmptyState 
                          icon="messages"
                          title="No messages yet"
                          description="Start the conversation!"
                          className="py-4"
                        />
                      )}
                    </div>
                  )}
                  {!loading && selectedConversation && selectedConversation.messages?.length > 0 && [...selectedConversation.messages]
                    .sort((a, b) => {
                      // Sort by timestamp chronologically (oldest first)
                      const timeA = new Date(a.timestamp || a.time || 0).getTime();
                      const timeB = new Date(b.timestamp || b.time || 0).getTime();
                      return timeA - timeB;
                    })
                    .filter(msg => {
                      // If no search query, show all messages
                      if (!conversationSearchQuery) return true;
                      // Filter messages that match the search query
                      const msgText = (msg.text || msg.content || msg.preview || '').toLowerCase();
                      return msgText.includes(conversationSearchQuery.toLowerCase());
                    })
                    .map((msg, idx) => {
                    const messageKey = `${selectedConversation.id}-${idx}`;
                    if (deletedMessages[messageKey]) return null;
                    
                    // Determine if this is the user's own message
                    const isOwn = msg.from_name === 'You' || msg.sent === true || msg.is_sent === true || 
                                  (msg.sender && msg.sender.toLowerCase() === 'me');
                    // Detect category from message content - check persisted assignments first
                    const msgId = msg.id || msg.message_id || `${selectedConversation?.id}-${msg.timestamp}`;
                    const persistedMsgCategory = messageCategoryAssignments[msgId];
                    const detectedMsgCategory = detectMessageCategory(msg.text || msg.content || msg.preview);
                    const messageCategory = persistedMsgCategory || detectedMsgCategory;
                    const categoryBadge = getCategoryBadge(messageCategory);
                    
                    // Helper to highlight search matches
                    const highlightText = (text) => {
                      if (!text) return text;
                      
                      // First, handle the attachment placeholder character (￼)
                      // Replace it with a placeholder indicating an attachment
                      let processedText = text.replace(/￼/g, '📎 ');
                      
                      // URL regex pattern
                      const urlRegex = /(https?:\/\/[^\s<]+[^<.,:;"')\]\s])/g;
                      
                      // Split text by URLs
                      const parts = processedText.split(urlRegex);
                      
                      // Process each part
                      const result = parts.map((part, i) => {
                        // Check if this part is a URL
                        if (urlRegex.test(part)) {
                          // Reset regex lastIndex
                          urlRegex.lastIndex = 0;
                          return (
                            <a 
                              key={i}
                              href={part}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="text-blue-400 hover:text-blue-300 underline break-all"
                            >
                              {part.length > 50 ? part.substring(0, 50) + '...' : part}
                            </a>
                          );
                        }
                        
                        // Apply search highlighting if there's a search query
                        if (conversationSearchQuery) {
                          const regex = new RegExp(`(${conversationSearchQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
                          const subParts = part.split(regex);
                          return subParts.map((subPart, j) => 
                            regex.test(subPart) ? <mark key={`${i}-${j}`} className="bg-yellow-500 text-black rounded px-0.5">{subPart}</mark> : subPart
                          );
                        }
                        
                        return part;
                      });
                      
                      return result;
                    };
                    
                    return (
                    <div key={msg.id || idx} className={`flex gap-3 ${isOwn ? 'justify-end' : 'justify-start'}`}>
                      <div className={`flex flex-col ${isOwn ? 'items-end' : 'items-start'} max-w-md relative`}>
                        
                        {/* Sender Name for Group Chats - show phone number/name of who sent the message */}
                        {!isOwn && (msg.sender || msg.from) && selectedConversation.phone?.startsWith('chat') && (
                          <span className="text-xs text-purple-400 font-medium mb-1 ml-1">
                            {msg.sender || msg.from}
                          </span>
                        )}
                        
                        {/* Message Container */}
                        <div className="relative group">
                          {/* Category Tag - styled like iMessage (gray pill, inside message bubble) - hide General */}
                          {categoryBadge && messageCategory !== 'general' && (
                            <div className={`inline-block px-3 py-1 rounded-full text-xs font-semibold mb-2 ${categoryBadge.bg} ${categoryBadge.text}`}>
                              {categoryBadge.label}
                            </div>
                          )}
                          
                          {/* Text Message */}
                          <div
                            onClick={() => setShowEmojiPicker(showEmojiPicker === `${selectedConversation.id}-${idx}` ? null : `${selectedConversation.id}-${idx}`)}
                            className={`rounded-2xl px-4 py-2.5 cursor-pointer ${
                              isOwn
                                ? 'bg-purple-600 text-white rounded-br-sm'
                                : 'bg-gray-800 text-white rounded-bl-sm'
                            }`}
                          >
                            <p className="text-sm whitespace-pre-wrap">{highlightText(msg.text || msg.content || msg.preview)}</p>
                            
                            {/* Show attachments if exists */}
                            {msg.attachments && msg.attachments.length > 0 && (
                              <div className="mt-2 space-y-2">
                                {msg.attachments.map((att, attIdx) => {
                                  const isImage = att.mime_type?.startsWith('image/');
                                  const isVideo = att.mime_type?.startsWith('video/');
                                  const isAudio = att.mime_type?.startsWith('audio/');
                                  const apiBase = process.env.REACT_APP_BACKEND_URL || '';
                                  const attUrl = att.url?.startsWith('/') ? `${apiBase}${att.url}` : att.url;
                                  
                                  if (isImage && attUrl) {
                                    return (
                                      <a 
                                        key={attIdx} 
                                        href={attUrl} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="block"
                                      >
                                        <img 
                                          src={attUrl} 
                                          alt={att.filename || 'Image'} 
                                          className="max-w-[300px] max-h-[300px] rounded-lg object-cover hover:opacity-90 transition-opacity"
                                          onError={(e) => {
                                            e.target.style.display = 'none';
                                            e.target.nextSibling.style.display = 'flex';
                                          }}
                                        />
                                        <div className="hidden items-center gap-2 text-xs text-gray-400 mt-1">
                                          <ImageIcon size={14} />
                                          <span>{att.filename || 'Image'}</span>
                                        </div>
                                      </a>
                                    );
                                  } else if (isVideo && attUrl) {
                                    return (
                                      <video 
                                        key={attIdx}
                                        src={attUrl} 
                                        controls 
                                        className="max-w-[300px] max-h-[200px] rounded-lg"
                                      >
                                        Your browser does not support video playback.
                                      </video>
                                    );
                                  } else if (isAudio && attUrl) {
                                    return (
                                      <audio 
                                        key={attIdx}
                                        src={attUrl} 
                                        controls 
                                        className="max-w-[250px]"
                                      >
                                        Your browser does not support audio playback.
                                      </audio>
                                    );
                                  } else {
                                    // Generic file attachment
                                    return (
                                      <a 
                                        key={attIdx}
                                        href={attUrl || '#'}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-2 bg-black/30 rounded-lg px-3 py-2 hover:bg-black/50 transition-colors"
                                      >
                                        <Paperclip size={16} className="text-purple-400" />
                                        <span className="text-sm truncate max-w-[200px]">{att.filename || 'Attachment'}</span>
                                        {att.size > 0 && (
                                          <span className="text-xs text-gray-500">
                                            ({(att.size / 1024).toFixed(1)} KB)
                                          </span>
                                        )}
                                      </a>
                                    );
                                  }
                                })}
                              </div>
                            )}
                            
                            {/* Show reaction if exists */}
                            {messageReactions[`${selectedConversation.id}-${idx}`] && (
                              <div className="mt-1 inline-block bg-black/30 rounded-full px-2 py-0.5 text-base">
                                {messageReactions[`${selectedConversation.id}-${idx}`]}
                              </div>
                            )}
                          </div>

                          {/* Emoji Picker */}
                          {showEmojiPicker === `${selectedConversation.id}-${idx}` && (
                            <div className={`absolute ${isOwn ? 'right-0' : 'left-0'} -bottom-12 bg-gray-900 border border-gray-400/20 rounded-lg shadow-xl p-2 flex gap-1 z-20`}>
                              {availableEmojis.map((emoji) => (
                                <button
                                  key={emoji}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleAddReaction(selectedConversation.id, idx, emoji);
                                  }}
                                  className="text-xl hover:bg-gray-800 rounded p-1 transition-colors"
                                >
                                  {emoji}
                                </button>
                              ))}
                            </div>
                          )}

                          {/* Voice Message */}
                          {msg.hasVoiceMessage && (
                            <div className={`rounded-2xl px-4 py-3 flex items-center gap-3 min-w-[200px] ${
                              isOwn
                                ? 'bg-purple-600'
                                : 'bg-gray-800'
                            }`}>
                              <button className="w-8 h-8 bg-black rounded-full flex items-center justify-center flex-shrink-0 hover:bg-gray-900 transition-colors">
                                <div className="w-0 h-0 border-t-4 border-t-transparent border-l-6 border-l-white border-b-4 border-b-transparent ml-0.5"></div>
                              </button>
                              <div className="flex-1 flex items-center gap-1">
                                {[2,4,3,5,4,6,5,7,6,8,7,6,5,4,3,2].map((height, i) => (
                                  <div key={i} className="w-1 bg-white rounded-full" style={{height: `${height * 2}px`}}></div>
                                ))}
                              </div>
                              <span className="text-xs text-white font-medium">{msg.voiceDuration}</span>
                            </div>
                          )}

                          {/* Dropdown Button - Appears on Hover */}
                          <button
                            onClick={() => setOpenMessageDropdown(openMessageDropdown === idx ? null : idx)}
                            className={`absolute ${isOwn ? 'left-0 -translate-x-8' : 'right-0 translate-x-8'} top-0 p-1 bg-gray-800 hover:bg-gray-700 rounded-full opacity-0 group-hover:opacity-100 transition-opacity`}
                          >
                            <MoreVertical size={16} className="text-gray-300" />
                          </button>

                          {/* Dropdown Menu */}
                          {openMessageDropdown === idx && (
                            <div className={`absolute ${isOwn ? 'left-0' : 'right-0'} top-8 w-48 bg-gray-900 border border-gray-400/20 rounded-lg shadow-xl z-10`}>
                              <button
                                onClick={() => {
                                  setOpenMessageDropdown(null);
                                }}
                                className="w-full text-left px-4 py-2.5 hover:bg-gray-800 text-sm text-white flex items-center gap-2 border-b border-gray-400/10"
                              >
                                <Reply size={16} className="text-purple-400" />
                                Reply
                              </button>
                              <button
                                onClick={() => {
                                  handleAddToMeeting(selectedConversation);
                                }}
                                className="w-full text-left px-4 py-2.5 hover:bg-gray-800 text-sm text-white flex items-center gap-2 border-b border-gray-400/10"
                              >
                                <Calendar size={16} className="text-purple-400" />
                                Add Meeting
                              </button>
                              <button
                                onClick={() => {
                                  handleNewMeeting(selectedConversation);
                                }}
                                className="w-full text-left px-4 py-2.5 hover:bg-gray-800 text-sm text-white flex items-center gap-2 border-b border-gray-400/10"
                              >
                                <CalendarPlus size={16} className="text-purple-400" />
                                New Meeting
                              </button>
                              <button
                                onClick={() => openAddContactFromConversation({ clear: false })}
                                className="w-full text-left px-4 py-2.5 hover:bg-gray-800 text-sm text-white flex items-center gap-2 border-b border-gray-400/10"
                              >
                                <UserCheck size={16} className="text-purple-400" />
                                Add Contact
                              </button>
                              <button
                                onClick={() => openAddContactFromConversation({ clear: true })}
                                className="w-full text-left px-4 py-2.5 hover:bg-gray-800 text-sm text-white flex items-center gap-2 border-b border-gray-400/10"
                              >
                                <UserPlus size={16} className="text-purple-400" />
                                New Contact
                              </button>
                              <button
                                onClick={() => {
                                  handleDeleteMessage(selectedConversation.id, idx);
                                }}
                                className="w-full text-left px-4 py-2.5 hover:bg-red-900 text-sm text-red-400 flex items-center gap-2"
                              >
                                <Trash2 size={16} className="text-red-400" />
                                Delete Message
                              </button>
                            </div>
                          )}
                        </div>

                        {/* Message Footer */}
                        <div className="flex items-center gap-2 mt-1">
                          {msg.reactions && (
                            <div className="flex gap-1">
                              {msg.reactions.map((reaction, i) => (
                                <span key={i} className="text-xs bg-gray-800 border border-gray-400/20 rounded-full px-2 py-0.5 flex items-center gap-1 text-white">
                                  {reaction.emoji} {reaction.count}
                                </span>
                              ))}
                            </div>
                          )}
                          {msg.readBy && (
                            <span className="text-xs text-gray-500 flex items-center gap-1">
                              <CheckCircle size={12} /> {msg.readBy}
                            </span>
                          )}
                          <span className="text-xs text-gray-500">{formatTimestamp(msg.time || msg.timestamp)}</span>
                        </div>
                      </div>

                      {isOwn && (
                        <div className="w-10"></div>
                      )}
                    </div>
                    );
                  })}
                  {/* Scroll anchor */}
                  <div ref={messagesEndRef} />
                </div>

                {/* Input Area */}
                <div className="px-3 md:px-6 py-3 md:py-4 border-t border-gray-400/20 bg-black flex-shrink-0">
                  {showAIDraftMessage && aiDraftMessageText && (
                    <div className="bg-purple-900/30 border border-purple-500/50 rounded-lg p-3 mb-3">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Sparkles size={16} className="text-purple-400" />
                          <span className="text-xs font-semibold text-purple-300">AI Suggested Message</span>
                        </div>
                        <button onClick={() => { setShowAIDraftMessage(false); setAiDraftMessageText(''); }} className="text-gray-400 hover:text-white">
                          <X size={14} />
                        </button>
                      </div>
                      <p className="text-sm text-white mb-2 whitespace-pre-wrap">{aiDraftMessageText}</p>
                      <div className="flex gap-2">
                        <button 
                          onClick={() => {
                            setMessageInput(aiDraftMessageText);
                            setShowAIDraftMessage(false);
                            setAiDraftMessageText('');
                          }}
                          className="text-xs bg-purple-600 hover:bg-purple-700 text-white px-3 py-1 rounded"
                        >
                          Use This Draft
                        </button>
                        <button 
                          onClick={async () => {
                            if (!selectedConversation || generatingAIReply) return;
                            setGeneratingAIReply(true);
                            try {
                              const conversationMessages = selectedConversation.messages || [];
                              const lastContactMessage = conversationMessages
                                .filter(m => m.from_name !== 'You')
                                .slice(-1)[0];
                              
                              if (lastContactMessage) {
                                const messageText = lastContactMessage.content || lastContactMessage.preview || lastContactMessage.text || '';
                                const result = await aiEmailAPI.generateMessageReply({
                                  conversation_id: selectedConversation.id,
                                  sender_name: selectedConversation.name,
                                  message_content: messageText,
                                  context: conversationMessages.slice(-5).map(m => `${m.from_name === 'You' ? 'You' : selectedConversation.name}: ${m.content || m.preview || m.text || ''}`).join('\n'),
                                  reply_tone: 'professional'
                                });
                                if (result.reply) {
                                  setAiDraftMessageText(result.reply);
                                }
                              }
                            } catch (err) {
                              console.error('Failed to regenerate AI reply:', err);
                            } finally {
                              setGeneratingAIReply(false);
                            }
                          }}
                          disabled={generatingAIReply}
                          className="text-xs bg-gray-700 hover:bg-gray-600 text-white px-3 py-1 rounded flex items-center gap-1"
                          title="Generate new draft"
                        >
                          <RefreshCw size={12} className={generatingAIReply ? 'animate-spin' : ''} />
                          {generatingAIReply ? 'Generating...' : 'Refresh'}
                        </button>
                      </div>
                    </div>
                  )}
                  <div className="flex items-start gap-1 md:gap-3">
                    {/* Hidden file input for attachments */}
                    <input
                      type="file"
                      ref={messageAttachmentInputRef}
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          // Check file size (max 10MB)
                          if (file.size > 10 * 1024 * 1024) {
                            alert('File too large. Maximum size is 10MB.');
                            return;
                          }
                          setMessageAttachment(file);
                          console.log('📎 Attached file:', file.name);
                        }
                      }}
                      className="hidden"
                      accept="image/*,.pdf,.doc,.docx,.txt"
                    />
                    <button 
                      onClick={() => messageAttachmentInputRef.current?.click()}
                      className={`p-1.5 md:p-2 hover:bg-gray-900 rounded-full transition-colors ${messageAttachment ? 'bg-purple-600/20' : ''}`}
                      title={messageAttachment ? `Attached: ${messageAttachment.name}` : "Attach file"}
                      data-testid="attach-file-btn"
                    >
                      <Paperclip size={18} className={messageAttachment ? 'text-purple-400' : 'text-gray-400'} />
                    </button>
                    {/* Emoji Picker Button */}
                    <button
                      type="button"
                      onClick={() => setShowMsgEmojiPicker(prev => !prev)}
                      className={`p-1.5 md:p-2 hover:bg-gray-900 rounded-full transition-colors ${showMsgEmojiPicker ? 'bg-purple-600/20' : ''}`}
                      title="Add emoji"
                      data-testid="emoji-btn"
                    >
                      <Smile size={18} className={showMsgEmojiPicker ? 'text-purple-400' : 'text-gray-400'} />
                    </button>
                    <button 
                      onClick={() => setShowScheduleModal(true)}
                      className="hidden sm:block p-1.5 md:p-2 hover:bg-gray-900 rounded-full transition-colors"
                      title="Schedule message"
                    >
                      <Clock size={18} className="text-gray-400" />
                    </button>
                    <button 
                      onClick={async () => {
                        if (!selectedConversation) return;
                        setGeneratingAIReply(true);
                        try {
                          // Get the conversation messages
                          const conversationMessages = selectedConversation.messages || [];
                          // Get the last message from the contact (not sent by user)
                          // Messages from API have from_name, not sent property
                          const lastContactMessage = conversationMessages
                            .filter(m => m.from_name !== 'You')
                            .slice(-1)[0];
                          
                          if (lastContactMessage) {
                            const messageText = lastContactMessage.content || lastContactMessage.preview || lastContactMessage.text || '';
                            const result = await aiEmailAPI.generateMessageReply({
                              conversation_id: selectedConversation.id,
                              sender_name: selectedConversation.name,
                              message_content: messageText,
                              context: conversationMessages.slice(-5).map(m => `${m.from_name === 'You' ? 'You' : selectedConversation.name}: ${m.content || m.preview || m.text || ''}`).join('\n'),
                              reply_tone: 'professional'
                            });
                            if (result.reply) {
                              setAiDraftMessageText(result.reply);
                              setShowAIDraftMessage(true);
                            }
                          } else {
                            // No messages from contact, use generic
                            setAiDraftMessageText('Thank you for reaching out. I\'d be happy to help. What can I assist you with?');
                            setShowAIDraftMessage(true);
                          }
                        } catch (err) {
                          console.error('Failed to generate AI reply:', err);
                          // Fallback
                          setAiDraftMessageText('Thank you for reaching out. I\'ve reviewed your request and would be happy to discuss this further. Let\'s schedule a meeting at your earliest convenience.');
                          setShowAIDraftMessage(true);
                        } finally {
                          setGeneratingAIReply(false);
                        }
                      }}
                      disabled={generatingAIReply}
                      className="p-1.5 md:p-2 hover:bg-gray-900 rounded-full transition-colors"
                      title="Generate AI Reply"
                      data-testid="message-ai-reply-btn"
                    >
                      {generatingAIReply ? (
                        <Loader2 size={18} className="text-purple-400 animate-spin" />
                      ) : (
                        <Sparkles size={18} className="text-purple-400" />
                      )}
                    </button>
                    <div className="flex-1 flex flex-col gap-2">
                      {/* Attachment Preview */}
                      {messageAttachment && (
                        <div className="flex items-center gap-2 px-2 md:px-3 py-1.5 md:py-2 bg-gray-800 border border-gray-600 rounded-lg">
                          <FileText size={14} className="text-purple-400" />
                          <span className="text-xs md:text-sm text-gray-300 truncate flex-1">{messageAttachment.name}</span>
                          <span className="hidden sm:inline text-xs text-gray-500">({(messageAttachment.size / 1024).toFixed(1)} KB)</span>
                          <button
                            onClick={() => setMessageAttachment(null)}
                            className="p-1 hover:bg-gray-700 rounded-full"
                          >
                            <X size={14} className="text-gray-400" />
                          </button>
                        </div>
                      )}
                      <div className="relative">
                        {/* Emoji Picker Popup */}
                        {showMsgEmojiPicker && (
                          <div
                            ref={emojiPickerRef}
                            className="absolute bottom-full mb-2 left-0 z-50 bg-[#111827] border border-gray-700 rounded-xl shadow-2xl p-3 w-72"
                            data-testid="emoji-picker"
                          >
                            {[
                              { label: 'Smileys', emojis: ['😀','😂','😍','🥰','😊','😎','🤩','😅','🤣','😭','😤','🥺','😏','🤔','😬','🙄','😴','🤯','🥳','😇'] },
                              { label: 'Gestures', emojis: ['👍','👎','👏','🙌','🤝','👋','✌️','🤞','💪','🤙','👌','🤟','✋','🖐️','☝️','🤜','🤛','🫶','🫂','🤲'] },
                              { label: 'Hearts', emojis: ['❤️','🧡','💛','💚','💙','💜','🖤','🤍','💔','❤️‍🔥','💕','💞','💓','💗','💖','💘','💝','🫀','♥️','❣️'] },
                              { label: 'Objects', emojis: ['🎉','🎊','🔥','✨','⚡','🌟','💫','🎯','🎁','📱','💻','📧','📅','🗓️','⏰','💡','🔑','🏆','💎','🚀'] },
                            ].map(({ label, emojis }) => (
                              <div key={label} className="mb-2">
                                <p className="text-xs text-gray-500 mb-1 font-medium">{label}</p>
                                <div className="flex flex-wrap gap-0.5">
                                  {emojis.map(e => (
                                    <button
                                      key={e}
                                      type="button"
                                      onClick={() => { insertEmoji(e); setShowMsgEmojiPicker(false); }}
                                      className="text-xl hover:bg-gray-700 rounded p-0.5 transition-colors leading-none"
                                    >
                                      {e}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                        <textarea
                          ref={messageTextareaRef}
                          value={messageInput}
                          onChange={(e) => setMessageInput(e.target.value)}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              handleSendMessage();
                            }
                          }}
                          placeholder="Your message"
                          rows="1"
                          onInput={(e) => {
                            e.target.style.height = 'auto';
                            e.target.style.height = Math.min(e.target.scrollHeight, 150) + 'px';
                          }}
                          className="w-full px-3 md:px-4 py-2 md:py-2.5 bg-gray-900 border border-gray-400/20 rounded-lg text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-400 resize-none overflow-y-auto"
                          style={{ minHeight: '40px', maxHeight: '150px' }}
                        />
                      </div>
                    </div>
                    <button className="hidden sm:block p-1.5 md:p-2.5 hover:bg-gray-900 rounded-full transition-colors self-end">
                      <Mic size={18} className="text-gray-400" />
                    </button>
                    <button 
                      onClick={handleSendMessage}
                      disabled={!messageInput.trim()}
                      className="p-2 md:p-2.5 bg-purple-600 hover:bg-purple-700 rounded-full transition-colors self-end disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Send size={16} className="text-white" />
                    </button>
                  </div>
                </div>
              </>
              )}
              </div>
              )}
          </>
          )}

          {/* Calls Tab Content */}
          {activeTab === 'calls' && (
            <div className="flex flex-col md:flex-row flex-1 overflow-hidden">
              {/* Left Sidebar - Call Filters - Horizontal on mobile */}
              <div className="w-full md:w-48 bg-black border-b md:border-b-0 md:border-r border-gray-400/20 flex-shrink-0 p-3 md:p-4">
                <h3 className="text-xs md:text-sm font-bold text-white mb-2 md:mb-4 hidden md:block">FILTERS</h3>
                <div className="flex md:flex-col gap-1 md:gap-1 overflow-x-auto md:overflow-visible">
                  <button
                    onClick={() => setActiveCallFilter('all')}
                    className={`flex-shrink-0 px-3 py-1.5 md:py-2 rounded-lg transition-colors flex items-center gap-1.5 md:gap-2 text-xs md:text-sm whitespace-nowrap ${
                      activeCallFilter === 'all'
                        ? 'bg-white text-black'
                        : 'text-gray-400 hover:bg-gray-900'
                    }`}
                  >
                    <Phone size={14} />
                    All Calls
                  </button>
                  <button
                    onClick={() => setActiveCallFilter('missed')}
                    className={`flex-shrink-0 px-3 py-1.5 md:py-2 rounded-lg transition-colors flex items-center gap-1.5 md:gap-2 text-xs md:text-sm whitespace-nowrap ${
                      activeCallFilter === 'missed'
                        ? 'bg-white text-black'
                        : 'text-gray-400 hover:bg-gray-900'
                    }`}
                  >
                    <AlertTriangle size={14} />
                    Missed
                  </button>
                  <button
                    onClick={() => setActiveCallFilter('voicemail')}
                    className={`flex-shrink-0 px-3 py-1.5 md:py-2 rounded-lg transition-colors flex items-center gap-1.5 md:gap-2 text-xs md:text-sm whitespace-nowrap ${
                      activeCallFilter === 'voicemail'
                        ? 'bg-white text-black'
                        : 'text-gray-400 hover:bg-gray-900'
                    }`}
                  >
                    <Mic size={14} />
                    Voicemails
                  </button>
                  <button
                    onClick={() => setActiveCallFilter('facetime')}
                    className={`flex-shrink-0 px-3 py-1.5 md:py-2 rounded-lg transition-colors flex items-center gap-1.5 md:gap-2 text-xs md:text-sm whitespace-nowrap ${
                      activeCallFilter === 'facetime'
                        ? 'bg-white text-black'
                        : 'text-gray-400 hover:bg-gray-900'
                    }`}
                  >
                    <Video size={14} />
                    FaceTime
                  </button>
                </div>
              </div>

              {/* Call List */}
              <div className="flex-1 flex flex-col overflow-hidden">
                {/* Calls Connection Panel - Using Refactored Component */}
                <CallsConnectionPanel
                  isConnected={isCallsConnected}
                  connectionType={callsConnectionType}
                  onConnect={() => setShowCallsConnectionModal(true)}
                  onDisconnect={() => {
                    localStorage.removeItem('calls_connected');
                    localStorage.removeItem('calls_connection_type');
                    setIsCallsConnected(false);
                    setCallsConnectionType(null);
                  }}
                />
                
                {/* Category Tabs + New Call Button - Using Refactored Component */}
                <CallCategoryTabs
                  categories={callCategories}
                  activeCategory={activeCallCategory}
                  onCategoryChange={setActiveCallCategory}
                  onEditCategories={() => openCategoryModal('calls')}
                  onNewCall={() => setShowNewCallModal(true)}
                  formatCategoryName={formatCategoryName}
                />

                <div className="flex-1 overflow-y-auto p-3 md:p-4 space-y-2" style={{ paddingBottom: '5rem' }}>
                  {calls.length === 0 ? (
                    <EmptyState
                      icon="calls"
                      title="No Call History"
                      description="Your Twilio call logs will appear here"
                    />
                  ) : (
                    calls
                      .filter(call => {
                        // Call type filter
                        if (activeCallFilter === 'all') return true;
                        if (activeCallFilter === 'missed') return call.status === 'no-answer' || call.status === 'busy' || call.status === 'failed';
                        if (activeCallFilter === 'voicemail') return call.recording_url;
                        return true;
                      })
                      .map((call) => {
                        // Determine phone number to display (the other party)
                        const displayPhone = call.direction === 'inbound' ? call.from_phone : call.to_phone;
                        const displayName = call.contact_name || displayPhone;
                        
                        // Format the timestamp
                        const callDate = call.created_at ? new Date(call.created_at) : new Date();
                        const formattedDate = callDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                        const formattedTime = callDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
                        
                        const outcomeInfo = callOutcomes[call.call_sid];
                        
                        return (
                          <CallListItem
                            key={call.id}
                            call={call}
                            displayName={displayName}
                            displayPhone={displayPhone}
                            formattedDate={formattedDate}
                            formattedTime={formattedTime}
                            isDropdownOpen={activeCallDropdown === call.id}
                            onToggleDropdown={() => setActiveCallDropdown(activeCallDropdown === call.id ? null : call.id)}
                            onCall={async (call, phone, name) => {
                              if (voiceReady && voiceMakeCall) {
                                try {
                                  console.log('📞 Initiating browser call to:', phone);
                                  await voiceMakeCall(phone);
                                } catch (error) {
                                  console.error('Failed to initiate call:', error);
                                  alert('Failed to initiate call: ' + error.message);
                                }
                              } else {
                                setActiveCall({ ...call, phone, name });
                                setShowCallModal(true);
                                setIsCalling(true);
                                setCallDuration(0);
                              }
                            }}
                            onNewMeeting={(name, phone) => {
                              setActiveCallDropdown(null);
                              handleNewMeeting({ name, phone });
                            }}
                            onAddContact={(name, phone) => {
                              setActiveCallDropdown(null);
                              handleAddContactFromDropdown({ name, phone });
                            }}
                            onDelete={(call) => {
                              if (window.confirm('Are you sure you want to delete this call?')) {
                                deleteCall(call.call_sid);
                              }
                              setActiveCallDropdown(null);
                            }}
                            onClick={(call) => setSelectedCallDetail(call)}
                            voiceReady={voiceReady}
                            outcomeInfo={outcomeInfo}
                          />
                        );
                      })
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Group Members Modal - Using Refactored Component */}
          <GroupMembersModal
            isOpen={showGroupMembers && conversations[selectedConversation]?.isGroup}
            onClose={() => setShowGroupMembers(false)}
            groupName={conversations[selectedConversation]?.name}
            members={getCurrentGroupMembers()}
          />

          {/* Phone Call Modal */}
          {/* Phone Call Modal - Using Refactored Component */}
          <PhoneCallModal
            isOpen={showCallModal && activeCall}
            call={activeCall}
            isCalling={isCalling}
            callDuration={callDuration}
            onClose={() => {
              setShowCallModal(false);
              setActiveCall(null);
              setIsCalling(false);
              setCallDuration(0);
            }}
            onToggleMute={() => setIsCalling(!isCalling)}
            onEndCall={() => {
              setShowCallModal(false);
              setActiveCall(null);
              setIsCalling(false);
              setCallDuration(0);
            }}
          />

          {/* FaceTime Modal */}
          {/* FaceTime Modal - Refactored */}
          <FaceTimeModal
            isOpen={showFaceTimeModal && !!activeCall}
            onClose={() => {
              setShowFaceTimeModal(false);
              setActiveCall(null);
              setIsCalling(false);
              setCallDuration(0);
            }}
            activeCall={activeCall}
            isCalling={isCalling}
            setIsCalling={setIsCalling}
            callDuration={callDuration}
          />

          {/* New Call Modal */}
          {showNewCallModal && (
            <NewCallModal
              isOpen={showNewCallModal}
              onClose={() => setShowNewCallModal(false)}
              contactsList={contactsList}
              loadingContacts={loadingContacts}
              fetchICloudContacts={fetchICloudContacts}
              voiceMakeCall={voiceMakeCall}
              voiceReady={voiceReady}
              onCallInitiated={(callData) => {
                setActiveCall(callData);
                setShowNewCallModal(false);
                // Don't show the old call modal for browser calls - the IncomingCallPopup handles active calls
                if (!callData.isBrowserCall) {
                  setShowCallModal(true);
                  setIsCalling(true);
                  setCallDuration(0);
                }
              }}
            />
          )}

          {/* Call Detail Modal - Shows transcript, summary, lead capture, outcome */}
          {selectedCallDetail && (
            <CallDetailModal
              call={selectedCallDetail}
              isOpen={!!selectedCallDetail}
              onClose={() => setSelectedCallDetail(null)}
              onOutcomeUpdate={(callSid, outcome) => {
                setCallOutcomes(prev => ({ ...prev, [callSid]: outcome }));
                // Refresh calls list
                fetchCalls();
              }}
              onLeadCapture={(result) => {
                console.log('Lead captured:', result);
                // Refresh calls list
                fetchCalls();
              }}
            />
          )}

          {/* Meeting Tab Content */}
          {activeTab === 'meeting' && (
            <div className="flex-1 flex flex-col bg-black overflow-hidden" style={{ height: 'calc(100% - 57px)' }}>
              {/* Compact Meeting Platform Bar */}
              {isMeetingActive ? (
                <ActiveMeetingView
                  activeMeetingPlatform={activeMeetingPlatform}
                  isCameraOff={isCameraOff}
                  setIsCameraOff={setIsCameraOff}
                  isMicMuted={isMicMuted}
                  setIsMicMuted={setIsMicMuted}
                  mediaStream={mediaStream}
                  setMediaStream={setMediaStream}
                  onLeaveMeeting={() => {
                    setIsMeetingActive(false);
                    setActiveMeetingPlatform(null);
                    setIsMicMuted(false);
                    setIsCameraOff(false);
                  }}
                />
              ) : (
                <div className="flex-1 flex flex-col items-center justify-center p-8 overflow-y-auto">
                  {/* Start a Meeting Header */}
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                      <Video className="w-12 h-12 text-purple-500" />
                    </div>
                    <h2 className="text-2xl font-bold text-white mb-2">Start a Meeting</h2>
                    <p className="text-gray-400">Connect to your favorite meeting platform</p>
                  </div>
                  
                  {/* Meeting Platform Cards Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl w-full">
                    {/* Zoom Card */}
                    <div className="bg-[#1a1f2e] border border-gray-700 rounded-2xl p-8 flex flex-col items-center text-center hover:border-gray-500 transition-colors">
                      <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center mb-5">
                        <Video className="w-8 h-8 text-black" />
                      </div>
                      <h3 className="text-xl font-bold text-white mb-2">Zoom</h3>
                      <p className="text-gray-400 text-sm mb-5">Connect to Zoom Meeting</p>
                      <button
                        onClick={() => { setActiveMeetingPlatform('zoom'); setIsMeetingActive(true); }}
                        data-testid="meeting-connect-zoom"
                        className="px-6 py-2 bg-white text-black font-medium rounded-lg hover:bg-gray-200 transition-colors"
                      >
                        Connect
                      </button>
                    </div>
                    
                    {/* Microsoft Teams Card */}
                    <div className="bg-[#1a1f2e] border border-gray-700 rounded-2xl p-8 flex flex-col items-center text-center hover:border-gray-500 transition-colors">
                      <div className="w-16 h-16 bg-purple-600 rounded-2xl flex items-center justify-center mb-5">
                        <Video className="w-8 h-8 text-white" />
                      </div>
                      <h3 className="text-xl font-bold text-white mb-2">Microsoft Teams</h3>
                      <p className="text-gray-400 text-sm mb-5">Connect to Teams Meeting</p>
                      <button
                        onClick={() => { setActiveMeetingPlatform('teams'); setIsMeetingActive(true); }}
                        data-testid="meeting-connect-teams"
                        className="px-6 py-2 bg-white text-black font-medium rounded-lg hover:bg-gray-200 transition-colors"
                      >
                        Connect
                      </button>
                    </div>
                    
                    {/* Google Meet Card */}
                    <div className="bg-[#1a1f2e] border border-gray-700 rounded-2xl p-8 flex flex-col items-center text-center hover:border-gray-500 transition-colors">
                      <div className="w-16 h-16 bg-green-500 rounded-2xl flex items-center justify-center mb-5">
                        <Video className="w-8 h-8 text-white" />
                      </div>
                      <h3 className="text-xl font-bold text-white mb-2">Google Meet</h3>
                      <p className="text-gray-400 text-sm mb-5">Connect to Google Meet</p>
                      <button
                        onClick={() => { setActiveMeetingPlatform('meet'); setIsMeetingActive(true); }}
                        data-testid="meeting-connect-meet"
                        className="px-6 py-2 bg-white text-black font-medium rounded-lg hover:bg-gray-200 transition-colors"
                      >
                        Connect
                      </button>
                    </div>
                    
                    {/* Zoho Meeting Card */}
                    <div className="bg-[#1a1f2e] border border-gray-700 rounded-2xl p-8 flex flex-col items-center text-center hover:border-gray-500 transition-colors">
                      <div className="w-16 h-16 bg-orange-500 rounded-2xl flex items-center justify-center mb-5">
                        <Video className="w-8 h-8 text-white" />
                      </div>
                      <h3 className="text-xl font-bold text-white mb-2">Zoho Meeting</h3>
                      <p className="text-gray-400 text-sm mb-5">Connect to Zoho Meeting</p>
                      <button
                        onClick={() => { setActiveMeetingPlatform('zoho'); setIsMeetingActive(true); }}
                        data-testid="meeting-connect-zoho"
                        className="px-6 py-2 bg-white text-black font-medium rounded-lg hover:bg-gray-200 transition-colors"
                      >
                        Connect
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Compose Message Modal */}
          {/* Message Compose Modal - Using Refactored Component */}
          <MessageComposeModal
            isOpen={showComposeModal && activeTab === 'messages'}
            onClose={() => {
              setShowComposeModal(false);
              setComposeRecipient('');
              setComposeMessage('');
              setFilteredContacts([]);
              setShowContactSuggestions(false);
            }}
            composeRecipient={composeRecipient}
            setComposeRecipient={setComposeRecipient}
            showContactSuggestions={showContactSuggestions}
            setShowContactSuggestions={setShowContactSuggestions}
            filteredContacts={filteredContacts}
            setFilteredContacts={setFilteredContacts}
            loadingContacts={loadingContacts}
            iCloudContacts={iCloudContacts}
            contactsList={contactsList}
            fetchICloudContacts={fetchICloudContacts}
            composeMessage={composeMessage}
            setComposeMessage={setComposeMessage}
            sendFromPhone={composeFromPhone}
            setSendFromPhone={(phone) => {
              setComposeFromPhone(phone);
              localStorage.setItem('compose_default_phone', phone);
            }}
            userPhoneNumbers={userPhoneNumbers}
            onOpenPhoneSettings={() => setShowPhoneSettingsModal(true)}
            onOpenScheduleModal={() => setShowScheduleModal(true)}
            sendingMessage={composeSending}
            onSend={async () => {
              if (!composeRecipient.trim()) { alert('Please enter a recipient phone number'); return; }
              if (!composeMessage.trim()) { alert('Please enter a message'); return; }
              try {
                setComposeSending(true);
                const fromPhoneNumber = userPhoneNumbers[composeFromPhone] || '';
                let response;
                if (composeFromPhone === 'personal' || composeFromPhone === 'business') {
                  response = await apiCall('/mac-messages/outbox', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ recipient: composeRecipient, message: composeMessage, phone_type: composeFromPhone, user_id: userId })
                  });
                  toast.success('Message queued! Run mac_sync.py --send-pending on your Mac to send.');
                  const tempConversation = {
                    id: response?.message_id || `temp-${Date.now()}`,
                    name: composeRecipient,
                    phone: composeRecipient,
                    lastMessage: composeMessage,
                    timestamp: new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }),
                    created_at: new Date().toISOString(),
                    phone_type: composeFromPhone,
                    messages: [{ id: response?.message_id || `msg-${Date.now()}`, sender: 'You', text: composeMessage, time: new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }), sent: true, pending: true, phone_type: composeFromPhone }],
                    pending: true
                  };
                  setConversations(prev => [tempConversation, ...prev.filter(c => c.phone !== composeRecipient)]);
                  setSelectedConversation(tempConversation);
                  setMobileShowChat(true);
                } else {
                  response = await apiCall('/messages/send-sms', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ phone_number: composeRecipient, message: composeMessage, recipient_name: composeRecipient, from_phone: composeFromPhone, from_phone_number: fromPhoneNumber })
                  });
                  toast.success('Message sent!');
                  const sentConversation = response?.conversation;
                  if (sentConversation) {
                    setConversations(prev => [sentConversation, ...prev.filter(c => c.phone !== sentConversation.phone && c.id !== sentConversation.id)]);
                    setSelectedConversation(sentConversation);
                    setMobileShowChat(true);
                  } else {
                    const conversationsData = await apiCall('/conversations');
                    const sorted = conversationsData.sort((a, b) => (b.created_at || '') > (a.created_at || '') ? 1 : -1);
                    setConversations(sorted);
                    const found = sorted.find(c => c.phone === composeRecipient);
                    if (found) { setSelectedConversation(found); setMobileShowChat(true); }
                  }
                }
                setComposeRecipient(''); setComposeMessage(''); setFilteredContacts([]); setShowContactSuggestions(false); setShowComposeModal(false);
              } catch (error) {
                console.error('Failed to send message:', error);
                toast.error('Failed to send message: ' + error.message);
              } finally {
                setComposeSending(false);
              }
            }}
          />

          {/* Phone Numbers Settings Modal */}
          {/* Phone Settings Modal - Using Refactored Component */}
          <PhoneSettingsModal
            isOpen={showPhoneSettingsModal}
            onClose={() => setShowPhoneSettingsModal(false)}
            userPhoneNumbers={userPhoneNumbers}
            setUserPhoneNumbers={setUserPhoneNumbers}
          />

          {/* Email Tab Content - Split Pane Layout */}
          {activeTab === 'email' && (
            <div className="flex flex-1 overflow-hidden">
              {/* LEFT PANEL - Email List & Controls */}
              <div className={`flex flex-col bg-black overflow-hidden border-r border-gray-800 ${selectedEmail ? 'w-[45%] min-w-[400px]' : 'flex-1'}`}>
                {/* Email Connection Panel with Account Tabs - Using Refactored Component */}
                <EmailConnectionPanel
                  onAddAccount={() => setShowAddEmailModal(true)}
                  connectedAccounts={emailAccounts}
                  emailAccounts={emailAccounts}
                  activeEmailAccount={activeEmailAccount}
                  onSelectAccount={setActiveEmailAccount}
                  onSyncAccount={(account) => {
                    if (account.id === 'outlook' || account.type === 'outlook') syncOutlookEmails();
                    else if (account.id === 'gmail' || account.type === 'gmail') syncGmailEmails(activeFolder);
                    else if (account.type === 'nylas' || account.nylasGrantId) {
                      syncNylasEmails('inbox', account.email, account.id);
                    }
                    else if (account.type === 'icloud') alert('Syncing iCloud emails...');
                  }}
                  onConnectAccount={(account) => {
                    if (account.id === 'outlook' || account.type === 'outlook') syncOutlookEmails();
                    else if (account.id === 'gmail' || account.type === 'gmail') connectGmail(account.email);
                    else if (!account.connected) {
                      connectNylasAccount(['email']);
                    }
                    else if (account.type === 'icloud') {
                      const creds = localStorage.getItem(`email_creds_${account.id}`);
                      if (creds) {
                        setEmailAccounts(prev => prev.map(acc => 
                          acc.id === account.id ? { ...acc, connected: true } : acc
                        ));
                      } else {
                        alert('Please add credentials for this account');
                      }
                    }
                  }}
                  onRemoveAccount={handleRemoveEmailAccount}
                  onDisconnectAll={handleDisconnectAllEmails}
                  disconnectingAll={disconnectingAllEmails}
                  renamingAccountId={renamingAccountId}
                  renameValue={renameValue}
                  setRenameValue={setRenameValue}
                  onStartRename={startRenamingAccount}
                  onRename={handleRenameEmailAccount}
                  onCancelRename={() => { setRenamingAccountId(null); setRenameValue(''); }}
                  nylasLoading={nylasLoading}
                />
                
                {/* Email Header - Using Refactored Component */}
                <EmailTabHeader
                  activeFolder={activeFolder}
                  emailCount={filteredEmails.length}
                  emailAccounts={emailAccounts}
                  activeEmailAccount={activeEmailAccount}
                  renamingAccountId={renamingAccountId}
                  renameValue={renameValue}
                  setRenameValue={setRenameValue}
                  onSelectAccount={setActiveEmailAccount}
                  onStartRename={startRenamingAccount}
                  onRename={handleRenameEmailAccount}
                  onCancelRename={() => { setRenamingAccountId(null); setRenameValue(''); }}
                  onRemoveAccount={handleRemoveEmailAccount}
                  onReorderAccounts={handleReorderEmailAccounts}
                  onSyncAccount={(account) => {
                    if (account.id === 'outlook' || account.type === 'outlook') syncOutlookEmails();
                    else if (account.id === 'gmail' || account.type === 'gmail') syncGmailEmails(activeFolder);
                    else if (account.type === 'nylas') syncNylasEmails(activeFolder, account.email, account.id);
                    else if (account.type === 'icloud') alert('Syncing iCloud emails...');
                  }}
                  onConnectAccount={(account) => {
                    if (account.id === 'outlook' || account.type === 'outlook') syncOutlookEmails();
                    else if (account.id === 'gmail' || account.type === 'gmail') connectGmail(account.email);
                    else if (account.type === 'icloud') {
                      const creds = localStorage.getItem(`email_creds_${account.id}`);
                      if (creds) {
                        setEmailAccounts(prev => prev.map(acc => 
                          acc.id === account.id ? { ...acc, connected: true } : acc
                        ));
                      } else {
                        alert('Please add credentials for this account');
                      }
                    }
                  }}
                  onAddAccount={() => setShowAddEmailModal(true)}
                  outlookConnected={outlookConnected}
                  outlookLoading={outlookLoading}
                  gmailConnected={gmailConnected}
                  gmailLoading={gmailLoading}
                  gmailEmail={gmailEmail}
                  nylasLoading={nylasLoading}
                />

                {/* Delete Email Account Confirmation Modal */}
                <DeleteEmailAccountModal
                  isOpen={showDeleteEmailConfirm && !!emailAccountToDelete}
                  onClose={() => {
                    setShowDeleteEmailConfirm(false);
                    setEmailAccountToDelete(null);
                  }}
                  emailAccount={emailAccountToDelete}
                  onConfirm={confirmRemoveEmailAccount}
                  isDeleting={deletingEmailAccount}
                />

                {/* Category Tabs - Using Refactored Component */}
                <EmailCategoryFilter
                  categories={emailCategories}
                  activeCategory={activeCategory}
                  onCategoryChange={setActiveCategory}
                  onEditCategories={() => openCategoryModal('emailCategories')}
                  onScanEmails={scanAndRecategorizeEmails}
                  isScanning={isScanning}
                  categoryCounts={categoryCounts}
                  formatCategoryName={formatCategoryName}
                />

                {/* Horizontal Folder Tabs - Now under category tabs */}
                <div className="flex-shrink-0 px-3 py-2 overflow-x-auto">
                  <div className="flex gap-2 items-center">
                    {emailFolders.map((folder) => {
                      const isActive = activeFolder === folder;
                      const folderLabels = {
                        inbox: 'Inbox',
                        sent: 'Sent',
                        drafts: 'Drafts',
                        flagged: 'Flagged',
                        archive: 'Archive',
                        trash: 'Trash',
                        spam: 'Spam'
                      };
                      return (
                        <button
                          key={folder}
                          onClick={() => handleFolderChange(folder)}
                          data-testid={`email-folder-${folder}-btn`}
                          className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all whitespace-nowrap border ${
                            isActive
                              ? 'bg-white text-black border-white'
                              : 'bg-black text-white border-gray-600 hover:bg-gray-900 hover:border-gray-500'
                          }`}
                        >
                          {folderLabels[folder] || folder.charAt(0).toUpperCase() + folder.slice(1)}
                        </button>
                      );
                    })}
                    
                    {/* Draft Center Button */}
                    <div className="h-4 w-px bg-white/20 mx-1" />
                    <button
                      onClick={() => setShowDraftCenter(true)}
                      className="px-3 py-1.5 rounded-lg text-xs font-medium bg-purple-500/20 text-purple-400 border border-purple-500/30 hover:bg-purple-500/30 transition-all whitespace-nowrap flex items-center gap-1.5"
                      data-testid="draft-center-btn"
                    >
                      <FileText className="w-3.5 h-3.5" />
                      Draft Center
                    </button>
                  </div>
                </div>

                {/* Email Search Bar - Using Refactored Component */}
                <EmailSearchBar
                  searchQuery={emailSearchQuery}
                  setSearchQuery={setEmailSearchQuery}
                  resultCount={filteredEmails.length}
                  onCompose={() => {
                    setEmailTo('');
                    setEmailCc('');
                    setEmailBcc('');
                    setEmailSubject('');
                    setEmailBody('');
                    setShowEmailComposeModal(true);
                  }}
                />

                {/* Email List - Using Refactored Component */}
                <EmailList
                  emails={paginatedEmails}
                  flaggedEmails={flaggedEmails}
                  openDropdownId={openEmailDropdown}
                  selectedEmailId={selectedEmail?.id}
                  onSelectEmail={handleSelectEmail}
                  onToggleFlag={handleToggleFlag}
                  onToggleDropdown={(key) => setOpenEmailDropdown(openEmailDropdown === key ? null : key)}
                  onCloseDropdown={() => setOpenEmailDropdown(null)}
                  onReplyEmail={handleReplyEmail}
                  onAddToMeeting={handleAddToMeeting}
                  onNewMeeting={handleNewMeeting}
                  onDeleteEmail={handleDeleteEmail}
                  listHeight={selectedEmail ? "calc(100vh - 350px)" : "calc(100vh - 280px)"}
                />
                
                {/* Email Pagination - Using Refactored Component */}
                <EmailPagination
                  currentPage={emailCurrentPage}
                  totalPages={totalEmailPages}
                  totalEmails={filteredEmails.length}
                  emailsPerPage={emailsPerPage}
                  onPageChange={setEmailCurrentPage}
                />
              </div>

              {/* RIGHT PANEL - Email Thread View */}
              {selectedEmail && (
                <div className="flex-1 flex flex-col bg-black overflow-hidden">
                  <EmailThreadView
                    email={selectedEmail}
                    onClose={() => setSelectedEmail(null)}
                    onReply={() => {
                      const lastMsg = selectedEmail.thread?.[selectedEmail.thread.length - 1];
                      const senderEmail = selectedEmail.email || lastMsg?.email || '';
                      const senderName = selectedEmail.from || lastMsg?.from || '';
                      const originalBody = lastMsg?.body || lastMsg?.parsedBody || '';
                      setEmailTo(senderEmail);
                      setEmailCc('');
                      setEmailBcc('');
                      setEmailSubject(selectedEmail.subject?.startsWith('RE:') ? selectedEmail.subject : `RE: ${selectedEmail.subject}`);
                      setEmailBody(`\n\n--- Original Message ---\nFrom: ${senderName} <${senderEmail}>\nSubject: ${selectedEmail.subject}\n\n${originalBody}`);
                      setComposeAttachments([]);
                      setShowEmailComposeModal(true);
                    }}
                    onReplyAll={() => {
                      const lastMsg = selectedEmail.thread?.[selectedEmail.thread.length - 1];
                      const senderEmail = selectedEmail.email || lastMsg?.email || '';
                      const senderName = selectedEmail.from || lastMsg?.from || '';
                      const originalBody = lastMsg?.body || lastMsg?.parsedBody || '';
                      const allEmails = new Set();
                      selectedEmail.thread?.forEach(msg => { if (msg.email) allEmails.add(msg.email); });
                      if (selectedEmail.email) allEmails.add(selectedEmail.email);
                      allEmails.delete(senderEmail);
                      const ccList = Array.from(allEmails).filter(e => !e.toLowerCase().includes('creatrhq') && !e.toLowerCase().includes('you'));
                      setEmailTo(senderEmail);
                      setEmailCc(ccList.join(', '));
                      setEmailBcc('');
                      setEmailSubject(selectedEmail.subject?.startsWith('RE:') ? selectedEmail.subject : `RE: ${selectedEmail.subject}`);
                      setEmailBody(`\n\n--- Original Message ---\nFrom: ${senderName} <${senderEmail}>\nSubject: ${selectedEmail.subject}\n\n${originalBody}`);
                      setComposeAttachments([]);
                      setShowEmailComposeModal(true);
                    }}
                    onForward={() => {
                      const lastMsg = selectedEmail.thread?.[selectedEmail.thread.length - 1];
                      const senderEmail = selectedEmail.email || lastMsg?.email || '';
                      const senderName = selectedEmail.from || lastMsg?.from || '';
                      const originalBody = lastMsg?.body || lastMsg?.parsedBody || '';
                      setEmailTo('');
                      setEmailCc('');
                      setEmailBcc('');
                      setEmailSubject(selectedEmail.subject?.startsWith('FWD:') ? selectedEmail.subject : `FWD: ${selectedEmail.subject}`);
                      setEmailBody(`\n\n--- Forwarded Message ---\nFrom: ${senderName} <${senderEmail}>\nSubject: ${selectedEmail.subject}\n\n${originalBody}`);
                      setComposeAttachments([]);
                      setShowEmailComposeModal(true);
                    }}
                    openDropdownKey={openEmailDropdown}
                    onToggleDropdown={(key) => setOpenEmailDropdown(openEmailDropdown === key ? null : key)}
                    onCloseDropdown={() => setOpenEmailDropdown(null)}
                    parseEmailThread={parseEmailThread}
                    formatTime={formatEmailThreadTime}
                    handleAddToMeetingWithAttachments={handleAddToMeetingWithAttachments}
                    handleNewMeetingWithAttachments={handleNewMeetingWithAttachments}
                    handleAddContactFromEmail={handleAddContactFromEmail}
                    handleAddToFlight={handleAddToFlight}
                    handleAddToFinances={handleAddToFinances}
                    handleAddAttachmentToDocuments={handleAddAttachmentToDocuments}
                    handleDownloadAttachment={handleDownloadAttachment}
                    handleReplyEmail={handleReplyEmail}
                    handleAddToMeeting={handleAddToMeeting}
                    setNewContactName={setNewContactName}
                    setNewContactPhone={setNewContactPhone}
                    setNewContactEmail={setNewContactEmail}
                    setNewContactCompany={setNewContactCompany}
                    setNewContactRole={setNewContactRole}
                    setNewContactCategory={setNewContactCategory}
                    setNewContactNotes={setNewContactNotes}
                    setOriginalConversationName={setOriginalConversationName}
                    setShowAddContactModal={setShowAddContactModal}
                    replyInput={emailReplyInput}
                    setReplyInput={setEmailReplyInput}
                    showAIDraft={showAIDraftEmail}
                    aiDraftText={aiDraftEmailText}
                    setAiDraftText={setAiDraftEmailText}
                    setShowAIDraft={setShowAIDraftEmail}
                    generatingAIReply={generatingAIReply}
                    setGeneratingAIReply={setGeneratingAIReply}
                    sendingReply={sendingEmailReply}
                    onSendReply={handleSendEmailReply}
                    onOpenSchedule={() => setShowScheduleModal(true)}
                    aiEmailAPI={aiEmailAPI}
                    userId={userId}
                    slackChannels={slackChannelsForDrafts}
                    onDraftCreated={(draft) => {
                      // Refresh drafts if Draft Center is open
                      console.log('Draft created:', draft);
                    }}
                    onActionItemsExtracted={(items) => {
                      console.log('Action items extracted:', items);
                    }}
                  />
                </div>
              )}
            </div>
          )}

          {/* Email Thread View - Using Refactored Component */}
          {/* NOTE: EmailThreadView is now rendered as part of the split-pane layout above */}
        </div>
      </div>

 

      {/* Schedule Modal - Using Refactored Component */}
      <ScheduleModal
        isOpen={showScheduleModal}
        onClose={() => setShowScheduleModal(false)}
        type={activeTab === 'messages' ? 'message' : 'email'}
        messagePreview={messageInput}
        recipient={selectedConversation}
        scheduleDate={scheduleDate}
        setScheduleDate={setScheduleDate}
        scheduleTime={scheduleTime}
        setScheduleTime={setScheduleTime}
        scheduledMessages={scheduledMessages}
        onRemoveScheduled={(idx) => setScheduledMessages(prev => prev.filter((_, i) => i !== idx))}
        onSchedule={async () => {
          if (!scheduleDate || !scheduleTime) {
            alert('Please select both date and time');
            return;
          }
          if (!messageInput.trim()) {
            alert('Please enter a message to schedule');
            return;
          }
          if (!selectedConversation) {
            alert('Please select a conversation first');
            return;
          }
          
          const scheduledFor = new Date(`${scheduleDate}T${scheduleTime}`);
          if (scheduledFor <= new Date()) {
            alert('Please select a future date and time');
            return;
          }
          
          // Create scheduled message object
          const newScheduledMessage = {
            id: `scheduled-${Date.now()}`,
            to: selectedConversation.name,
            phone: selectedConversation.phone,
            message: messageInput,
            scheduledFor: scheduledFor.toISOString(),
            status: 'pending'
          };
          
          // Save to backend
          try {
            await apiCall('/messages/schedule', {
              method: 'POST',
              body: JSON.stringify({
                to_phone: selectedConversation.phone,
                message: messageInput,
                scheduled_for: scheduledFor.toISOString(),
                conversation_id: selectedConversation.id
              })
            });
            
            // Add to local state
            setScheduledMessages(prev => [...prev, newScheduledMessage]);
            
            // Show the scheduled messages tracker popup
            setShowScheduledTracker(true);
            
            // Clear inputs
            setShowScheduleModal(false);
            setScheduleDate('');
            setScheduleTime('');
            setMessageInput('');
          } catch (error) {
            console.error('Failed to schedule message:', error);
            // Still add locally even if backend fails
            setScheduledMessages(prev => [...prev, newScheduledMessage]);
            
            // Show the scheduled messages tracker popup
            setShowScheduledTracker(true);
            
            setShowScheduleModal(false);
            setScheduleDate('');
            setScheduleTime('');
            setMessageInput('');
          }
        }}
      />

      {/* Scheduled Messages Tracker - Using Refactored Component */}
      <ScheduledMessagesTracker
        isOpen={showScheduledTracker && scheduledMessages.length > 0}
        onClose={() => setShowScheduledTracker(false)}
        scheduledMessages={scheduledMessages}
        onCancelMessage={(msgId) => {
          setScheduledMessages(prev => prev.filter(m => m.id !== msgId));
          if (scheduledMessages.filter(m => m.status === 'pending').length <= 1) {
            setShowScheduledTracker(false);
          }
        }}
      />

      {/* Email Compose Modal - Using Refactored Component */}
      <EmailComposeModal
        isOpen={showEmailComposeModal}
        onClose={() => {
          setShowEmailComposeModal(false);
          setEmailTo('');
          setEmailCc('');
          setEmailBcc('');
          setEmailSubject('');
          setEmailBody('');
          setComposeAttachments([]);
        }}
        emailTo={emailTo}
        setEmailTo={setEmailTo}
        emailCc={emailCc}
        setEmailCc={setEmailCc}
        emailBcc={emailBcc}
        setEmailBcc={setEmailBcc}
        emailSubject={emailSubject}
        setEmailSubject={setEmailSubject}
        emailBody={emailBody}
        setEmailBody={setEmailBody}
        composeAttachments={composeAttachments}
        setComposeAttachments={setComposeAttachments}
        senderEmail={activeEmailAccount === 'gmail' ? gmailEmail : (emailAccounts.find(a => a.id === activeEmailAccount)?.email || 'your account')}
        onSend={async () => {
          if (!emailTo.trim() || !emailSubject.trim()) {
            alert('Please enter recipient and subject');
            return;
          }
          setSendingEmail(true);
          try {
            const attachmentData = [];
            if (composeAttachments && composeAttachments.length > 0) {
              for (const file of composeAttachments) {
                const base64 = await new Promise((resolve, reject) => {
                  const reader = new FileReader();
                  reader.onload = () => resolve(reader.result.split(',')[1]);
                  reader.onerror = reject;
                  reader.readAsDataURL(file);
                });
                attachmentData.push({ name: file.name, contentType: file.type || 'application/octet-stream', contentBytes: base64, size: file.size });
              }
            }
            const currentAccount = emailAccounts.find(acc => acc.id === activeEmailAccount);
            const accountType = currentAccount?.type || 'outlook';
            const accountEmail = currentAccount?.email || '';
            if (activeEmailAccount === 'gmail') {
              const response = await fetch(`${window.location.origin}/api/gmail/send`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ to: emailTo.split(',').map(e => e.trim()).filter(e => e), cc: emailCc ? emailCc.split(',').map(e => e.trim()).filter(e => e) : [], bcc: emailBcc ? emailBcc.split(',').map(e => e.trim()).filter(e => e) : [], subject: emailSubject, body: emailBody, account_id: 'creatrhq', attachments: attachmentData })
              });
              if (!response.ok) { const errorData = await response.json(); throw new Error(errorData.detail || 'Failed to send email'); }
              alert('Email sent successfully via Gmail!');
            } else if (accountType === 'nylas' && accountEmail) {
              const authToken = localStorage.getItem('auth_token');
              const response = await fetch(`${window.location.origin}/api/nylas/send?from_email=${encodeURIComponent(accountEmail)}`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {})
                },
                body: JSON.stringify({ 
                  to: emailTo.split(',').map(e => e.trim()).filter(e => e), 
                  cc: emailCc ? emailCc.split(',').map(e => e.trim()).filter(e => e) : [], 
                  bcc: emailBcc ? emailBcc.split(',').map(e => e.trim()).filter(e => e) : [], 
                  subject: emailSubject, 
                  body: `<div style="font-family: Arial, sans-serif;">${emailBody.replace(/\n/g, '<br>')}</div>`,
                  attachments: attachmentData.length > 0 ? attachmentData : null
                })
              });
              if (!response.ok) {
                const rawText = await response.text();
                let detail = 'Failed to send email via Nylas';
                try { detail = JSON.parse(rawText)?.detail || detail; } catch { /* ignored */ }
                throw new Error(detail);
              }
              alert(`Email sent successfully via ${currentAccount?.name || 'connected account'}!`);
            } else {
              const { outlook2EmailAPI } = await import('../services/api');
              await outlook2EmailAPI.sendEmail({ to: [emailTo.trim()], cc: emailCc.trim() ? [emailCc.trim()] : [], subject: emailSubject, body: `<div style="font-family: Arial, sans-serif;">${emailBody.replace(/\n/g, '<br>')}</div>`, body_type: 'html', attachments: attachmentData });
              alert('Email sent successfully!');
            }
            setShowEmailComposeModal(false);
            setEmailTo(''); setEmailCc(''); setEmailBcc(''); setEmailSubject(''); setEmailBody(''); setComposeAttachments([]);
            if (activeFolder === 'sent' && activeEmailAccount === 'gmail') { syncGmailEmails('sent'); }
          } catch (err) {
            console.error('Error sending email:', err);
            alert(`Failed to send email: ${err.message || 'Please try again.'}`);
          } finally {
            setSendingEmail(false);
          }
        }}
        onSchedule={() => setShowScheduleModal(true)}
        onAIGenerate={async () => {
          if (!emailTo.trim() || !emailSubject.trim()) {
            alert('Please enter recipient and subject first for AI to generate a draft.');
            return;
          }
          setGeneratingComposeDraft(true);
          try {
            const result = await aiEmailAPI.generateEmailReply({ email_id: 'compose-new', subject: emailSubject, sender: emailTo, sender_name: emailTo.split('@')[0], body: `Write a professional email about: ${emailSubject}`, reply_tone: 'professional' });
            if (result.reply) { setEmailBody(result.reply); }
          } catch (err) {
            console.error('Failed to generate AI draft:', err);
            setEmailBody(`Dear ${emailTo.split('@')[0]},\n\nRegarding: ${emailSubject}\n\nI wanted to reach out to discuss this matter further. Please let me know a convenient time to connect.\n\nBest regards`);
          } finally {
            setGeneratingComposeDraft(false);
          }
        }}
        sendingEmail={sendingEmail}
        generatingDraft={generatingComposeDraft}
      />

      {/* Delete Confirmation Modal - Using Refactored Component */}
      <DeleteConfirmModal
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setConversationToDelete(null);
        }}
        onConfirm={() => {
          // Delete logic here
          setShowDeleteConfirm(false);
          setConversationToDelete(null);
        }}
        title="Delete Conversation?"
        message="Are you sure you want to delete this conversation? This action cannot be undone."
      />


      
      {/* Add Contact Toast Notification */}
      {/* Add to Flight Modal - Using Refactored Component */}
      <AddFlightModal
        isOpen={showAddFlightModal}
        onClose={() => setShowAddFlightModal(false)}
        flightForm={flightForm}
        setFlightForm={setFlightForm}
        onSave={handleSaveFlight}
      />

      {/* Add to Finances Modal - Using Refactored Component */}
      <AddFinanceModal
        isOpen={showAddFinanceModal}
        onClose={() => setShowAddFinanceModal(false)}
        financeForm={financeForm}
        setFinanceForm={setFinanceForm}
        onSave={handleSaveFinance}
      />

      {showAddContactToast && (
        <div className="fixed bottom-6 right-6 bg-green-600 text-white px-6 py-3 rounded-lg shadow-xl flex items-center gap-3 z-50 animate-slide-up">
          <Check size={20} />
          <span className="font-medium">{showAddContactToast}</span>
        </div>
      )}

      {/* Communications Widget Modal - Using Refactored Component */}
      <CommWidgetModal
        isOpen={showCommWidgetModal}
        onClose={() => setShowCommWidgetModal(false)}
        modalType={commWidgetModalType}
      />

      {/* Add to Meeting Modal - Using Refactored Component */}
      <AddToMeetingModal
        isOpen={showAddToMeetingModal}
        onClose={() => setShowAddToMeetingModal(false)}
        meetingConversation={meetingConversation}
        existingMeetings={existingMeetings}
        selectedMeetingId={selectedMeetingId}
        setSelectedMeetingId={setSelectedMeetingId}
        onAddToMeeting={handleAddToExistingMeeting}
      />

      {/* New Meeting Modal */}
      {/* New Meeting Modal - Using Refactored Component */}
      <NewMeetingModal
        isOpen={showNewMeetingModal}
        onClose={() => setShowNewMeetingModal(false)}
        meetingConversation={meetingConversation}
        newMeetingTitle={newMeetingTitle}
        setNewMeetingTitle={setNewMeetingTitle}
        newMeetingDate={newMeetingDate}
        setNewMeetingDate={setNewMeetingDate}
        newMeetingNotes={newMeetingNotes}
        setNewMeetingNotes={setNewMeetingNotes}
        onCreateMeeting={handleCreateNewMeeting}
      />

      {/* Add Contact Modal - Using Refactored Component */}
      <AddContactModal
        isOpen={showAddContactModal}
        onClose={() => {
          setShowAddContactModal(false);
          setNewContactName('');
          setNewContactEmail('');
          setNewContactPhone('');
          setNewContactCompany('');
          setNewContactRole('');
          setNewContactCategory('Personal');
          setNewContactNotes('');
        }}
        newContactName={newContactName}
        setNewContactName={setNewContactName}
        newContactEmail={newContactEmail}
        setNewContactEmail={setNewContactEmail}
        newContactPhone={newContactPhone}
        setNewContactPhone={setNewContactPhone}
        newContactCompany={newContactCompany}
        setNewContactCompany={setNewContactCompany}
        newContactRole={newContactRole}
        setNewContactRole={setNewContactRole}
        newContactCategory={newContactCategory}
        setNewContactCategory={setNewContactCategory}
        newContactNotes={newContactNotes}
        setNewContactNotes={setNewContactNotes}
        contactAutoScanning={contactAutoScanning}
        onAutoScan={async () => {
          if (!newContactPhone.trim()) {
            alert('Please enter a phone number first');
            return;
          }
          try {
            setContactAutoScanning(true);
            const response = await apiCall(`/contacts/scan-icloud/${encodeURIComponent(newContactPhone)}`);
            if (response.found) {
              setNewContactName(response.name || newContactName);
              setNewContactEmail(response.email || newContactEmail);
              setNewContactCompany(response.company || newContactCompany);
              alert(`Found in iCloud: ${response.name}`);
            } else {
              alert('No match found in iCloud contacts');
            }
          } catch (error) {
            console.error('Auto scan error:', error);
            alert('Auto scan failed: ' + error.message);
          } finally {
            setContactAutoScanning(false);
          }
        }}
        onSave={async () => {
          if (!newContactName.trim()) {
            alert('Please enter a contact name');
            return;
          }
          if (!newContactPhone.trim()) {
            alert('Please enter a phone number');
            return;
          }
          try {
            const contactData = {
              name: newContactName,
              email: newContactEmail,
              phone: newContactPhone,
              company: newContactCompany,
              role: newContactRole,
              category: newContactCategory.toLowerCase(),
              notes: newContactNotes,
              source: 'manual',
              original_name: originalConversationName
            };
            await apiCall('/contacts', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(contactData)
            });
            const savedContactName = newContactName.trim();
            const savedContactPhone = newContactPhone.trim();
            const savedOriginalName = originalConversationName;
            const savedCategory = newContactCategory.toLowerCase();
            const normalizedSavedPhone = savedContactPhone.replace(/\D/g, '');
            toast.success(`Contact "${savedContactName}" saved to ${newContactCategory} category!`);
            setConversations(prev => prev.map(conv => {
              const normalizedConvPhone = conv.phone?.replace(/\D/g, '');
              const phoneMatches = normalizedConvPhone && normalizedSavedPhone && normalizedConvPhone === normalizedSavedPhone;
              const exactPhoneMatch = conv.phone === savedContactPhone;
              const nameMatches = savedOriginalName && conv.name === savedOriginalName;
              if (phoneMatches || exactPhoneMatch || nameMatches) {
                return { ...conv, name: savedContactName, contact_name: savedContactName, category: savedCategory };
              }
              return conv;
            }));
            setSelectedConversation(prev => {
              if (!prev) return prev;
              const normalizedPrevPhone = prev.phone?.replace(/\D/g, '');
              const phoneMatches = normalizedPrevPhone && normalizedSavedPhone && normalizedPrevPhone === normalizedSavedPhone;
              const exactPhoneMatch = prev.phone === savedContactPhone;
              const nameMatches = savedOriginalName && prev.name === savedOriginalName;
              if (phoneMatches || exactPhoneMatch || nameMatches) {
                return { ...prev, name: savedContactName, contact_name: savedContactName, category: savedCategory };
              }
              return prev;
            });
            setShowAddContactModal(false);
            setNewContactName('');
            setNewContactEmail('');
            setNewContactPhone('');
            setNewContactCompany('');
            setNewContactRole('');
            setNewContactCategory('Personal');
            setNewContactNotes('');
            setOriginalConversationName('');
            apiCall('/messages').then(messagesData => setMessages(messagesData)).catch(err => console.error('Failed to refresh messages:', err));
          } catch (error) {
            console.error('Failed to save contact:', error);
            alert('Failed to save contact: ' + error.message);
          }
        }}
      />

      {/* Messaging Connection Modal - Using Refactored Component */}
      <MessagingConnectionModal
        isOpen={showMessagingConnectionModal}
        onClose={() => {
          setShowMessagingConnectionModal(false);
          setMessagingConnectionError('');
        }}
        messagingConnectionError={messagingConnectionError}
        twilioPhone={twilioPhoneNumber}
        setTwilioPhone={setTwilioPhoneNumber}
        twilioSid={twilioAccountSid}
        setTwilioSid={setTwilioAccountSid}
        twilioToken={twilioAuthToken}
        setTwilioToken={setTwilioAuthToken}
        onShortcutsConnect={handleShortcutsConnect}
        onTwilioConnect={handleTwilioConnect}
      />

      {/* Mac Messages Sync Modal - Using Refactored Component */}
      <MacSyncModal
        isOpen={showMacSyncModal}
        onClose={() => setShowMacSyncModal(false)}
        onImportSuccess={fetchConversations}
        apiUrl={process.env.REACT_APP_BACKEND_URL || ''}
      />

      {/* Calls Connection Modal - Using Refactored Component */}
      <CallsConnectionModal
        isOpen={showCallsConnectionModal}
        onClose={() => {
          setShowCallsConnectionModal(false);
          setCallsConnectionError('');
        }}
        callsConnectionError={callsConnectionError}
        onShortcutsConnect={handleCallsShortcutsConnect}
        onTwilioConnect={handleTwilioCallsConnect}
        twilioAccountSid={twilioCallsAccountSid}
        setTwilioAccountSid={setTwilioCallsAccountSid}
        twilioAuthToken={twilioCallsAuthToken}
        setTwilioAuthToken={setTwilioCallsAuthToken}
        twilioPhoneNumber={twilioCallsPhoneNumber}
        setTwilioPhoneNumber={setTwilioCallsPhoneNumber}
        connectingCalls={connectingCalls}
      />

      {/* Add Email Account Modal - Using Refactored Component */}
      <AddEmailAccountModal
        isOpen={showAddEmailModal}
        onClose={() => setShowAddEmailModal(false)}
        newEmailName={newEmailName}
        setNewEmailName={setNewEmailName}
        addEmailError={addEmailError}
        setAddEmailError={setAddEmailError}
        userId={userId}
      />
      
      {/* Category Management Modal - Using Refactored Component */}
      <CategoryManagementModal
        isOpen={showCategoryModal}
        onClose={() => setShowCategoryModal(false)}
        categoryModalType={categoryModalType}
        categories={
          categoryModalType === 'messages' ? messageCategories : 
          categoryModalType === 'calls' ? callCategories : 
          categoryModalType === 'emailCategories' ? emailCategories : emailFolders
        }
        newCategoryName={newCategoryName}
        setNewCategoryName={setNewCategoryName}
        newCategoryKeywords={newCategoryKeywords}
        setNewCategoryKeywords={setNewCategoryKeywords}
        addCategory={addCategory}
        editingCategory={editingCategory}
        setEditingCategory={setEditingCategory}
        editCategoryValue={editCategoryValue}
        setEditCategoryValue={setEditCategoryValue}
        updateCategory={updateCategory}
        deleteCategory={deleteCategory}
        reorderCategories={
          categoryModalType === 'messages' ? reorderMessageCategories :
          categoryModalType === 'calls' ? reorderCallCategories :
          categoryModalType === 'emailCategories' ? reorderEmailCategories : reorderEmailFolders
        }
        formatCategoryName={formatCategoryName}
        editingKeywordsCategory={editingKeywordsCategory}
        setEditingKeywordsCategory={setEditingKeywordsCategory}
        keywordsInputValue={keywordsInputValue}
        setKeywordsInputValue={setKeywordsInputValue}
        saveKeywordsForCategory={saveKeywordsForCategory}
        customCategoryKeywords={categoryModalType === 'emailCategories' ? emailCategoryKeywords : customCategoryKeywords}
        generateKeywordsFromCategory={generateKeywordsFromCategory}
        scanForCategory={scanForCategory}
        scanningCategory={scanningCategory}
        // Email category specific props
        emailCategoryKeywords={emailCategoryKeywords}
        setEmailCategoryKeywords={setEmailCategoryKeywords}
        saveEmailCategoryKeywords={saveEmailCategoryKeywords}
        scanAndCategorizeEmails={scanAndCategorizeEmails}
        scanningEmails={scanningEmails}
      />

      {/* Draft Center Modal */}
      <DraftCenter
        userId={userId}
        isOpen={showDraftCenter}
        onClose={() => setShowDraftCenter(false)}
        onSendDraft={async (draft) => {
          // Handle sending drafts - for now just mark as sent
          // In future, this would actually send via Slack/Email APIs
          console.log('Sending draft:', draft);
          return true;
        }}
      />
    </div>
  );
};

export default RealEstateManagingPartnerCommunications;
// Build: 1771979303
